import React from 'react'
import { flushSync } from 'react-dom'
import { useOutletContext, useSearchParams } from 'react-router-dom'
import { keyframes } from '@emotion/react'
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  type AlertProps,
} from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrowRounded'
import FastForwardIcon from '@mui/icons-material/FastForwardRounded'
import ExpandMoreIcon from '@mui/icons-material/ExpandMoreRounded'
import ExpandLessIcon from '@mui/icons-material/ExpandLessRounded'
import AccessTimeIcon from '@mui/icons-material/AccessTimeRounded'
import TimerIcon from '@mui/icons-material/TimerRounded'
import NotesIcon from '@mui/icons-material/NotesRounded'
import DragIndicatorIcon from '@mui/icons-material/DragIndicatorRounded'
import DeleteIcon from '@mui/icons-material/DeleteOutlineRounded'
import MoreVertIcon from '@mui/icons-material/MoreVertRounded'
import AddIcon from '@mui/icons-material/AddRounded'
import RemoveIcon from '@mui/icons-material/RemoveRounded'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpwardRounded'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownwardRounded'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUpRounded'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import CheckIcon from '@mui/icons-material/CheckRounded'
import CloseIcon from '@mui/icons-material/CloseRounded'
import NextPlanIcon from '@mui/icons-material/NextPlanRounded'
import MusicNoteIcon from '@mui/icons-material/MusicNoteRounded'
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOverRounded'
import ArticleIcon from '@mui/icons-material/ArticleRounded'
import WbSunnyIcon from '@mui/icons-material/WbSunnyRounded'
import CommuteIcon from '@mui/icons-material/CommuteRounded'
import CampaignIcon from '@mui/icons-material/CampaignRounded'
import LocalOfferIcon from '@mui/icons-material/LocalOfferRounded'
import MovieIcon from '@mui/icons-material/MovieRounded'
import WidgetsIcon from '@mui/icons-material/WidgetsRounded'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  type DragEndEvent,
  type DraggableAttributes,
  type DraggableSyntheticListeners,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { alpha, createTheme, ThemeProvider as MuiThemeProvider, useTheme } from '@mui/material/styles'
import type { MeResponse } from '../../api/me'
import { getClockMeta, type ClockMetaResponse } from '../../api/showClock'
import { TypeChipProvider } from '../../components/showClock/TypeChip'
import StatusChip from '../../components/showClock/StatusChip'
import {
  attachClockDayItemPrep,
  createClockDayItem,
  detachClockDayItemPrep,
  getClockDay,
  listClockDayItemPreps,
  lookupClockDay,
  reorderClockDayItemPreps,
  reorderClockDayItems,
  deleteClockDayItem,
  updateClockDayItem,
  type ClockDay,
  type ClockDayItem,
  type ClockDayItemPrep,
  type ClockDayLookupResponse,
  type ClockDaySummary,
} from '../../api/showClockDays'
import {
  adjustClockDayItemDuration,
  endClockDayItem,
  estimateClockDay,
  skipClockDayItem,
  startClockDayItem,
  autoAdvanceClockDayItem,
  enforceClockFixedAnchor,
  reconcileClockDay,
  type ClockLiveActionResponse,
} from '../../api/showClockLive'
import {
  fetchPrepItemDetail,
  listMyPrepItems,
  searchPrepItems,
  type PrepDetailResponse,
} from '../../api/prep'
import { getStation } from '../../api/stations'
import { usePageTitle } from '../../components/common/PageTitleContext'
import PrepDetailDrawer from '../../components/prep/PrepDetailDrawer'
import PrepPicker from '../../components/showClock/PrepPicker'
import useShowClockLiveStore from '../../store/showClockLive'
import { computeSequentialProjection } from '../../store/helpers/showClockLive'
import {
  currentItemCardStyles,
  driftColor,
  formatClockDuration,
  formatClockTimeLabel,
  formatDriftWithSign,
  resolveClockEpochMs,
  resolveClockItemColorStyles,
  resolveClockItemTypes,
  slackTierForSeconds,
  type AnchorSlackTier,
  urgencyColor,
  type ClockItemTypeMeta,
} from '../../utils/showClock'
import useUserSettingsStore from '../../store/userSettings'
import { useDisplayTitles } from '../../utils/clockDisplayTitle'
import useStationStore from '../../store/station'
import {
  formatStationDateTime,
  formatStationTime,
  isSameStationDay,
  resolveStationTimeZone,
} from '../../utils/time'
import { getLocalShowClockLiveSplit, setLocalShowClockLiveSplit } from '../../utils/storage'

const LIVE_BUILD_MARKER = 'live-build-202512-03-2'
const ITEM_TYPE_ICON_MAP: Record<string, React.ElementType> = {
  song: MusicNoteIcon,
  link: RecordVoiceOverIcon,
  news: ArticleIcon,
  weather: WbSunnyIcon,
  travel: CommuteIcon,
  jingle_id: CampaignIcon,
  commercial_break: LocalOfferIcon,
  promo: MovieIcon,
  custom: WidgetsIcon,
}

interface OutletContext {
  me: MeResponse
  includeSuperadmin: boolean
}

type ApiError = Error & { status?: number; data?: unknown }

type PrepAttachment = ClockDayItemPrep & { prep: PrepDetailResponse | null }

type PrepAttachmentsState = Record<
  number,
  { loading: boolean; error: string | null; attachments: PrepAttachment[]; cache: Record<number, PrepDetailResponse> }
>

type LookupState = {
  stationId: number
  response: ClockDayLookupResponse
  fetchedAt: number
}

type ActionType = 'start' | 'skip' | 'adjust'

type RunActionOptions = {
  optimistic?: () => void
  rollback?: () => void
}

const SnackbarAlert = React.forwardRef<HTMLDivElement, AlertProps>(function SnackbarAlert(props, ref) {
  return <Alert elevation={6} variant="filled" ref={ref} {...props} />
})

const MIN_PLANNED_DURATION_SECONDS = 5

const normalizeDuration = (
  minutes: number,
  seconds: number,
): { minutes: number; seconds: number; totalSeconds: number } => {
  const safeMinutes = Number.isFinite(minutes) ? Math.max(0, Math.floor(minutes)) : 0
  const safeSeconds = Number.isFinite(seconds) ? Math.floor(seconds) : 0
  const total = Math.max(0, safeMinutes * 60 + safeSeconds)
  return {
    minutes: Math.floor(total / 60),
    seconds: total % 60,
    totalSeconds: total,
  }
}

const SORTABLE_DRAG_DISTANCE = 6

const MISSED_ITEMS_THRESHOLD = 2
const HEADER_BAR_REFILL_ON_WARNINGS = true // flip to false to keep continuous drain on warnings
const HEADER_WARN_THRESHOLD_SECONDS = 30
const HEADER_CRITICAL_THRESHOLD_SECONDS = 10
const LAG_THRESHOLD_SECONDS = 3 * 60
const RECONCILE_COOLDOWN_MS = 2 * 60 * 1000
const UNKNOWN_DURATION_FALLBACK_SECONDS = 30
const RECONCILE_NOW_FALLBACK_MS = 0
const AUTO_DWELL_SECONDS = 2
const INSERT_SONG_DEFAULT_SECONDS = 210
const LIVE_SPLIT_DEFAULT = 0.5
const LIVE_SPLIT_MIN = 0.3
const LIVE_SPLIT_MAX = 0.7

const usePrepSensors = () =>
  useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: SORTABLE_DRAG_DISTANCE } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

const clampLiveSplit = (value: number): number => {
  if (!Number.isFinite(value)) {
    return LIVE_SPLIT_DEFAULT
  }
  const clamped = Math.max(LIVE_SPLIT_MIN, Math.min(LIVE_SPLIT_MAX, value))
  return Number(clamped.toFixed(2))
}

const createEmptyPrepState = (): PrepAttachmentsState[number] => ({
  loading: false,
  error: null,
  attachments: [],
  cache: {},
})

const sortPrepAttachments = (attachments: PrepAttachment[]): PrepAttachment[] => {
  return [...attachments].sort((a, b) => {
    const positionA = Number.isFinite(a.position) ? Number(a.position) : 0
    const positionB = Number.isFinite(b.position) ? Number(b.position) : 0
    if (positionA !== positionB) {
      return positionA - positionB
    }
    return a.id - b.id
  })
}

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message
  }
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message?: unknown }).message ?? fallback)
  }
  return fallback
}

const hasValue = (value: string | null | undefined): boolean => Boolean(value && value.trim().length > 0)

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const linkifyText = (value: string): string => {
  if (!value) {
    return ''
  }
  const escaped = escapeHtml(value)
  const urlPattern = /(https?:\/\/[^\s]+)/gi
  const withLinks = escaped.replace(
    urlPattern,
    (match) => `<a href="${match}" target="_blank" rel="noopener noreferrer">${match}</a>`,
  )
  return withLinks.replace(/\n/g, '<br />')
}

const sanitizeLabel = (value: string | null | undefined): string => {
  if (!value) {
    return ''
  }
  return value.replace(/\?/g, '').trim()
}

const findCurrentItem = (items: ClockDayItem[]): ClockDayItem | null => {
  if (!items.length) {
    return null
  }

  const live = items.find(
    (item) =>
      hasValue(item.actual_start) &&
      !hasValue(item.actual_end) &&
      !item.skipped &&
      (Number(item.planned_duration_sec) > 0 || hasValue(item.actual_end)),
  )
  if (live) {
    return live
  }

  const upcoming = items.find((item) => !item.skipped && !hasValue(item.actual_start))
  if (upcoming) {
    return upcoming
  }

  const completed = [...items].reverse().find((item) => hasValue(item.actual_end))
  return completed ?? items[items.length - 1] ?? null
}

const findNextItem = (items: ClockDayItem[], current: ClockDayItem | null): ClockDayItem | null => {
  if (!items.length) {
    return null
  }

  const currentIndex = current ? items.findIndex((candidate) => candidate.id === current.id) : -1
  const startIndex = currentIndex >= 0 ? currentIndex + 1 : 0

  for (let index = startIndex; index < items.length; index += 1) {
    const candidate = items[index]
    const isZeroDuration = Number(candidate.planned_duration_sec) <= 0
    if (!candidate.skipped && !(isZeroDuration && !hasValue(candidate.actual_end))) {
      return candidate
    }
  }

  return null
}

const formatCountdownLabel = (seconds: number | null): string => {
  if (!Number.isFinite(seconds) || seconds === null) {
    return '?'
  }
  const clamped = Math.max(0, Math.round(seconds))
  const hours = Math.floor(clamped / 3600)
  const minutes = Math.floor((clamped % 3600) / 60)
  const remaining = clamped % 60
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${remaining.toString().padStart(2, '0')}`
  }
  return `${minutes}:${remaining.toString().padStart(2, '0')}`
}

const formatAdjustmentLabel = (seconds: number): string => {
  const prefix = seconds >= 0 ? '+' : '-'
  return `${prefix}${Math.abs(seconds)}s`
}

const resolveStationTimeInfo = (
  targetEpochMs: number | null | undefined,
  timeZone: string | null | undefined,
  referenceEpochMs: number | null | undefined,
): { timeLabel: string; dateLabel: string | null } => {
  if (!Number.isFinite(targetEpochMs ?? NaN)) {
    return { timeLabel: '?', dateLabel: null }
  }
  try {
    const resolvedTimeZone = resolveStationTimeZone(timeZone)
    const targetMs = Number(targetEpochMs)
    const referenceMs = Number.isFinite(referenceEpochMs ?? Number.NaN)
      ? Number(referenceEpochMs)
      : Date.now()
    const timeLabel = formatStationTime(targetMs, resolvedTimeZone, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })

    if (isSameStationDay(targetMs, referenceMs, resolvedTimeZone)) {
      return { timeLabel, dateLabel: null }
    }

    const dateLabel = formatStationTime(targetMs, resolvedTimeZone, {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
    })

    return { timeLabel, dateLabel }
  } catch (error) {
    console.error('Failed to resolve station time info', error)
    return { timeLabel: '?', dateLabel: null }
  }
}

const ShowClockLivePage: React.FC = () => {
  const theme = useTheme()
  const { me } = useOutletContext<OutletContext>()
  const [searchParams, setSearchParams] = useSearchParams()
  const dayIdParam = searchParams.get('day_id')
  const stations = me.stations ?? []
  const liveAdvanceMode = useUserSettingsStore((state) => state.liveAdvanceMode)
  const setLiveAdvanceMode = useUserSettingsStore((state) => state.setLiveAdvanceMode)
  const showPrepTease = useUserSettingsStore((state) => state.showClock.livePrepTease)
  const showHeaderBar = useUserSettingsStore((state) => state.showClock.liveHeaderVisible)
  const headerFillEnabled = useUserSettingsStore((state) => state.showClock.liveHeaderFill)
  const liveThemeOverride = useUserSettingsStore((state) => state.showClock.liveTheme)
  const resolvedDayId = React.useMemo(() => {
    if (!dayIdParam) {
      return null
    }
    const numeric = Number(dayIdParam)
    if (!Number.isFinite(numeric) || numeric <= 0) {
      return null
    }
    return Math.round(numeric)
  }, [dayIdParam])

  const [activeDayId, setActiveDayId] = React.useState<number | null>(resolvedDayId)
  React.useEffect(() => {
    setActiveDayId(resolvedDayId)
  }, [resolvedDayId])
  const [lookupState, setLookupState] = React.useState<LookupState | null>(null)
  const [lookupLoading, setLookupLoading] = React.useState(false)
  const [lookupError, setLookupError] = React.useState<string | null>(null)
  const [lookupRequestId, setLookupRequestId] = React.useState(0)
  const activeLookupSummary = React.useMemo(() => {
    if (!lookupState || !activeDayId) {
      return null
    }
    if (lookupState.response.current && lookupState.response.current.id === activeDayId) {
      return lookupState.response.current
    }
    if (lookupState.response.next && lookupState.response.next.id === activeDayId) {
      return lookupState.response.next
    }
    return null
  }, [activeDayId, lookupState])

  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [snackbar, setSnackbar] = React.useState<
    {
      message: string
      details?: string | null
      severity: 'success' | 'error' | 'info' | 'warning'
      autoHideDuration?: number
      big?: boolean
    } | null
  >(null)
  const [clockMeta, setClockMeta] = React.useState<ClockMetaResponse | null>(null)
  const itemTypes = React.useMemo(() => resolveClockItemTypes(clockMeta), [clockMeta])
  const [noteDrafts, setNoteDrafts] = React.useState<Record<number, string>>({})
  const [noteStatuses, setNoteStatuses] = React.useState<Record<number, { saving: boolean; error: string | null }>>({})
  const [autoAdvanceSavingById, setAutoAdvanceSavingById] = React.useState<Record<number, boolean>>({})
  const noteBaselinesRef = React.useRef<Record<number, string>>({})
  const [noteEditorForcedVisible, setNoteEditorForcedVisible] = React.useState<Record<number, boolean>>({})
  const [noteFocusTargetId, setNoteFocusTargetId] = React.useState<number | null>(null)
  const [noteViewModes, setNoteViewModes] = React.useState<Record<number, 'edit' | 'preview'>>({})
  const [headerCollapsed, setHeaderCollapsed] = React.useState(false)
  const [autoExpandCandidateId, setAutoExpandCandidateId] = React.useState<number | null>(null)
  const noteInputRefs = React.useRef<Map<number, HTMLTextAreaElement | null>>(new Map())
  const [liveSplit, setLiveSplit] = React.useState(() => {
    const stored = getLocalShowClockLiveSplit()
    return clampLiveSplit(stored ?? LIVE_SPLIT_DEFAULT)
  })
  const splitContainerRef = React.useRef<HTMLDivElement | null>(null)
  const autoAdvanceCooldownRef = React.useRef<Map<number, number>>(new Map())
  const autoAdvanceLockRef = React.useRef<Set<number>>(new Set())
  const autoAdvanceRetryTimerRef = React.useRef<NodeJS.Timeout | null>(null)
  const suppressAutoScrollRef = React.useRef(false)
  const autoAdvanceTimerRef = React.useRef<number | null>(null)
  const autoAdvanceInFlightRef = React.useRef(false)
  const reconcileReadyRef = React.useRef(false)
  const autoStartAttemptedRef = React.useRef<Set<number>>(new Set())
  const autoStartTimerRef = React.useRef<number | null>(null)

  const [prepAttachments, setPrepAttachments] = React.useState<PrepAttachmentsState>({})
  const prepAttachmentsRef = React.useRef(prepAttachments)
  const prepCacheRef = React.useRef<Map<number, PrepDetailResponse>>(new Map())
  const prepRequestAtRef = React.useRef<Record<number, number>>({})
  const stationTimeZoneCacheRef = React.useRef<Map<number, string>>(new Map())
  const stationTimeZoneRequestsRef = React.useRef<Map<number, Promise<string | null>>>(new Map())
  const [liveStationTimeZone, setLiveStationTimeZone] = React.useState<string | null>(null)
  const [liveStationId, setLiveStationId] = React.useState<number | null>(null)
  const [stationTimeZoneFallbackActive, setStationTimeZoneFallbackActive] = React.useState(false)
  const lastNotifiedErrorRef = React.useRef<string | null>(null)
  const hasNotifiedTimeZoneFallbackRef = React.useRef(false)
  const stationNowBaseRef = React.useRef<{ epochMs: number; fetchedAt: number } | null>(null)
  const holdOffsetMsRef = React.useRef(0)
  const holdStartRef = React.useRef<number | null>(null)
  const [prepDrawerOpen, setPrepDrawerOpen] = React.useState(false)
  const [activePrepId, setActivePrepId] = React.useState<number | null>(null)
  const [activePrepContextItemId, setActivePrepContextItemId] = React.useState<number | null>(null)
  const lastInvokerRef = React.useRef<HTMLElement | null>(null)
  const [prepPickerOpen, setPrepPickerOpen] = React.useState(false)
  const [pickerForItemId, setPickerForItemId] = React.useState<number | null>(null)
  const [removingPrepIds, setRemovingPrepIds] = React.useState<Record<number, boolean>>({})
  const itemRefs = React.useRef<Map<number, HTMLDivElement | null>>(new Map())
  const headerRef = React.useRef<HTMLDivElement | null>(null)
  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null)
  const [rowMenuState, setRowMenuState] = React.useState<{
    anchor: HTMLElement
    item: ClockDayItem
  } | null>(null)
  const [deleteTarget, setDeleteTarget] = React.useState<ClockDayItem | null>(null)
  const [deleteLoading, setDeleteLoading] = React.useState(false)
  const [busyCount, setBusyCount] = React.useState(0)
  const [durationEdit, setDurationEdit] = React.useState<{
    itemId: number
    minutes: string
    seconds: string
  } | null>(null)
  const prepFreshnessRef = React.useRef<Record<number, number>>({})
  const [editingTitleId, setEditingTitleId] = React.useState<number | null>(null)
  const [titleDrafts, setTitleDrafts] = React.useState<Record<number, string>>({})
  const titleDraftsRef = React.useRef<Record<number, string>>({})
  const titleTimersRef = React.useRef<Record<number, number>>({})
  const [titleStatuses, setTitleStatuses] = React.useState<Record<number, { saving: boolean; error: string | null }>>(
    {},
  )
  React.useEffect(() => {
    setLocalShowClockLiveSplit(liveSplit)
  }, [liveSplit])
  const autoAdvanceEffect = React.useCallback(() => {
    return () => {
      Object.values(titleTimersRef.current).forEach((timer) => {
        if (typeof timer === 'number') {
          clearTimeout(timer)
        }
      })
    }
  }, [])
  const [actionState, setActionState] = React.useState<{ type: ActionType | null; itemId: number | null }>({
    type: null,
    itemId: null,
  })
  const actionControllerRef = React.useRef<AbortController | null>(null)
  const latestStationNowRef = React.useRef<number>(Date.now())
  const holdFreezeRef = React.useRef<number | null>(null)
  const isMountedRef = React.useRef(true)
  const projectionNowRef = React.useRef<number>(Date.now())
  const anchorEnforceTimerRef = React.useRef<number | null>(null)
  const anchorEnforceKeyRef = React.useRef<string | null>(null)
  const anchorEnforceInFlightRef = React.useRef(false)
  const anchorEnforceCooldownUntilRef = React.useRef<number>(0)
  const anchorEnforceLastAttemptRef = React.useRef<{ key: string; at: number } | null>(null)
  const reconcileSuppressUntilRef = React.useRef<number>(0)
  const fillerAutoCreateRef = React.useRef<string | null>(null)
  const initialScheduledMapRef = React.useRef<
    Map<number, { scheduled_start?: string; scheduled_end?: string; is_fixed?: boolean }>
  >(new Map())
  const autoStartForcedRef = React.useRef<Set<number>>(new Set())
  const headerCountdownRef = React.useRef<HTMLDivElement | null>(null)
  const autoAdvanceStateRef = React.useRef<{
    runningId: number | null
    lastSuccessId: number | null
    lastAttempt: { id: number; at: number } | null
  }>({
    runningId: null,
    lastSuccessId: null,
    lastAttempt: null,
  })
  const autoAdvanceControllerRef = React.useRef<AbortController | null>(null)
  const [holdActive, setHoldActive] = React.useState(false)
  const [catchUpLockActive, setCatchUpLockActive] = React.useState(false)
  const [reconcilePromptOpen, setReconcilePromptOpen] = React.useState(false)
  const [reconcileCandidates, setReconcileCandidates] = React.useState<ClockDayItem[]>([])
  const [reconcileCheckedIds, setReconcileCheckedIds] = React.useState<Set<number>>(new Set())
  const [reconcileApplying, setReconcileApplying] = React.useState(false)
  const [reconcileCurrentItem, setReconcileCurrentItem] = React.useState<ClockDayItem | null>(null)
  const [reconcileBacklog, setReconcileBacklog] = React.useState(false)
  const reconcileCooldownRef = React.useRef<number | null>(null)
  const reconcileForcedOpenRef = React.useRef(false)
  const prevHoldActiveRef = React.useRef(false)
  const initializedRef = React.useRef(false)
  const prevCurrentIdRef = React.useRef<number | null>(null)
  const prepSensors = usePrepSensors()
  const prepPickerApi = React.useMemo(
    () => ({
      listMyPrepItems,
      searchPrepItems,
    }),
    [listMyPrepItems, searchPrepItems],
  )
  const handleSplitPointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const container = splitContainerRef.current
      if (!container) {
        return
      }
      event.preventDefault()
      const bounds = container.getBoundingClientRect()

      const handleMove = (moveEvent: PointerEvent) => {
        const ratio = (moveEvent.clientX - bounds.left) / bounds.width
        setLiveSplit(clampLiveSplit(ratio))
      }

      const handleUp = () => {
        window.removeEventListener('pointermove', handleMove)
        window.removeEventListener('pointerup', handleUp)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }

      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
      window.addEventListener('pointermove', handleMove)
      window.addEventListener('pointerup', handleUp)
    },
    [],
  )

  const cacheStationTimeZone = React.useCallback(
    (stationId: number | null | undefined, timeZone: string | null | undefined): string | null => {
      if (!stationId || stationId <= 0) {
        return null
      }
      if (typeof timeZone !== 'string') {
        return null
      }
      const normalized = timeZone.trim()
      if (!normalized) {
        return null
      }
      const existing = stationTimeZoneCacheRef.current.get(stationId)
      if (existing === normalized) {
        return normalized
      }
      stationTimeZoneCacheRef.current.set(stationId, normalized)
      return normalized
    },
    [],
  )

  const getCachedStationTimeZone = React.useCallback(
    (stationId: number | null | undefined): string | null => {
      if (!stationId || stationId <= 0) {
        return null
      }
      const cached = stationTimeZoneCacheRef.current.get(stationId)
      if (cached && cached.trim().length > 0) {
        return cached
      }
      const fallback = stations.find((station) => station.id === stationId)?.timezone ?? null
      if (typeof fallback === 'string' && fallback.trim().length > 0) {
        return cacheStationTimeZone(stationId, fallback)
      }
      return null
    },
    [cacheStationTimeZone, stations],
  )

  React.useEffect(() => {
    stations.forEach((station) => {
      cacheStationTimeZone(station.id, station.timezone)
    })
  }, [cacheStationTimeZone, stations])

  React.useEffect(() => {
    if (!lookupState?.response.station_timezone) {
      return
    }
    const tz = lookupState.response.station_timezone
    const currentStationId = lookupState.response.current?.station_id ?? null
    const nextStationId = lookupState.response.next?.station_id ?? null
    cacheStationTimeZone(lookupState.stationId ?? currentStationId ?? nextStationId, tz)
    if (currentStationId) {
      cacheStationTimeZone(currentStationId, tz)
    }
    if (nextStationId) {
      cacheStationTimeZone(nextStationId, tz)
    }
  }, [cacheStationTimeZone, lookupState])

  const {
    day,
    items,
    projection,
    stationId,
    warning,
    targetDayId,
    stationNowEpochMs,
  } = useShowClockLiveStore((state) => ({
    day: state.day,
    items: state.items,
    projection: state.projection,
    stationId: state.stationId,
    warning: state.warning,
    targetDayId: state.targetDayId,
    stationNowEpochMs: state.stationNowEpochMs,
  }))
  const setTargetDay = useShowClockLiveStore((state) => state.setTargetDay)
  const setDayDetail = useShowClockLiveStore((state) => state.setDayDetail)
  const setProjectionState = useShowClockLiveStore((state) => state.setProjection)
  const applyActionPayload = useShowClockLiveStore((state) => state.applyActionPayload)
  const updateItem = useShowClockLiveStore((state) => state.updateItem)
  const addItem = useShowClockLiveStore((state) => state.addItem)
  const setItems = useShowClockLiveStore((state) => state.setItems)
  const clearWarning = useShowClockLiveStore((state) => state.clearWarning)
  const resetStore = useShowClockLiveStore((state) => state.reset)
  const setStationNowEpochMs = useShowClockLiveStore((state) => state.setStationNowEpochMs)

  const runAction = React.useCallback(
    async (
      type: ActionType,
      item: ClockDayItem,
      executor: (signal: AbortSignal) => Promise<void | ClockLiveActionResponse>,
      successMessage: string,
      errorFallback: string,
      options?: RunActionOptions,
    ): Promise<boolean> => {
      if (!item.id) {
        return false
      }
      if (actionControllerRef.current) {
        return false
      }

      setBusyCount((count) => count + 1)
      const controller = new AbortController()
      actionControllerRef.current = controller
      setActionState({ type, itemId: item.id })
      let didOptimistic = false
      let succeeded = false

      try {
        if (options?.optimistic) {
          options.optimistic()
          didOptimistic = true
        }
        const response = await executor(controller.signal)
        if (response && typeof response === 'object') {
          applyActionPayload(response)
        }
        if (isMountedRef.current && successMessage) {
          setSnackbar({ message: successMessage, severity: 'success' })
        }
        succeeded = true
      } catch (error) {
        if ((error as Error)?.name === 'AbortError') {
          if (didOptimistic && options?.rollback) {
            options.rollback()
          }
          return false
        }
        if (didOptimistic && options?.rollback) {
          options.rollback()
        }
        if (isMountedRef.current) {
          setSnackbar({ message: getErrorMessage(error, errorFallback), severity: 'error' })
        }
      } finally {
        if (actionControllerRef.current === controller) {
          actionControllerRef.current = null
        }
        if (isMountedRef.current) {
          setActionState({ type: null, itemId: null })
        }
        setBusyCount((count) => Math.max(0, count - 1))
      }
      return succeeded
    },
    [applyActionPayload],
  )

  const ensureNoteEditorVisible = React.useCallback((itemId: number) => {
    setNoteEditorForcedVisible((prev) => {
      if (prev[itemId]) {
        return prev
      }
      return { ...prev, [itemId]: true }
    })
  }, [])

  const hideNoteEditorIfEmpty = React.useCallback((itemId: number) => {
    setNoteEditorForcedVisible((prev) => {
      if (!prev[itemId]) {
        return prev
      }
      const next = { ...prev }
      delete next[itemId]
      return next
    })
  }, [])

  const setNoteViewMode = React.useCallback((itemId: number, mode: 'edit' | 'preview') => {
    setNoteViewModes((prev) => ({ ...prev, [itemId]: mode }))
  }, [])

  const handleNoteInputRef = React.useCallback((itemId: number, node: HTMLTextAreaElement | null) => {
    const refs = noteInputRefs.current
    if (node) {
      refs.set(itemId, node)
    } else {
      refs.delete(itemId)
    }
  }, [])

  const handleNoteDraftChange = React.useCallback((itemId: number, value: string) => {
    setNoteDrafts((prev) => ({ ...prev, [itemId]: value }))
    setNoteStatuses((prev) => {
      const existing = prev[itemId]
      if (!existing || existing.error === null) {
        return prev
      }
      return { ...prev, [itemId]: { ...existing, error: null } }
    })
  }, [])

  const handleResetNote = React.useCallback(
    (item: ClockDayItem) => {
      const value = item.notes ?? ''
      setNoteDrafts((prev) => ({ ...prev, [item.id]: value }))
      setNoteStatuses((prev) => {
        const existing = prev[item.id]
        if (!existing || existing.error === null) {
          return prev
        }
        return { ...prev, [item.id]: { ...existing, error: null } }
      })
      if (!value.trim()) {
        hideNoteEditorIfEmpty(item.id)
      }
      setNoteViewMode(item.id, value.trim() ? 'preview' : 'edit')
    },
    [hideNoteEditorIfEmpty, setNoteViewMode],
  )

  const handleSaveNote = React.useCallback(
    async (item: ClockDayItem) => {
      if (!stationId || !item.id) {
        return
      }
      const draftValue = noteDrafts[item.id] ?? ''
      const trimmed = draftValue.trim()
      const payload = trimmed ? trimmed : null
      setNoteStatuses((prev) => ({
        ...prev,
        [item.id]: { saving: true, error: null },
      }))
      try {
        const response = await updateClockDayItem(stationId, item.id, { notes: payload })
        updateItem(response.item)
        const nextValue = response.item.notes ?? ''
        noteBaselinesRef.current = { ...noteBaselinesRef.current, [item.id]: nextValue }
        if (isMountedRef.current) {
          setNoteDrafts((prev) => ({ ...prev, [item.id]: nextValue }))
          setNoteStatuses((prev) => ({
            ...prev,
            [item.id]: { saving: false, error: null },
          }))
          setSnackbar({ message: nextValue.trim() ? 'Note saved.' : 'Note cleared.', severity: 'success' })
          if (!nextValue.trim()) {
            hideNoteEditorIfEmpty(item.id)
          }
          setNoteViewMode(item.id, nextValue.trim() ? 'preview' : 'edit')
        }
      } catch (error) {
        if (!isMountedRef.current) {
          return
        }
        const message = getErrorMessage(error, 'Unable to save note.')
        setNoteStatuses((prev) => ({
          ...prev,
          [item.id]: { saving: false, error: message },
        }))
        setSnackbar({ message, severity: 'error' })
      }
    },
    [hideNoteEditorIfEmpty, noteDrafts, setSnackbar, stationId, updateItem],
  )

  const handleToggleItemAutoAdvance = React.useCallback(
    async (item: ClockDayItem, nextValue: boolean) => {
      if (!stationId || !item.id) {
        return
      }
      const previousValue = Boolean(item.auto_advance)
      if (previousValue === nextValue) {
        return
      }
      setAutoAdvanceSavingById((prev) => ({
        ...prev,
        [item.id]: true,
      }))
      updateItem({ ...item, auto_advance: nextValue })
      try {
        const response = await updateClockDayItem(stationId, item.id, { auto_advance: nextValue })
        updateItem(response.item)
        setSnackbar({
          message: nextValue ? 'Auto advance enabled.' : 'Auto advance disabled.',
          severity: 'success',
        })
      } catch (error) {
        updateItem({ ...item, auto_advance: previousValue })
        setSnackbar({
          message: getErrorMessage(error, 'Unable to update auto advance.'),
          severity: 'error',
        })
      } finally {
        if (isMountedRef.current) {
          setAutoAdvanceSavingById((prev) => ({
            ...prev,
            [item.id]: false,
          }))
        }
      }
    },
    [setSnackbar, stationId, updateItem],
  )

  React.useEffect(() => {
    const targetStationId = day?.station_id ?? activeLookupSummary?.station_id ?? null
    setLiveStationId(targetStationId)
    if (!targetStationId) {
      setLiveStationTimeZone(null)
      setStationTimeZoneFallbackActive(false)
      return
    }
    const cached = getCachedStationTimeZone(targetStationId)
    if (cached) {
      setLiveStationTimeZone(cached)
      setStationTimeZoneFallbackActive(false)
      return
    }
    const lookupMatch =
      lookupState?.response.station_timezone &&
      ((lookupState.response.current && lookupState.response.current.station_id === targetStationId) ||
        (lookupState.response.next && lookupState.response.next.station_id === targetStationId))
        ? lookupState.response.station_timezone
        : null
    if (lookupMatch) {
      const normalized = cacheStationTimeZone(targetStationId, lookupMatch) ?? lookupMatch
      setLiveStationTimeZone(normalized)
      setStationTimeZoneFallbackActive(false)
      return
    }

    setLiveStationTimeZone(null)
    setStationTimeZoneFallbackActive(false)

    let cancelled = false
    const pending = stationTimeZoneRequestsRef.current.get(targetStationId)
    const request =
      pending ??
      getStation(targetStationId)
        .then((result) => cacheStationTimeZone(targetStationId, result.timezone))
        .catch((error) => {
          console.error('Failed to fetch station timezone', error)
          return null
        })

    stationTimeZoneRequestsRef.current.set(targetStationId, request)

    request
      .then((tz) => {
        if (cancelled) {
          return
        }
        if (tz && tz.trim().length > 0) {
          setLiveStationTimeZone(tz)
          setStationTimeZoneFallbackActive(false)
        } else {
          setStationTimeZoneFallbackActive(true)
        }
      })
      .finally(() => {
        if (stationTimeZoneRequestsRef.current.get(targetStationId) === request) {
          stationTimeZoneRequestsRef.current.delete(targetStationId)
        }
      })

    return () => {
      cancelled = true
    }
  }, [
    activeLookupSummary?.station_id,
    cacheStationTimeZone,
    day?.station_id,
    getCachedStationTimeZone,
    lookupState,
  ])

  React.useEffect(() => {
    return () => {
      isMountedRef.current = false
      actionControllerRef.current?.abort()
    }
  }, [])

  React.useEffect(() => {
    prepAttachmentsRef.current = prepAttachments
  }, [prepAttachments])

  React.useEffect(() => {
    setNoteDrafts((prevDrafts) => {
      const itemIds = new Set(items.map((item) => item.id))
      const baselines = noteBaselinesRef.current
      const updatedBaselines: Record<number, string> = { ...baselines }
      let baselinesChanged = false
      const nextDrafts: Record<number, string> = { ...prevDrafts }
      let draftsChanged = false

      items.forEach((item) => {
        const currentNote = item.notes ?? ''
        if (updatedBaselines[item.id] !== currentNote) {
          updatedBaselines[item.id] = currentNote
          baselinesChanged = true
        }

        if (!(item.id in nextDrafts)) {
          nextDrafts[item.id] = currentNote
          draftsChanged = true
          return
        }

        const baseline = baselines[item.id] ?? ''
        if (prevDrafts[item.id] === baseline && baseline !== currentNote) {
          nextDrafts[item.id] = currentNote
          draftsChanged = true
        }
      })

      Object.keys(nextDrafts).forEach((key) => {
        const id = Number(key)
        if (!itemIds.has(id)) {
          delete nextDrafts[id]
          draftsChanged = true
        }
      })

      Object.keys(updatedBaselines).forEach((key) => {
        const id = Number(key)
        if (!itemIds.has(id)) {
          delete updatedBaselines[id]
          baselinesChanged = true
        }
      })

      if (baselinesChanged) {
        noteBaselinesRef.current = updatedBaselines
      }

      return draftsChanged ? nextDrafts : prevDrafts
    })
  }, [items])

  React.useEffect(() => {
    setNoteViewModes((prev) => {
      const next: Record<number, 'edit' | 'preview'> = {}
      items.forEach((item) => {
        const baseline = noteBaselinesRef.current[item.id] ?? item.notes ?? ''
        const draft = noteDrafts[item.id] ?? baseline
        const trimmed = (draft ?? '').trim()
        const existing = prev[item.id]
        next[item.id] = trimmed.length === 0 ? 'edit' : existing ?? 'preview'
      })
      return next
    })
  }, [items, noteDrafts])

  React.useEffect(() => {
    setNoteStatuses((prev) => {
      const itemIds = new Set(items.map((item) => item.id))
      const next = { ...prev }
      let changed = false
      Object.keys(next).forEach((key) => {
        const id = Number(key)
        if (!itemIds.has(id)) {
          delete next[id]
          changed = true
        }
      })
      return changed ? next : prev
    })
  }, [items])

  React.useEffect(() => {
    setNoteEditorForcedVisible((prev) => {
      if (!Object.keys(prev).length) {
        return prev
      }
      const itemIds = new Set(items.map((item) => item.id))
      const next = { ...prev }
      let changed = false
      Object.keys(next).forEach((key) => {
        const id = Number(key)
        if (!itemIds.has(id)) {
          delete next[id]
          changed = true
        }
      })
      return changed ? next : prev
    })
  }, [items])

  React.useEffect(() => {
    if (noteFocusTargetId === null) {
      return
    }
    const targetId = noteFocusTargetId
    const tryFocus = () => {
      const node = noteInputRefs.current.get(targetId)
      if (node) {
        node.focus()
        const length = node.value.length
        node.setSelectionRange(length, length)
        setNoteFocusTargetId(null)
        return true
      }
      return false
    }
    if (tryFocus()) {
      return
    }
    const raf = window.requestAnimationFrame(() => {
      if (!tryFocus()) {
        setNoteFocusTargetId(null)
      }
    })
    return () => {
      window.cancelAnimationFrame(raf)
    }
  }, [noteFocusTargetId])


  const itemTypeMap = React.useMemo(() => {
    const map = new Map<string, ClockItemTypeMeta>()
    itemTypes.forEach((item) => map.set(item.key, item))
    return map
  }, [itemTypes])

  const orderedTimelineItems = React.useMemo(
    () =>
      items.map((item) => ({
        id: item.id,
        item_type: item.item_type,
        title: item.title,
      })),
    [items],
  )
  const displayTitles = useDisplayTitles(orderedTimelineItems)

  const displayTitleForItem = React.useCallback(
    (item: ClockDayItem | null) => {
      if (!item) {
        return ''
      }
      const displayTitle = displayTitles[item.id] ?? item.title ?? ''
      const trimmed = displayTitle.trim()
      if (trimmed.length > 0) {
        return trimmed
      }
      const meta = itemTypeMap.get(item.item_type)
      return meta?.label ?? item.item_type
    },
    [displayTitles, itemTypeMap],
  )

  const getItemLabel = React.useCallback(
    (item: ClockDayItem | null) => {
      if (!item) {
        return '?'
      }
      const meta = itemTypeMap.get(item.item_type)
      const typeLabel = meta?.label ?? item.item_type
      const effectiveTitle = displayTitleForItem(item) || typeLabel
      return `${typeLabel} • ${effectiveTitle}`
    },
    [displayTitleForItem, itemTypeMap],
  )

  const closeRowMenu = React.useCallback(() => setRowMenuState(null), [])

  const flushTitleSave = React.useCallback(
    async (itemId: number) => {
      const timer = titleTimersRef.current[itemId]
      if (typeof timer === 'number') {
        clearTimeout(timer)
        delete titleTimersRef.current[itemId]
      }
      if (!stationId) {
        return
      }
      const draftValue = titleDraftsRef.current[itemId] ?? ''
      const trimmed = draftValue.trim()
      const normalized = trimmed === '' ? null : trimmed
      const snapshot = useShowClockLiveStore.getState()
      const currentItem = snapshot.itemsById[itemId]
      if (!currentItem) {
        return
      }
      const existingTitle = currentItem.title ?? null
      if (existingTitle === normalized) {
        return
      }

      setTitleStatuses((prev) => ({
        ...prev,
        [itemId]: { saving: true, error: null },
      }))

      try {
        const response = await updateClockDayItem(stationId, itemId, { title: normalized })
        updateItem(response.item)
        if (response.projection) {
          setProjectionState(response.projection)
        }
        if (isMountedRef.current) {
          setTitleStatuses((prev) => ({
            ...prev,
            [itemId]: { saving: false, error: null },
          }))
          setSnackbar({
            message: normalized ? 'Title saved.' : 'Title cleared.',
            severity: 'success',
          })
        }
      } catch (error) {
        if (isMountedRef.current) {
          const message = getErrorMessage(error, 'Unable to save title.')
          setTitleStatuses((prev) => ({
            ...prev,
            [itemId]: { saving: false, error: message },
          }))
          setSnackbar({ message, severity: 'error' })
        }
      }
    },
    [setProjectionState, setSnackbar, stationId, updateItem],
  )

  const scheduleTitleSave = React.useCallback(
    (itemId: number) => {
      const timer = titleTimersRef.current[itemId]
      if (typeof timer === 'number') {
        clearTimeout(timer)
      }
      titleTimersRef.current[itemId] = window.setTimeout(() => {
        void flushTitleSave(itemId)
      }, 800)
    },
    [flushTitleSave],
  )

  const startTitleEdit = React.useCallback((item: ClockDayItem) => {
    setEditingTitleId(item.id)
    setTitleDrafts((prev) => {
      const next = { ...prev, [item.id]: item.title ?? '' }
      titleDraftsRef.current = next
      return next
    })
  }, [])

  const handleTitleFieldChange = React.useCallback(
    (itemId: number, value: string) => {
      setTitleDrafts((prev) => {
        const next = { ...prev, [itemId]: value }
        titleDraftsRef.current = next
        return next
      })
      scheduleTitleSave(itemId)
    },
    [scheduleTitleSave],
  )

  const handleTitleFieldBlur = React.useCallback(
    (itemId: number) => {
      void flushTitleSave(itemId)
      setEditingTitleId((prev) => (prev === itemId ? null : prev))
    },
    [flushTitleSave],
  )

  const cancelTitleEdit = React.useCallback((item: ClockDayItem) => {
    const snapshot = useShowClockLiveStore.getState()
    const latest = snapshot.itemsById[item.id]
    const fallback = latest?.title ?? item.title ?? ''
    setTitleDrafts((prev) => {
      const next = { ...prev, [item.id]: fallback }
      titleDraftsRef.current = next
      return next
    })
    const timer = titleTimersRef.current[item.id]
    if (typeof timer === 'number') {
      clearTimeout(timer)
      delete titleTimersRef.current[item.id]
    }
    setTitleStatuses((prev) => ({
      ...prev,
      [item.id]: { saving: false, error: null },
    }))
    setEditingTitleId((prev) => (prev === item.id ? null : prev))
  }, [])

  const persistLiveItemOrder = React.useCallback(
    async (previous: ClockDayItem[], nextItems: ClockDayItem[]) => {
      if (!stationId || !targetDayId) {
        return
      }
      setBusyCount((count) => count + 1)
      try {
        const response = await reorderClockDayItems(stationId, targetDayId, {
          order: nextItems.map((item) => item.id),
        })
        if (Array.isArray(response.items)) {
          setItems(response.items)
        }
        if (response.projection) {
          setProjectionState(response.projection)
        }
      } catch (error) {
        setItems(previous)
        setSnackbar({ message: getErrorMessage(error, 'Unable to reorder items.'), severity: 'error' })
      } finally {
        setBusyCount((count) => Math.max(0, count - 1))
      }
    },
    [setItems, setProjectionState, setSnackbar, stationId, targetDayId],
  )

  const handleLiveDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      if (!stationId || !targetDayId) {
        return
      }
      const { active, over } = event
      if (!over || active.id === over.id) {
        return
      }
      const currentItems = items
      const currentIndex = currentItems.findIndex((item) => item.id === Number(active.id))
      const targetIndex = currentItems.findIndex((item) => item.id === Number(over.id))
      if (currentIndex === -1 || targetIndex === -1) {
        return
      }
      const nextItems = arrayMove(currentItems, currentIndex, targetIndex)
      setItems(nextItems)
      void persistLiveItemOrder(currentItems, nextItems)
    },
    [items, persistLiveItemOrder, setItems, stationId, targetDayId],
  )

  const openRowMenu = React.useCallback(
    (event: React.MouseEvent<HTMLElement>, item: ClockDayItem) => {
      event.stopPropagation()
      setRowMenuState({ anchor: event.currentTarget, item })
    },
    [],
  )

  const moveItemRelative = React.useCallback(
    (direction: -1 | 1) => {
      const target = rowMenuState?.item
      if (!target) {
        return
      }
      const currentItems = useShowClockLiveStore.getState().items
      const currentIndex = currentItems.findIndex((candidate) => candidate.id === target.id)
      const nextIndex = currentIndex + direction
      if (currentIndex === -1 || nextIndex < 0 || nextIndex >= currentItems.length) {
        return
      }
      const nextItems = arrayMove(currentItems, currentIndex, nextIndex)
      setItems(nextItems)
      persistLiveItemOrder(currentItems, nextItems)
      closeRowMenu()
    },
    [persistLiveItemOrder, rowMenuState, setItems],
  )

  const activeStation = useStationStore((state) => state.activeStation)
  const stationTimeZone = React.useMemo(() => {
    if (liveStationTimeZone && liveStationTimeZone.trim().length > 0) {
      return liveStationTimeZone
    }
    if (liveStationId) {
      const cached = getCachedStationTimeZone(liveStationId)
      if (cached) {
        return cached
      }
    }
    if (activeLookupSummary?.station_id) {
      const cached = getCachedStationTimeZone(activeLookupSummary.station_id)
      if (cached) {
        return cached
      }
    }
    if (lookupState?.response.station_timezone) {
      return lookupState.response.station_timezone
    }
    if (lookupState?.stationId) {
      const cached = getCachedStationTimeZone(lookupState.stationId)
      if (cached) {
        return cached
      }
    }
    if (activeStation?.timezone && activeStation.timezone.trim().length > 0) {
      return activeStation.timezone
    }
    return null
  }, [
    activeLookupSummary?.station_id,
    activeStation?.timezone,
    getCachedStationTimeZone,
    liveStationId,
    liveStationTimeZone,
    lookupState,
  ])
  React.useEffect(() => {
    latestStationNowRef.current = stationNowEpochMs ?? Date.now()
  }, [stationNowEpochMs])

  const getHoldAccumMs = React.useCallback(() => {
    const baseNow = stationNowEpochMs ?? Date.now()
    const runningHold = holdStartRef.current !== null ? Math.max(0, baseNow - holdStartRef.current) : 0
    return holdOffsetMsRef.current + runningHold
  }, [stationNowEpochMs])

  React.useEffect(() => {
    if (holdActive) {
      const baseNow = stationNowEpochMs ?? Date.now()
      const offset = holdOffsetMsRef.current
      const adjusted = baseNow - offset
      holdStartRef.current = holdStartRef.current ?? baseNow
      holdFreezeRef.current = holdFreezeRef.current ?? adjusted
    } else {
      if (holdStartRef.current !== null) {
        const baseNow = stationNowEpochMs ?? Date.now()
        const delta = Math.max(0, baseNow - holdStartRef.current)
        holdOffsetMsRef.current += delta
      }
      holdStartRef.current = null
      holdFreezeRef.current = null
    }
  }, [holdActive])

  const effectiveNowMs = React.useMemo(() => {
    const baseNow = stationNowEpochMs ?? Date.now()
    const runningHold = holdStartRef.current !== null ? Math.max(0, baseNow - holdStartRef.current) : 0
    const totalOffset = holdOffsetMsRef.current + runningHold
    const adjusted = baseNow - totalOffset
    if (holdActive) {
      return holdFreezeRef.current ?? adjusted
    }
    return adjusted
  }, [holdActive, stationNowEpochMs])

  const locateDay = React.useCallback(
    async (
      dayId: number,
      preferredStationId?: number | null,
    ): Promise<{ stationId: number; detail: { day: ClockDay; items: ClockDayItem[] } } | null> => {
      const orderedStations = [...stations]
      if (preferredStationId) {
        orderedStations.sort((a, b) => {
          if (a.id === preferredStationId) {
            return -1
          }
          if (b.id === preferredStationId) {
            return 1
          }
          return 0
        })
      }
      const seen = new Set<number>()
      for (const station of orderedStations) {
        if (seen.has(station.id)) {
          continue
        }
        seen.add(station.id)
        try {
          const detail = await getClockDay(station.id, dayId)
          return { stationId: station.id, detail }
        } catch (error) {
          const status = (error as ApiError)?.status ?? 0
          if (status === 404 || status === 403) {
            continue
          }
          throw error
        }
      }
      return null
    },
    [stations],
  )

  React.useEffect(() => {
    let cancelled = false
    setError(null)
    setSnackbar(null)
    resetStore()
    setCatchUpLockActive(false)
    setReconcileCandidates([])
    setReconcileCheckedIds(new Set<number>())
    setReconcilePromptOpen(false)
    reconcileForcedOpenRef.current = false
    stationNowBaseRef.current = null
    setStationNowEpochMs(null)
    setPrepAttachments({})
    prepAttachmentsRef.current = {}
    prepCacheRef.current = new Map()
    setNoteDrafts({})
    setNoteStatuses({})
    noteBaselinesRef.current = {}
    setPrepDrawerOpen(false)
    setActivePrepId(null)
    lastInvokerRef.current = null
    setPrepPickerOpen(false)
    setPickerForItemId(null)
    setRemovingPrepIds({})
    autoStartAttemptedRef.current = new Set()
    if (autoStartTimerRef.current !== null) {
      window.clearTimeout(autoStartTimerRef.current)
      autoStartTimerRef.current = null
    }
    initializedRef.current = true

    if (activeDayId) {
      setTargetDay(activeDayId, activeLookupSummary?.station_id ?? lookupState?.stationId ?? null)
    } else {
      setTargetDay(null, lookupState?.stationId ?? null)
    }

    if (!activeDayId) {
      setLoading(false)
      if (stations.length === 0) {
        setError('You need access to at least one station to open the live show clock.')
      }
      return () => {
        cancelled = true
      }
    }

    if (stations.length === 0) {
      setLoading(false)
      setError('You need access to at least one station to open the live show clock.')
      return () => {
        cancelled = true
      }
    }

    setLoading(true)

    void (async () => {
      try {
        const located = await locateDay(activeDayId, lookupState?.stationId ?? null)
        if (cancelled) {
          return
        }
        if (!located) {
          setError('Clock day not found for your stations.')
          return
        }
        setTargetDay(activeDayId, located.stationId)
        setDayDetail(located.detail.day, located.detail.items)
        try {
          const projectionData = await estimateClockDay({
            dayId: located.detail.day.id,
            stationId: located.stationId,
          })
          if (!cancelled) {
            setProjectionState(projectionData)
          }
        } catch (projectionError) {
          if (!cancelled) {
            console.error('Failed to load show clock projection', projectionError)
          }
        }
      } catch (err) {
        if (cancelled) {
          return
        }
        setError(getErrorMessage(err, 'Unable to load the live show clock.'))
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    })()

    return () => {
      cancelled = true
      resetStore()
    }
  }, [
    activeDayId,
    activeLookupSummary?.station_id,
    locateDay,
    lookupState?.stationId,
    resetStore,
    setDayDetail,
    setProjectionState,
    setStationNowEpochMs,
    setTargetDay,
    stations.length,
  ])

  const updateStationNow = React.useCallback(() => {
    const base = stationNowBaseRef.current
    const nowTimestamp = Date.now()
    const epochMs = base ? base.epochMs + (nowTimestamp - base.fetchedAt) : nowTimestamp
    if (!base) {
      stationNowBaseRef.current = { epochMs: nowTimestamp, fetchedAt: nowTimestamp }
    }
    setStationNowEpochMs(epochMs)
  }, [setStationNowEpochMs])

  React.useEffect(() => {
    if (stationNowBaseRef.current === null) {
      const nowTimestamp = Date.now()
      stationNowBaseRef.current = { epochMs: nowTimestamp, fetchedAt: nowTimestamp }
    }
    updateStationNow()
    const timer = window.setInterval(() => {
      updateStationNow()
    }, 1000)
    return () => {
      window.clearInterval(timer)
    }
  }, [updateStationNow])

  React.useEffect(() => {
    const nowInstant = lookupState?.response.now ?? null
    const fetchedAt = lookupState?.fetchedAt ?? null
    if (nowInstant && fetchedAt !== null) {
      const resolvedEpoch = resolveClockEpochMs(nowInstant, stationTimeZone)
      if (resolvedEpoch !== null) {
        stationNowBaseRef.current = { epochMs: resolvedEpoch, fetchedAt }
        updateStationNow()
        return
      }
    }

    if (stationNowBaseRef.current === null) {
      const nowTimestamp = Date.now()
      stationNowBaseRef.current = { epochMs: nowTimestamp, fetchedAt: nowTimestamp }
      updateStationNow()
    }
  }, [lookupState?.fetchedAt, lookupState?.response.now, stationTimeZone, updateStationNow])

  React.useEffect(() => {
    if (resolvedDayId) {
      setLookupState(null)
      setLookupLoading(false)
      setLookupError(null)
      return
    }

    if (stations.length === 0) {
      setLookupState(null)
      setLookupLoading(false)
      return
    }

    const primaryStationId = activeStation?.id ?? stations[0]?.id ?? null
    if (!primaryStationId) {
      setLookupState(null)
      return
    }

    let cancelled = false
    setLookupLoading(true)
    setLookupError(null)

    void (async () => {
      try {
        const response: ClockDayLookupResponse = await lookupClockDay({ stationId: primaryStationId })
        if (cancelled) {
          return
        }
        const currentStationId = response.current?.station_id ?? null
        const nextStationId = response.next?.station_id ?? null
        const resolvedStationId = currentStationId ?? nextStationId ?? primaryStationId
        if (response.station_timezone) {
          cacheStationTimeZone(resolvedStationId, response.station_timezone)
          if (currentStationId) {
            cacheStationTimeZone(currentStationId, response.station_timezone)
          }
          if (nextStationId) {
            cacheStationTimeZone(nextStationId, response.station_timezone)
          }
        }
        setLookupState({
          stationId: resolvedStationId ?? primaryStationId,
          response,
          fetchedAt: Date.now(),
        })
        const currentId = response.current?.id ?? null
        if (currentId) {
          setActiveDayId((prev) => {
            if (prev === currentId) {
              return prev
            }
            return currentId
          })
          if (resolvedDayId !== currentId) {
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev)
              next.set('day_id', String(currentId))
              return next
            }, { replace: true })
          }
        } else {
          setActiveDayId((prev) => (prev !== null ? null : prev))
          if (resolvedDayId !== null) {
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev)
              next.delete('day_id')
              return next
            }, { replace: true })
          }
        }
      } catch (lookupErr) {
        if (cancelled) {
          return
        }
        setLookupError(getErrorMessage(lookupErr, 'Unable to look up show clocks.'))
        setLookupState(null)
      } finally {
        if (!cancelled) {
          setLookupLoading(false)
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [
    activeStation?.id,
    cacheStationTimeZone,
    lookupRequestId,
    resolvedDayId,
    setSearchParams,
    stations,
  ])

  const baseScheduleEpochMs = React.useMemo(() => {
    const resolvedWindow = day?.window_start ? resolveClockEpochMs(day.window_start, stationTimeZone) : null
    if (resolvedWindow !== null) {
      return resolvedWindow
    }
    if (day?.date && stationTimeZone) {
      const dateEpoch = resolveClockEpochMs(`${day.date} 00:00:00`, stationTimeZone)
      if (dateEpoch !== null) {
        return dateEpoch
      }
    }
    const firstFixed = items.length ? resolveClockEpochMs(items[0]?.fixed_time, stationTimeZone) : null
    if (firstFixed !== null) {
      return firstFixed
    }
    const projectionStart = projection?.items?.[0]?.projected_start ?? null
    if (projectionStart) {
      const resolved = resolveClockEpochMs(projectionStart, stationTimeZone)
      if (resolved !== null) {
        return resolved
      }
    }
    return null
  }, [day?.window_start, items, projection?.items, stationTimeZone])

  const catchUpRequirement = React.useMemo(() => {
    if (
      loading ||
      lookupLoading ||
      !stationTimeZone ||
      baseScheduleEpochMs === null ||
      items.length === 0 ||
      !day?.id
    ) {
      return {
        signature: null as string | null,
        candidates: [] as ClockDayItem[],
        currentFromProjection: null as ClockDayItem | null,
        behindSeconds: 0,
        shouldPrompt: false,
      }
    }

    const nowMs = effectiveNowMs
    const scheduled = computeSequentialProjection({
      items,
      previousProjection: null,
      nowEpochMs: nowMs,
      stationTimeZone,
      baseStartEpochMs: baseScheduleEpochMs,
    })

    let currentIndex = -1
     for (let i = 0; i < scheduled.items.length; i += 1) {
       const entry = scheduled.items[i]
       const startMs = resolveClockEpochMs(entry.scheduled_start ?? entry.projected_start ?? null, stationTimeZone)
       const endMs = resolveClockEpochMs(entry.scheduled_end ?? entry.projected_end ?? null, stationTimeZone)
       if (startMs === null || endMs === null) {
         continue
       }
       if (endMs <= nowMs) {
         continue
       }
       if (startMs <= nowMs && nowMs < endMs) {
         currentIndex = i
         break
       }
       if (startMs > nowMs) {
         break
       }
    }

    const currentFromProjection =
      currentIndex >= 0
        ? items.find((item) => item.id === scheduled.items[currentIndex].id) ?? null
        : null

     const candidates: ClockDayItem[] = []
     const limit = currentIndex >= 0 ? currentIndex : scheduled.items.length
     for (let i = 0; i < limit; i += 1) {
       const entry = scheduled.items[i]
       const scheduledEnd = resolveClockEpochMs(entry.scheduled_end ?? entry.projected_end ?? null, stationTimeZone)
       if (scheduledEnd === null || scheduledEnd > nowMs) {
         continue
       }
       const detail = items.find((item) => item.id === entry.id) ?? null
       if (!detail || detail.skipped || hasValue(detail.actual_end)) {
         continue
      }
      candidates.push(detail)
    }

    const behindSeconds = Math.min(0, projection?.ahead_behind_total ?? scheduled.ahead_behind_total ?? 0)
    const shouldPrompt =
      candidates.length >= MISSED_ITEMS_THRESHOLD || behindSeconds <= -LAG_THRESHOLD_SECONDS
    const signature = `${day.id}:${items.length}:${candidates.map((c) => c.id).join(',')}:${behindSeconds}:${currentIndex}`

    return {
      signature,
      candidates,
      currentFromProjection,
      behindSeconds,
      shouldPrompt,
    }
  }, [
    baseScheduleEpochMs,
    day?.id,
    effectiveNowMs,
    items,
    loading,
    lookupLoading,
    projection?.ahead_behind_total,
    stationTimeZone,
  ])

  const catchUpLockShouldEngage = Boolean(
    catchUpRequirement.shouldPrompt && catchUpRequirement.candidates.length > 0,
  )

  React.useEffect(() => {
    if (catchUpLockShouldEngage) {
      setCatchUpLockActive(true)
      reconcileForcedOpenRef.current = true
    }
  }, [catchUpLockShouldEngage])

  const catchUpLocked = catchUpLockActive || catchUpLockShouldEngage

  React.useEffect(() => {
    if (!day?.id || !stationId || !stationTimeZone) {
      return
    }

    if (autoStartTimerRef.current !== null) {
      window.clearTimeout(autoStartTimerRef.current)
      autoStartTimerRef.current = null
    }
    if (catchUpLocked) {
      return
    }

    const windowStartMs = resolveClockEpochMs(day.window_start, stationTimeZone)

    const triggerStart = (nowEpochMs: number) => {
      if (autoStartAttemptedRef.current.has(day.id)) {
        return
      }
      const firstItem = items.find((candidate) => !candidate.skipped) ?? null
      if (!firstItem) {
        autoStartAttemptedRef.current.add(day.id)
        return
      }
      if (hasValue(firstItem.actual_start)) {
        autoStartAttemptedRef.current.add(day.id)
        return
      }
      const fixedEpoch = resolveClockEpochMs(firstItem.fixed_time ?? null, stationTimeZone)
      if (fixedEpoch !== null && fixedEpoch > nowEpochMs) {
        return
      }
      const startEpoch =
        fixedEpoch && fixedEpoch <= nowEpochMs
          ? fixedEpoch
          : windowStartMs && windowStartMs <= nowEpochMs
            ? windowStartMs
            : nowEpochMs
      const projectedStartLabel = formatStationDateTime(startEpoch, stationTimeZone, {
        withSeconds: true,
        twentyFourHour: true,
      })
      const previousProjectionClone = projection
        ? {
            ...projection,
            items: projection.items ? projection.items.map((entry) => ({ ...entry })) : [],
          }
        : null
      const optimisticItem: ClockDayItem = {
        ...firstItem,
        actual_start: projectedStartLabel,
        actual_start_epoch: Math.round(startEpoch / 1000),
        actual_start_iso_utc: null,
      }
      const optimisticItems = items.map((entry) => (entry.id === firstItem.id ? optimisticItem : entry))
      const optimisticProjection = computeSequentialProjection({
        items: optimisticItems,
        previousProjection: projection,
        nowEpochMs,
        stationTimeZone,
        baseStartEpochMs: baseScheduleEpochMs,
      })
      autoStartAttemptedRef.current.add(day.id)
      void runAction(
        'start',
        firstItem,
        (signal) =>
          startClockDayItem(firstItem.id, {
            stationId,
            signal,
            confirm: true,
            projectedStart: projectedStartLabel ?? undefined,
          }),
        '',
        'Unable to mark this item as started.',
        {
          optimistic: () => {
            updateItem(optimisticItem)
            setProjectionState(optimisticProjection)
          },
          rollback: () => {
            updateItem(firstItem)
            setProjectionState(previousProjectionClone)
          },
        },
      )
    }

    if (windowStartMs !== null && stationNowEpochMs && stationNowEpochMs < windowStartMs) {
      const delay = Math.max(0, windowStartMs - stationNowEpochMs)
      autoStartTimerRef.current = window.setTimeout(() => {
        autoStartTimerRef.current = null
        triggerStart(windowStartMs)
      }, delay)
      return () => {
        if (autoStartTimerRef.current !== null) {
          window.clearTimeout(autoStartTimerRef.current)
          autoStartTimerRef.current = null
        }
      }
    }

    if (!stationNowEpochMs) {
      return
    }

    triggerStart(stationNowEpochMs)

    return () => {
      if (autoStartTimerRef.current !== null) {
        window.clearTimeout(autoStartTimerRef.current)
        autoStartTimerRef.current = null
      }
    }
  }, [baseScheduleEpochMs, catchUpLocked, day?.id, items, projection, runAction, startClockDayItem, stationId, stationNowEpochMs, stationTimeZone])

  React.useEffect(() => {
    if (!stationId || stationId <= 0) {
      setClockMeta(null)
      return
    }

    let active = true
    const loadMeta = async () => {
      try {
        const meta: ClockMetaResponse = await getClockMeta({ stationId })
        if (active) {
          setClockMeta(meta)
        }
      } catch (metaError) {
        console.error('Failed to load clock metadata', metaError)
      }
    }
    void loadMeta()
    return () => {
      active = false
    }
  }, [stationId])

  React.useEffect(() => {
    console.info(`Live ShowClock build marker: ${LIVE_BUILD_MARKER}`)
  }, [])

  const loadPreps = React.useCallback(
    async (itemId: number, options?: { force?: boolean }) => {
      if (!Number.isFinite(itemId) || itemId <= 0) {
        return null
      }
      if (!stationId || stationId <= 0) {
        return null
      }
      const force = options?.force ?? false
      const nowTs = Date.now()
      const lastReq = prepRequestAtRef.current[itemId] ?? 0
      if (!force && nowTs - lastReq < 2000) {
        const existing = prepAttachmentsRef.current[itemId]
        return existing?.attachments ?? null
      }
      const freshAt = prepFreshnessRef.current[itemId] ?? 0
      const isFresh = nowTs - freshAt < 60_000
      const existing = prepAttachmentsRef.current[itemId]
      if (!force && existing && isFresh && existing.attachments.length > 0 && existing.error === null) {
        return existing.attachments
      }
      prepRequestAtRef.current[itemId] = nowTs
      setPrepAttachments((prev) => {
        const current = prev[itemId] ?? createEmptyPrepState()
        return {
          ...prev,
          [itemId]: {
            ...current,
            loading: true,
            error: null,
          },
        }
      })
      try {
        const response = await listClockDayItemPreps(stationId, itemId)
        const existingCache = prepAttachmentsRef.current[itemId]?.cache ?? {}
        const nextCache: Record<number, PrepDetailResponse> = { ...existingCache }
        const attachments: PrepAttachment[] = await Promise.all(
          response.preps.map(async (prep) => {
            const cachedDetail = nextCache[prep.prep_item_id] ?? prepCacheRef.current.get(prep.prep_item_id) ?? null
            if (cachedDetail) {
              nextCache[cachedDetail.id] = cachedDetail
              return { ...prep, prep: cachedDetail }
            }
            try {
              const detail = await fetchPrepItemDetail(prep.prep_item_id)
              nextCache[detail.id] = detail
              prepCacheRef.current.set(detail.id, detail)
              return { ...prep, prep: detail }
            } catch (detailError) {
              console.error('Failed to load prep detail', detailError)
              return { ...prep, prep: null }
            }
          }),
        )
        const sorted = sortPrepAttachments(attachments)
        prepFreshnessRef.current[itemId] = Date.now()
        setPrepAttachments((prev) => ({
          ...prev,
          [itemId]: { loading: false, error: null, attachments: sorted, cache: nextCache },
        }))
        return sorted
      } catch (prepError) {
        const message = getErrorMessage(prepError, 'Unable to load prep attachments.')
        setSnackbar({ message, severity: 'error' })
        setPrepAttachments((prev) => ({
          ...prev,
          [itemId]: {
            loading: false,
            error: message,
            attachments: prev[itemId]?.attachments ?? [],
            cache: prev[itemId]?.cache ?? createEmptyPrepState().cache,
          },
        }))
        return null
      }
    },
    [setSnackbar, stationId],
  )

  const ensurePrepLoadedForItem = React.useCallback(
    (itemId: number, options?: { force?: boolean }) => loadPreps(itemId, options),
    [loadPreps],
  )

  const ensurePrepLoadedWithBusy = React.useCallback(
    async (itemId: number, options?: { force?: boolean }) => {
      setBusyCount((count) => count + 1)
      try {
        return await ensurePrepLoadedForItem(itemId, options)
      } finally {
        setBusyCount((count) => Math.max(0, count - 1))
      }
    },
    [ensurePrepLoadedForItem],
  )

  const openPrepPicker = React.useCallback((itemId: number) => {
    setPickerForItemId(itemId)
    setPrepPickerOpen(true)
  }, [])

  const [expandedIds, setExpandedIds] = React.useState<Set<number>>(() => new Set<number>())

  const isExpanded = React.useCallback((id: number) => expandedIds.has(id), [expandedIds])

  const setExpanded = React.useCallback(
    (id: number, open: boolean) => {
      setExpandedIds((prev) => {
        const next = new Set(prev)
        if (open) {
          next.add(id)
        } else {
          next.delete(id)
        }
        return next
      })
      if (open) {
        void ensurePrepLoadedForItem(id)
      }
    },
    [ensurePrepLoadedForItem],
  )

  const toggleExpanded = React.useCallback(
    (id: number) => {
      let shouldOpen = false
      setExpandedIds((prev) => {
        const next = new Set(prev)
        if (next.has(id)) {
          next.delete(id)
        } else {
          next.add(id)
          shouldOpen = true
        }
        return next
      })
      if (shouldOpen) {
        setAutoExpandCandidateId(null)
        void ensurePrepLoadedForItem(id)
      }
    },
    [ensurePrepLoadedForItem],
  )

  const baseScheduledProjection = React.useMemo(() => {
    return computeSequentialProjection({
      items,
      previousProjection: null,
      nowEpochMs: projectionNowRef.current,
      stationTimeZone,
      baseStartEpochMs: baseScheduleEpochMs,
    })
  }, [baseScheduleEpochMs, items, stationTimeZone])

  const scheduledProjection = baseScheduledProjection
  const reconcileProjection = baseScheduledProjection

  const apiScheduledItems = React.useMemo(() => {
    const src = projection?.items ?? []
    if (src.some((item) => item.scheduled_start)) {
      return src
    }
    return null
  }, [projection])

  const currentItem = React.useMemo(() => findCurrentItem(items), [items])
  const nextItem = React.useMemo(() => findNextItem(items, currentItem), [currentItem, items])
  const currentItemId = currentItem?.id ?? null
  const isCurrentRunning = Boolean(
    currentItem && hasValue(currentItem.actual_start) && !hasValue(currentItem.actual_end),
  )
  const advanceTarget = React.useMemo(() => {
    if (isCurrentRunning) {
      return nextItem
    }
    if (currentItem && !currentItem.skipped && !hasValue(currentItem.actual_start)) {
      return currentItem
    }
    return null
  }, [currentItem, isCurrentRunning, nextItem])

  const effectiveProjection = React.useMemo(() => {
    if (projection?.items?.length) {
      return projection
    }
    return scheduledProjection
  }, [projection, scheduledProjection])

  const projectionMap = React.useMemo(() => {
    const map = new Map<
      number,
      { projected_start: string; projected_end: string; delta_from_schedule: number; is_fixed: boolean }
    >()
    const source = effectiveProjection?.items ?? []
    source.forEach((item) => {
      map.set(item.id, {
        projected_start: item.projected_start,
        projected_end: item.projected_end,
        delta_from_schedule: item.delta_from_schedule,
        is_fixed: item.is_fixed,
      })
    })
    return map
  }, [effectiveProjection, scheduledProjection])

  const scheduledProjectionMap = React.useMemo(() => {
    const map = new Map<
      number,
      {
        projected_start: string
        projected_end: string
        scheduled_start?: string
        scheduled_end?: string
        delta_from_schedule: number
        is_fixed: boolean
        }
      >()
    const source = apiScheduledItems ?? scheduledProjection?.items ?? []
    source.forEach((item) => {
      const existing = initialScheduledMapRef.current.get(item.id)
      const scheduled_start = existing?.scheduled_start ?? item.scheduled_start
      const scheduled_end = existing?.scheduled_end ?? item.scheduled_end
      const entry = {
        projected_start: item.projected_start,
        projected_end: item.projected_end,
        scheduled_start,
        scheduled_end,
        delta_from_schedule: item.delta_from_schedule,
        is_fixed: item.is_fixed,
      }
      map.set(item.id, entry)
      if (scheduled_start || scheduled_end || item.is_fixed) {
        initialScheduledMapRef.current.set(item.id, {
          scheduled_start,
          scheduled_end,
          is_fixed: item.is_fixed,
        })
      }
    })
    return map
  }, [apiScheduledItems, scheduledProjection])

  const nextPendingFixedAnchor = React.useMemo(() => {
    if (!stationTimeZone) {
      return null
    }

    const anchorIndex = items.findIndex(
      (candidate) => Boolean(candidate.fixed_time) && !candidate.skipped && !hasValue(candidate.actual_start),
    )
    if (anchorIndex === -1) {
      return null
    }

    const anchor = items[anchorIndex]
    const projectionEntry = projectionMap.get(anchor.id) ?? scheduledProjectionMap.get(anchor.id) ?? null
    const startInstant = projectionEntry?.scheduled_start ?? anchor.fixed_time ?? null
    const scheduledStartMs = resolveClockEpochMs(startInstant, stationTimeZone)
    if (scheduledStartMs === null) {
      return null
    }

    return {
      anchor,
      anchorIndex,
      startInstant,
      scheduledStartMs,
    }
  }, [items, projectionMap, scheduledProjectionMap, stationTimeZone])

  const runFixedAnchorEnforcement = React.useCallback(
    async (anchorId: number, key?: string | null) => {
      if (!stationId || !stationTimeZone || !targetDayId) {
        return
      }
      const now = Date.now()
      reconcileSuppressUntilRef.current = Math.max(reconcileSuppressUntilRef.current, now + 3000)
      if (now < anchorEnforceCooldownUntilRef.current) {
        return
      }
      const attemptKey = (key ?? null) ?? String(anchorId)
      const lastAttempt = anchorEnforceLastAttemptRef.current
      if (lastAttempt && lastAttempt.key === attemptKey && now - lastAttempt.at < 1000) {
        return
      }
      if (anchorEnforceInFlightRef.current) {
        return
      }
      anchorEnforceInFlightRef.current = true
      anchorEnforceLastAttemptRef.current = { key: attemptKey, at: now }
      setBusyCount((count) => count + 1)
      try {
        const response = await enforceClockFixedAnchor({
          dayId: targetDayId,
          stationId,
          anchorItemId: anchorId,
        })
        if (response) {
          applyActionPayload(response)
          reconcileSuppressUntilRef.current = Math.max(reconcileSuppressUntilRef.current, Date.now() + 3000)
          try {
            const refreshed = await getClockDay(stationId, targetDayId)
            setDayDetail(refreshed.day, refreshed.items)
            const projectionData = await estimateClockDay({
              dayId: targetDayId,
              stationId,
            })
            setProjectionState(projectionData)
          } catch (refreshError) {
            console.error('Failed to refresh show clock after fixed-anchor enforcement', refreshError)
          }
        }
      } catch (error) {
        const status = (error && typeof error === 'object' && 'status' in error ? (error as any).status : null) as
          | number
          | null
        const payload = (error && typeof error === 'object' && 'data' in error ? (error as any).data : null) as
          | any
          | null
        const secondsUntilAnchor =
          typeof payload?.data?.seconds_until_anchor === 'number'
            ? payload.data.seconds_until_anchor
            : typeof payload?.data?.data?.seconds_until_anchor === 'number'
              ? payload.data.data.seconds_until_anchor
              : typeof payload?.data?.seconds_until_anchor === 'string'
                ? Number(payload.data.seconds_until_anchor)
                : typeof payload?.data?.data?.seconds_until_anchor === 'string'
                  ? Number(payload.data.data.seconds_until_anchor)
                  : null

        if (status === 409 && typeof secondsUntilAnchor === 'number' && Number.isFinite(secondsUntilAnchor)) {
          const backoffMs = Math.max(250, Math.round(secondsUntilAnchor * 1000) + 250)
          anchorEnforceCooldownUntilRef.current = Date.now() + backoffMs
          console.warn('Fixed anchor enforcement skipped (not due yet)', { secondsUntilAnchor })
        } else {
          anchorEnforceCooldownUntilRef.current = Date.now() + 1000
          console.warn('Fixed anchor enforcement skipped', error)
        }
      } finally {
        anchorEnforceInFlightRef.current = false
        setBusyCount((count) => Math.max(0, count - 1))
      }
    },
    [applyActionPayload, setDayDetail, setProjectionState, stationId, stationTimeZone, targetDayId],
  )

  React.useEffect(() => {
    if (!stationId || !targetDayId || !stationNowEpochMs || !stationTimeZone) {
      return
    }
    if (catchUpLocked) {
      if (anchorEnforceTimerRef.current !== null) {
        window.clearTimeout(anchorEnforceTimerRef.current)
        anchorEnforceTimerRef.current = null
      }
      anchorEnforceKeyRef.current = null
      return
    }

    if (!nextPendingFixedAnchor) {
      if (anchorEnforceTimerRef.current !== null) {
        window.clearTimeout(anchorEnforceTimerRef.current)
        anchorEnforceTimerRef.current = null
      }
      anchorEnforceKeyRef.current = null
      return
    }

    const key = `${nextPendingFixedAnchor.anchor.id}:${nextPendingFixedAnchor.scheduledStartMs}`
    if (anchorEnforceTimerRef.current !== null && anchorEnforceKeyRef.current === key) {
      return
    }

    if (anchorEnforceTimerRef.current !== null) {
      window.clearTimeout(anchorEnforceTimerRef.current)
      anchorEnforceTimerRef.current = null
    }

    anchorEnforceKeyRef.current = key

    const delayMs = nextPendingFixedAnchor.scheduledStartMs - stationNowEpochMs
    if (delayMs <= 0) {
      void runFixedAnchorEnforcement(nextPendingFixedAnchor.anchor.id, key)
      return
    }

    anchorEnforceTimerRef.current = window.setTimeout(() => {
      anchorEnforceTimerRef.current = null
      void runFixedAnchorEnforcement(nextPendingFixedAnchor.anchor.id, key)
    }, delayMs)

    return () => {
      if (anchorEnforceTimerRef.current !== null) {
        window.clearTimeout(anchorEnforceTimerRef.current)
        anchorEnforceTimerRef.current = null
      }
    }
  }, [
    catchUpLocked,
    nextPendingFixedAnchor,
    runFixedAnchorEnforcement,
    stationId,
    stationNowEpochMs,
    stationTimeZone,
    targetDayId,
  ])

  React.useEffect(() => {
    if (!holdActive || !warning || typeof warning !== 'object') {
      return
    }
    const ctx = (warning.context ?? null) as { hold_forced_off?: boolean } | null
    if (ctx?.hold_forced_off) {
      setHoldActive(false)
    }
  }, [holdActive, warning])

  React.useEffect(() => {
    if (
      !stationId ||
      stationId <= 0 ||
      !targetDayId ||
      !stationTimeZone ||
      !stationNowEpochMs ||
      loading ||
      lookupLoading
    ) {
      return
    }

    const running = items.find((candidate) => hasValue(candidate.actual_start) && !hasValue(candidate.actual_end))
    if (running) {
      fillerAutoCreateRef.current = null
      return
    }

    const nextPending = items.find((candidate) => !candidate.skipped && !hasValue(candidate.actual_end))
    if (!nextPending || !nextPending.fixed_time || hasValue(nextPending.actual_start)) {
      fillerAutoCreateRef.current = null
      return
    }

    const anchorStartMs = resolveClockEpochMs(nextPending.fixed_time, stationTimeZone)
    if (anchorStartMs === null) {
      return
    }

    const gapMs = anchorStartMs - stationNowEpochMs
    if (gapMs <= 1000) {
      return
    }

    const key = `${nextPending.id}:${anchorStartMs}`
    if (fillerAutoCreateRef.current === key) {
      return
    }

    fillerAutoCreateRef.current = key

    const gapSeconds = Math.max(1, Math.floor(gapMs / 1000))
    const anchorLabel = formatClockTimeLabel(nextPending.fixed_time, stationTimeZone, { withSeconds: true })
    const title = `AUTO FILL (early to fixed anchor ${anchorLabel})`
    const notes = `[RPA_AUTO_FILLER]\nAuto-generated because we arrived early to a fixed-time item.\nAnchor at: ${nextPending.fixed_time}\nAnchor item id: ${nextPending.id}\n`

    const anchorIndex = items.findIndex((candidate) => candidate.id === nextPending.id)
    const afterItemId = anchorIndex > 0 ? items[anchorIndex - 1]?.id ?? null : null
    const insertAtTop = anchorIndex <= 0

    setBusyCount((count) => count + 1)
    ;(async () => {
      try {
        const response = await createClockDayItem(stationId, {
          day_id: targetDayId,
          item_type: 'custom',
          planned_duration_sec: gapSeconds,
          title,
          notes,
          ...(insertAtTop ? {} : { after_item_id: afterItemId ?? undefined }),
        })
        if (Array.isArray(response.items)) {
          setItems(response.items)
        } else if (response.item) {
          addItem(response.item)
        }
        if (response.projection) {
          setProjectionState(response.projection)
        }

        const newItemId =
          response.item?.id ??
          response.items?.find((candidate) => candidate.item_type === 'custom' && candidate.title === title)?.id ??
          null

        if (insertAtTop && newItemId && Array.isArray(response.items)) {
          const order = [newItemId, ...response.items.map((candidate) => candidate.id).filter((id) => id !== newItemId)]
          const reordered = await reorderClockDayItems(stationId, targetDayId, { order })
          if (Array.isArray(reordered.items)) {
            setItems(reordered.items)
          }
          if (reordered.projection) {
            setProjectionState(reordered.projection)
          }
        }

        if (newItemId) {
          const startResponse = await startClockDayItem(newItemId, { stationId, confirm: true })
          if (startResponse) {
            applyActionPayload(startResponse)
          }
        }
      } catch (error) {
        fillerAutoCreateRef.current = null
        console.error('Failed to auto-create filler item', error)
      } finally {
        setBusyCount((count) => Math.max(0, count - 1))
      }
    })()
  }, [
    addItem,
    applyActionPayload,
    createClockDayItem,
    items,
    loading,
    lookupLoading,
    setItems,
    setProjectionState,
    stationId,
    stationNowEpochMs,
    stationTimeZone,
    startClockDayItem,
    targetDayId,
  ])

  const nextTimingStatus = React.useMemo(() => {
    const projectionItems = effectiveProjection?.items ?? []
    if (!projectionItems.length || !stationTimeZone) {
      return null
    }
    const nowMs = effectiveNowMs
    const candidates = projectionItems.filter((entry) => {
      const detail = items.find((candidate) => candidate.id === entry.id)
      if (!detail || detail.skipped) {
        return false
      }
      if (hasValue(detail.actual_end)) {
        return false
      }
      const endMs = resolveClockEpochMs(entry.projected_end, stationTimeZone)
      return endMs === null || endMs >= nowMs - 500
    })

    if (!candidates.length) {
      return null
    }

    const fixedCandidate = candidates.find((entry) => entry.is_fixed) ?? null
    const target = fixedCandidate ?? candidates[0]
    const scheduledEntry = scheduledProjectionMap.get(target.id) ?? null
    const scheduledStartMs = resolveClockEpochMs(
      scheduledEntry?.scheduled_start ?? scheduledEntry?.projected_start ?? target.projected_start,
      stationTimeZone,
    )
    const projectedStartMs = resolveClockEpochMs(target.projected_start, stationTimeZone)

    if (scheduledStartMs === null || projectedStartMs === null) {
      return null
    }

    const deltaSeconds = Math.round((projectedStartMs - scheduledStartMs) / 1000)
    let status: 'on_time' | 'early' | 'late' = 'on_time'
    if (deltaSeconds > 30) {
      status = 'late'
    } else if (deltaSeconds < -30) {
      status = 'early'
    }

    return {
      status,
      deltaSeconds,
      isFixed: Boolean(fixedCandidate),
    }
  }, [effectiveProjection?.items, items, scheduledProjectionMap, stationTimeZone, effectiveNowMs])

  const toggleReconcileCandidate = React.useCallback((itemId: number) => {
    setReconcileCheckedIds((prev) => {
      const next = new Set(prev)
      if (next.has(itemId)) {
        next.delete(itemId)
      } else {
        next.add(itemId)
      }
      return next
    })
  }, [])
  const reconcileSelectedCount = React.useMemo(() => {
    return reconcileCandidates.filter((item) => reconcileCheckedIds.has(item.id)).length
  }, [reconcileCandidates, reconcileCheckedIds])

  const closeReconcileDialog = React.useCallback(() => {
    reconcileForcedOpenRef.current = false
    setReconcilePromptOpen(false)
  }, [])

  const lastReconcileHashRef = React.useRef<string | null>(null)

  const projectionItemsForReconcile = React.useMemo(() => {
    if (scheduledProjection && Array.isArray(scheduledProjection.items) && scheduledProjection.items.length) {
      return scheduledProjection.items
    }
    if (projection && Array.isArray(projection.items) && projection.items.length) {
      return projection.items
    }
    if (reconcileProjection && Array.isArray(reconcileProjection.items) && reconcileProjection.items.length) {
      return reconcileProjection.items
    }
    return []
  }, [projection?.items ?? null, reconcileProjection?.items ?? null, scheduledProjection?.items ?? null])

  const reconcileReady =
    !loading &&
    !lookupLoading &&
    stationTimeZone !== null &&
    baseScheduleEpochMs !== null &&
    items.length > 0 &&
    day !== null &&
    projectionItemsForReconcile.length > 0

  React.useEffect(() => {
    if (!reconcileReady) {
      return
    }
    reconcileReadyRef.current = true

    const nowMs = effectiveNowMs

    let currentIndex = -1
     for (let i = 0; i < projectionItemsForReconcile.length; i += 1) {
       const entry = projectionItemsForReconcile[i]
       const startMs = resolveClockEpochMs(entry.scheduled_start ?? entry.projected_start ?? null, stationTimeZone)
       const endMs = resolveClockEpochMs(entry.scheduled_end ?? entry.projected_end ?? null, stationTimeZone)
       if (startMs === null || endMs === null) {
         continue
       }
       if (endMs <= nowMs) {
         continue
       }
       if (startMs <= nowMs && nowMs < endMs) {
         currentIndex = i
         break
       }
       if (startMs > nowMs) {
         break
       }
    }

    const currentFromProjection =
      currentIndex >= 0
        ? items.find((item) => item.id === projectionItemsForReconcile[currentIndex].id) ?? null
        : null
    setReconcileCurrentItem(currentFromProjection ?? currentItem ?? null)

     const candidates: ClockDayItem[] = []
     const candidateLimit = currentIndex >= 0 ? currentIndex : projectionItemsForReconcile.length
     for (let i = 0; i < candidateLimit; i += 1) {
       const entry = projectionItemsForReconcile[i]
       const scheduledEnd = resolveClockEpochMs(entry.scheduled_end ?? entry.projected_end ?? null, stationTimeZone)
       if (scheduledEnd === null || scheduledEnd > nowMs) {
         continue
       }
       const detail = items.find((item) => item.id === entry.id) ?? null
       if (!detail || detail.skipped || hasValue(detail.actual_end)) {
         continue
      }
      candidates.push(detail)
    }

    const behindSeconds = Math.min(
      0,
      projection?.ahead_behind_total ?? reconcileProjection?.ahead_behind_total ?? 0,
    )
    const hashSource = `${day.id}:${items.length}:${candidates.map((c) => c.id).join(',')}:${behindSeconds}:${currentIndex}`

    if (hashSource === lastReconcileHashRef.current && !reconcileForcedOpenRef.current) {
      return
    }

    lastReconcileHashRef.current = hashSource
    setReconcileCandidates(candidates)
    setReconcileCheckedIds(new Set<number>(candidates.map((c) => c.id)))
    setReconcileBacklog(candidates.length > 0)

    if (candidates.length === 0) {
      if (!reconcileApplying) {
        if (catchUpLocked) {
          setReconcilePromptOpen(false)
        } else {
          closeReconcileDialog()
        }
      }
      return
    }

    if (reconcileForcedOpenRef.current) {
      setReconcilePromptOpen(true)
      return
    }

    if (Date.now() < reconcileSuppressUntilRef.current) {
      setReconcilePromptOpen(false)
      return
    }

    const shouldPrompt =
      candidates.length >= MISSED_ITEMS_THRESHOLD || behindSeconds <= -LAG_THRESHOLD_SECONDS

    if (!shouldPrompt) {
      return
    }

    const lastPrompt = reconcileCooldownRef.current
    if (lastPrompt !== null && nowMs - lastPrompt < RECONCILE_COOLDOWN_MS) {
      return
    }

    reconcileCooldownRef.current = nowMs
    reconcileForcedOpenRef.current = true
    setReconcilePromptOpen(true)
  }, [
    catchUpLocked,
    closeReconcileDialog,
    day,
    items,
    projection,
    reconcileApplying,
    reconcileProjection?.ahead_behind_total,
    reconcileProjection?.items,
    stationNowEpochMs,
    stationTimeZone,
  ])

  const itemIndexMap = React.useMemo(() => {
    const map = new Map<number, number>()
    items.forEach((clockItem, index) => {
      if (clockItem?.id) {
        map.set(clockItem.id, index)
      }
    })
    return map
  }, [items])

  const scrollItemIntoView = React.useCallback(
    (itemId: number, behavior: ScrollBehavior = 'smooth') => {
      const container = scrollContainerRef.current
      const node = itemRefs.current.get(itemId)
      if (!container || !node) {
        return
      }
      const offset = 12 // keep a small gap below the header
      const containerRect = container.getBoundingClientRect()
      const nodeRect = node.getBoundingClientRect()
      const currentScroll = container.scrollTop
      const targetTop = nodeRect.top - containerRect.top + currentScroll - offset
      const clampedTop = Math.max(0, Math.min(targetTop, container.scrollHeight - container.clientHeight))
      container.scrollTo({ top: clampedTop, behavior })
    },
    [],
  )

  const handleScrollToCurrent = React.useCallback(() => {
    if (!currentItemId) {
      return
    }
    scrollItemIntoView(currentItemId)
  }, [currentItemId, scrollItemIntoView])

  // If we are late and nothing is running (e.g., after reconcile), start the first pending item whose scheduled start has passed.
  React.useEffect(() => {
    if (
      !stationId ||
      stationId <= 0 ||
      !stationTimeZone ||
      loading ||
      lookupLoading ||
      holdActive ||
      catchUpLocked ||
      reconcilePromptOpen ||
      reconcileApplying
    ) {
      return
    }

    const running = items.find((candidate) => hasValue(candidate.actual_start) && !hasValue(candidate.actual_end))
    if (running) {
      return
    }
    const nextCurrent = items.find((candidate) => !candidate.skipped && !hasValue(candidate.actual_end))
    if (!nextCurrent || autoStartForcedRef.current.has(nextCurrent.id)) {
      return
    }

    const scheduledEntry = scheduledProjectionMap.get(nextCurrent.id) ?? null
    const projectionEntry = projectionMap.get(nextCurrent.id) ?? null
    const startInstant =
      nextCurrent.fixed_time ??
      scheduledEntry?.scheduled_start ??
      scheduledEntry?.projected_start ??
      projectionEntry?.projected_start ??
      null
    const projectedStartMs = resolveClockEpochMs(startInstant, stationTimeZone)
    if (projectedStartMs === null) {
      return
    }
    if (projectedStartMs - effectiveNowMs > 1000) {
      return
    }

    autoStartForcedRef.current.add(nextCurrent.id)
    setBusyCount((count) => count + 1)
    ;(async () => {
      try {
        const startResponse = await startClockDayItem(nextCurrent.id, {
          stationId,
          projectedStart: startInstant,
          confirm: true,
        })
        if (startResponse) {
          applyActionPayload(startResponse)
        }
      } catch (error) {
        console.error('Failed to auto-start pending item after reconcile/late start', error)
      } finally {
        setBusyCount((count) => Math.max(0, count - 1))
      }
    })()
  }, [
    applyActionPayload,
    catchUpLocked,
    effectiveNowMs,
    holdActive,
    items,
    loading,
    lookupLoading,
    projectionMap,
    reconcileApplying,
    reconcilePromptOpen,
    scheduledProjectionMap,
    startClockDayItem,
    stationId,
    stationTimeZone,
  ])

  const resolveInsertDuration = React.useCallback(
    (itemType: string, target: ClockDayItem | null) => {
      const meta = itemTypeMap.get(itemType)
      const metaDefault = Number.isFinite(meta?.defaultDurationSeconds ?? Number.NaN)
        ? Math.max(0, Math.round(meta?.defaultDurationSeconds ?? 0))
        : null

      if (itemType === 'song') {
        return metaDefault ?? INSERT_SONG_DEFAULT_SECONDS
      }

      if (itemType === 'link' || itemType === 'custom') {
        return 120
      }

      const targetDuration = Number.isFinite(target?.planned_duration_sec ?? Number.NaN)
        ? Math.max(0, Math.round(target?.planned_duration_sec ?? 0))
        : null

      if (Number.isFinite(targetDuration ?? Number.NaN) && (targetDuration ?? 0) > 0) {
        return targetDuration as number
      }

      if (metaDefault !== null) {
        return metaDefault
      }

      return UNKNOWN_DURATION_FALLBACK_SECONDS
    },
    [itemTypeMap],
  )

  const insertItemBelow = React.useCallback(
    async (itemType: string) => {
      const target = rowMenuState?.item ?? null
      closeRowMenu()
      if (!target || !stationId || !targetDayId) {
        return
      }
      setBusyCount((count) => count + 1)
      const previousIds = new Set(items.map((candidate) => candidate.id))
      const duration = resolveInsertDuration(itemType, target)
      try {
        const response = await createClockDayItem(stationId, {
          day_id: targetDayId,
          item_type: itemType,
          planned_duration_sec: duration,
          after_item_id: target.id,
        })
        if (Array.isArray(response.items)) {
          setItems(response.items)
        } else if (response.item) {
          addItem(response.item)
        }
        if (response.projection) {
          setProjectionState(response.projection)
        }
        const insertedId =
          response.item?.id ??
          response.items?.find((candidate) => !previousIds.has(candidate.id))?.id ??
          null
        if (insertedId) {
          scrollItemIntoView(insertedId)
        }
        setSnackbar({ message: 'Item inserted below.', severity: 'success' })
      } catch (error) {
        setSnackbar({ message: getErrorMessage(error, 'Unable to insert item.'), severity: 'error' })
      } finally {
        setBusyCount((count) => Math.max(0, count - 1))
      }
    },
    [
      addItem,
      closeRowMenu,
      items,
      resolveInsertDuration,
      rowMenuState,
      scrollItemIntoView,
      setItems,
      setProjectionState,
      setSnackbar,
      stationId,
      targetDayId,
    ],
  )

  const handleAddSongBelow = React.useCallback(() => {
    void insertItemBelow('song')
  }, [insertItemBelow])

  const handleAddLinkBelow = React.useCallback(() => {
    void insertItemBelow('link')
  }, [insertItemBelow])

  const handleAddCustomBelow = React.useCallback(() => {
    void insertItemBelow('custom')
  }, [insertItemBelow])

  const handleRemoveItem = React.useCallback(async () => {
    const target = rowMenuState?.item ?? null
    closeRowMenu()
    if (!target) {
      return
    }
    setDeleteTarget(target)
  }, [closeRowMenu, rowMenuState])

  const handleConfirmRemove = React.useCallback(async () => {
    const target = deleteTarget
    if (!target || !stationId) {
      return
    }
    setBusyCount((count) => count + 1)
    setDeleteLoading(true)
    try {
      const response = await deleteClockDayItem(stationId, target.id)
      if (Array.isArray(response.items)) {
        setItems(response.items)
      } else {
        setItems((prev) => prev.filter((item) => item.id !== target.id))
      }
      if (response.projection) {
        setProjectionState(response.projection)
      }
      setSnackbar({ message: 'Item removed.', severity: 'success' })
      setDeleteTarget(null)
    } catch (error) {
      setSnackbar({ message: getErrorMessage(error, 'Unable to remove item.'), severity: 'error' })
    } finally {
      setDeleteLoading(false)
      setBusyCount((count) => Math.max(0, count - 1))
    }
  }, [deleteTarget, setItems, setProjectionState, setSnackbar, stationId])

  const handleCloseRemove = React.useCallback(() => {
    if (deleteLoading) {
      return
    }
    setDeleteTarget(null)
  }, [deleteLoading])

  const currentTitle = React.useMemo(
    () => (currentItem ? displayTitles[currentItem.id] ?? currentItem.title ?? '' : ''),
    [currentItem, displayTitles],
  )
  const nextTitle = React.useMemo(
    () => (nextItem ? displayTitles[nextItem.id] ?? nextItem.title ?? '' : ''),
    [displayTitles, nextItem],
  )

  const projectionDriftSeconds = React.useMemo(() => {
    if (!stationTimeZone) {
      return 0
    }
    const nowMs = stationNowEpochMs ?? Date.now()

    for (const item of items) {
      if (!item || !item.fixed_time || item.skipped || hasValue(item.actual_start)) {
        continue
      }
      const scheduledEntry = scheduledProjectionMap.get(item.id) ?? null
      const anchorLabel = scheduledEntry?.scheduled_start ?? item.fixed_time
      const anchorMs = resolveClockEpochMs(anchorLabel, stationTimeZone)
      if (anchorMs === null) {
        continue
      }
      if (anchorMs < nowMs - 1000) {
        continue
      }
      const projectionEntry = projectionMap.get(item.id) ?? null
      const projectedLabel = projectionEntry?.projected_start ?? scheduledEntry?.projected_start ?? anchorLabel
      const projectedMs = resolveClockEpochMs(projectedLabel, stationTimeZone)
      if (projectedMs === null) {
        continue
      }
      return Math.round((projectedMs - anchorMs) / 1000)
    }

    const total = effectiveProjection?.ahead_behind_total
    if (!Number.isFinite(total ?? Number.NaN)) {
      return 0
    }
    return Math.round(Number(total))
  }, [
    effectiveProjection?.ahead_behind_total,
    items,
    projectionMap,
    scheduledProjectionMap,
    stationNowEpochMs,
    stationTimeZone,
  ])

  const truncateLabel = React.useCallback((value: string, limit = 40) => {
    if (!value) {
      return value
    }
    if (value.length <= limit) {
      return value
    }
    return `${value.slice(0, limit - 1)}…`
  }, [])

  const applyAutoExpandForCurrent = React.useCallback(
    (nextCurrentId: number | null) => {
      if (!nextCurrentId) {
        setExpandedIds(new Set<number>())
        setAutoExpandCandidateId(null)
        return
      }

      const current = items.find((candidate) => candidate.id === nextCurrentId) ?? null
      const hasNotes = Boolean(current?.notes?.trim())
      const prepState = prepAttachments[nextCurrentId]
      const hasPrep = (prepState?.attachments?.length ?? 0) > 0

      if (hasNotes || hasPrep) {
        setExpandedIds(new Set<number>([nextCurrentId]))
        setAutoExpandCandidateId(null)
      } else {
        setExpandedIds(new Set<number>())
        setAutoExpandCandidateId(nextCurrentId)
      }
      const readyForPrepFetch =
        initializedRef.current &&
        !loading &&
        !lookupLoading &&
        day !== null &&
        Array.isArray(items) &&
        items.length > 0 &&
        error === null
      if (readyForPrepFetch) {
        void ensurePrepLoadedWithBusy(nextCurrentId)
      }
    },
    [day, ensurePrepLoadedWithBusy, error, items, loading, lookupLoading, prepAttachments],
  )

  React.useEffect(() => {
    if (!autoExpandCandidateId) {
      return
    }
    const candidate = items.find((item) => item.id === autoExpandCandidateId) ?? null
    if (!candidate) {
      setAutoExpandCandidateId(null)
      return
    }

    if (candidate.notes && candidate.notes.trim()) {
      setExpandedIds(new Set<number>([autoExpandCandidateId]))
      setAutoExpandCandidateId(null)
      return
    }

    const prepState = prepAttachments[autoExpandCandidateId]
    if (!prepState || prepState.loading) {
      return
    }

    if (prepState.attachments.length > 0) {
      setExpandedIds(new Set<number>([autoExpandCandidateId]))
    }
    setAutoExpandCandidateId(null)
  }, [autoExpandCandidateId, items, prepAttachments])

  React.useEffect(() => {
    if (loading || !currentItemId) {
      return
    }
    window.requestAnimationFrame(() => {
      scrollItemIntoView(currentItemId)
    })
  }, [currentItemId, loading, scrollItemIntoView])

  const headerInfo = React.useMemo(() => {
    const trimmedTitle = day?.title?.trim() ?? ''
    let windowLabel: string | null = null

    if (day) {
      const startLabel = formatClockTimeLabel(day.window_start, stationTimeZone, {
        withSeconds: false,
        twentyFourHour: true,
      })
      const endLabel = formatClockTimeLabel(day.window_end, stationTimeZone, {
        withSeconds: false,
        twentyFourHour: true,
      })
      if (startLabel && endLabel) {
        windowLabel = `${startLabel} – ${endLabel}`
      } else if (startLabel) {
        windowLabel = startLabel
      } else if (endLabel) {
        windowLabel = endLabel
      }
    }

      let dynamicTitle = 'Live Show Clock'
      if (trimmedTitle) {
        dynamicTitle = windowLabel ? `Live Show Clock: ${trimmedTitle} – ${windowLabel}` : `Live Show Clock: ${trimmedTitle}`
      } else if (windowLabel) {
        dynamicTitle = `Live Show Clock: ${windowLabel}`
      }

      return {
        dynamicTitle,
        currentChipLabel: currentTitle || 'Current',
        nextChipLabel: nextTitle || 'Next',
        currentTypeKey: currentItem?.item_type ?? '',
        nextTypeKey: nextItem?.item_type ?? '',
        driftSeconds: projectionDriftSeconds,
      }
    }, [currentItem, currentTitle, day, nextItem, nextTitle, projectionDriftSeconds, stationTimeZone])

  usePageTitle(headerInfo.dynamicTitle, { showBreadcrumbs: false })

  const truncatedCurrentChipLabel = React.useMemo(
    () => truncateLabel(headerInfo.currentChipLabel),
    [headerInfo.currentChipLabel, truncateLabel],
  )
  const headerCurrentMeta = React.useMemo(
    () => (headerInfo.currentTypeKey ? itemTypeMap.get(headerInfo.currentTypeKey) : undefined),
    [headerInfo.currentTypeKey, itemTypeMap],
  )
  const headerNextMeta = React.useMemo(
    () => (headerInfo.nextTypeKey ? itemTypeMap.get(headerInfo.nextTypeKey) : undefined),
    [headerInfo.nextTypeKey, itemTypeMap],
  )
  const resolveHeaderSolidColor = React.useCallback(
    (token?: string) => {
      switch (token) {
        case 'primaryContainer':
          return theme.palette.primary.main
        case 'secondaryContainer':
          return theme.palette.secondary.main
        case 'tertiaryContainer':
          return theme.palette.info.main
        case 'errorContainer':
          return theme.palette.error.main
        case 'surfaceContainerHigh':
          return alpha(theme.palette.common.white, 0.2)
        default:
          return theme.palette.primary.main
      }
    },
    [theme],
  )
  const headerCurrentBg = React.useMemo(
    () => resolveHeaderSolidColor(headerCurrentMeta?.colorToken),
    [headerCurrentMeta?.colorToken, resolveHeaderSolidColor],
  )
  const headerNextBg = React.useMemo(
    () => resolveHeaderSolidColor(headerNextMeta?.colorToken),
    [headerNextMeta?.colorToken, resolveHeaderSolidColor],
  )
  const truncatedNextChipLabel = React.useMemo(
    () => truncateLabel(headerInfo.nextChipLabel),
    [headerInfo.nextChipLabel, truncateLabel],
  )

  React.useEffect(() => {
    const readyForPrepFetch =
      initializedRef.current &&
      !loading &&
      !lookupLoading &&
      error === null &&
      day !== null &&
      Array.isArray(items) &&
      items.length > 0

    if (!readyForPrepFetch) {
      return
    }
    if (!currentItemId) {
      prevCurrentIdRef.current = null
      applyAutoExpandForCurrent(null)
      return
    }
    if (currentItemId === prevCurrentIdRef.current) {
      return
    }
    prevCurrentIdRef.current = currentItemId
    if (suppressAutoScrollRef.current) {
      suppressAutoScrollRef.current = false
      applyAutoExpandForCurrent(currentItemId)
      return
    }
    applyAutoExpandForCurrent(currentItemId)
    window.requestAnimationFrame(() => {
      scrollItemIntoView(currentItemId)
    })
  }, [applyAutoExpandForCurrent, currentItemId, day, error, items, loading, lookupLoading, scrollItemIntoView])


  const refreshProjection = React.useCallback(async () => {
    if (!stationId || stationId <= 0 || !targetDayId) {
      return null
    }
    try {
      const projectionData = await estimateClockDay({
        dayId: targetDayId,
        stationId,
      })
      setProjectionState(projectionData)
      return projectionData
    } catch (projectionError) {
      console.error('Failed to refresh show clock projection', projectionError)
      return null
    }
  }, [setProjectionState, stationId, targetDayId])

  const handleRunReconcile = React.useCallback(async () => {
    if (!reconcileReady) {
      setSnackbar({
        message: 'Reconcile data is still loading. Please try again in a moment.',
        severity: 'info',
      })
      return
    }
    if (reconcileApplying) {
      return
    }
    const selected = reconcileCandidates.filter((item) => reconcileCheckedIds.has(item.id))
    if (!selected.length) {
      closeReconcileDialog()
      reconcileCooldownRef.current = Date.now()
      return
    }

    if (!stationId || stationId <= 0 || !day?.id) {
      setSnackbar({
        message: 'A valid station and show clock are required to reconcile items.',
        severity: 'error',
      })
      return
    }

    setReconcileApplying(true)
    try {
      const currentForReconcile = reconcileCurrentItem ?? currentItem ?? null
      const response = await reconcileClockDay({
        dayId: day.id,
        stationId,
        currentItemId: currentForReconcile?.id ?? null,
      })
      if (response?.items) {
        response.items.forEach((item) => {
          applyActionPayload({
            station_id: stationId,
            day_id: day.id,
            day_item_id: item.id,
            item,
            projection: response.projection,
          } as ClockLiveActionResponse)
        })
      }
      setCatchUpLockActive(false)
      // Start the next current item if it should already be running
      const nowMs = effectiveNowMs
      const liveState = useShowClockLiveStore.getState()
      const liveItems = liveState.items ?? []
      const running = liveItems.find((candidate) => hasValue(candidate.actual_start) && !hasValue(candidate.actual_end))
      if (!running) {
        const nextCurrent = liveItems.find((candidate) => !candidate.skipped && !hasValue(candidate.actual_end))
        if (nextCurrent && !hasValue(nextCurrent.actual_start)) {
          const baseline = reconcileProjection?.items?.find((entry) => entry.id === nextCurrent.id) ?? null
          const scheduled = scheduledProjectionMap.get(nextCurrent.id) ?? null
          const startLabel =
            nextCurrent.fixed_time ??
            baseline?.scheduled_start ??
            scheduled?.scheduled_start ??
            scheduled?.projected_start ??
            null
          const scheduledStartMs = resolveClockEpochMs(startLabel, stationTimeZone)
          if (scheduledStartMs !== null && scheduledStartMs <= nowMs) {
            try {
              const startResponse = await startClockDayItem(nextCurrent.id, {
                stationId,
                projectedStart: startLabel,
                confirm: true,
              })
              if (startResponse) {
                applyActionPayload(startResponse)
              }
            } catch (startError) {
              console.error('Failed to start next current item after reconcile', startError)
            }
          }
        }
      }
      reconcileCooldownRef.current = Date.now()
      setSnackbar({
        message: `Reconciled ${selected.length} item${selected.length === 1 ? '' : 's'}.`,
        severity: 'success',
      })
      await refreshProjection()
      closeReconcileDialog()
    } catch (error) {
      setSnackbar({
        message: getErrorMessage(error, `Unable to reconcile selected items.`),
        severity: 'error',
      })
    } finally {
      setReconcileApplying(false)
    }
  }, [
    applyActionPayload,
    setCatchUpLockActive,
    currentItemId,
    day?.id,
    reconcileApplying,
    reconcileCandidates,
    reconcileCheckedIds,
    reconcileClockDay,
    reconcileProjection?.items,
    scheduledProjectionMap,
    refreshProjection,
    setSnackbar,
    stationId,
    stationNowEpochMs,
    stationTimeZone,
    startClockDayItem,
  ])

  const handleOpenReconcileDialog = React.useCallback(() => {
    if (!reconcileReady) {
      setSnackbar({
        message: 'Reconcile data is still loading. Please try again shortly.',
        severity: 'info',
      })
      return
    }
    reconcileForcedOpenRef.current = true
    setReconcilePromptOpen(true)
  }, [reconcileReady, setSnackbar])

  React.useEffect(() => {
    if (autoAdvanceTimerRef.current !== null) {
      clearTimeout(autoAdvanceTimerRef.current)
      autoAdvanceTimerRef.current = null
    }

    if (!stationId || stationId <= 0 || stationTimeZoneFallbackActive) {
      return
    }
    if (
      !reconcileReady ||
      catchUpLocked ||
      reconcilePromptOpen ||
      reconcileApplying ||
      !currentItem ||
      !day ||
      holdActive
    ) {
      return
    }

    const nextItemForAuto = currentItem ? findNextItem(items, currentItem) : null
    if (!nextItemForAuto) {
      return
    }

    const shouldAutoAdvanceCurrent =
      liveAdvanceMode === 'auto' || (liveAdvanceMode === 'hybrid' && Boolean(currentItem.auto_advance))
    if (!shouldAutoAdvanceCurrent) {
      return
    }

    const projectionEntry = projectionMap.get(currentItem.id) ?? null
    const scheduledEntry = scheduledProjectionMap.get(currentItem.id) ?? null
    if (!projectionEntry && !scheduledEntry) {
      return
    }

    const projectedEndEpochMs = projectionEntry
      ? resolveClockEpochMs(projectionEntry.projected_end ?? null, stationTimeZone)
      : null
    if (projectedEndEpochMs === null) {
      return
    }

    const dwellMs = AUTO_DWELL_SECONDS * 1000
    const effectiveNowForTimerMs = (() => {
      const baseNow = latestStationNowRef.current ?? Date.now()
      const runningHold = holdStartRef.current !== null ? Math.max(0, baseNow - holdStartRef.current) : 0
      const totalOffset = holdOffsetMsRef.current + runningHold
      const adjusted = baseNow - totalOffset
      return holdActive ? (holdFreezeRef.current ?? adjusted) : adjusted
    })()
    const delayMs = Math.max(0, projectedEndEpochMs + dwellMs - effectiveNowForTimerMs)

    const runAutoAdvance = async () => {
      const nowTs = Date.now()
      const cooldown = autoAdvanceCooldownRef.current.get(currentItem.id) ?? null
      if (cooldown !== null && nowTs - cooldown < 2000) {
        return
      }
      if (autoAdvanceInFlightRef.current || autoAdvanceLockRef.current.has(currentItem.id)) {
        return
      }
      if (autoAdvanceStateRef.current.runningId && autoAdvanceStateRef.current.runningId !== currentItem.id) {
        autoAdvanceControllerRef.current?.abort()
      }

      let controller: AbortController | null = null
      try {
        controller = new AbortController()
        autoAdvanceControllerRef.current = controller
      } catch (error) {
        console.error('AbortController unavailable for auto-advance', error)
        return
      }

      const fallbackProjectedStart = (() => {
        const startInstant =
          scheduledEntry?.scheduled_start ??
          scheduledEntry?.projected_start ??
          projectionEntry?.projected_start ??
          (currentItem.actual_start
            ? {
                stationLocal: currentItem.actual_start,
              }
            : null)
        const startEpoch = resolveClockEpochMs(startInstant ?? null, stationTimeZone)
        if (startEpoch === null) {
          return null
        }
        return formatStationDateTime(startEpoch, stationTimeZone)
      })()

      autoAdvanceInFlightRef.current = true
      autoAdvanceStateRef.current.lastAttempt = { id: currentItem.id, at: nowTs }
      autoAdvanceStateRef.current.runningId = currentItem.id
      autoAdvanceLockRef.current.add(currentItem.id)

      try {
        setBusyCount((count) => count + 1)
        flushSync(() => {
          setExpandedIds(new Set<number>())
        })
        const response = await autoAdvanceClockDayItem(currentItem.id, {
          stationId,
          projectedStart: fallbackProjectedStart ?? projectionEntry?.projected_start ?? null,
          signal: controller.signal,
        })
        if (response) {
          suppressAutoScrollRef.current = true
          if (response.ended_item) {
            applyActionPayload({
              station_id: response.station_id,
              day_id: response.day_id,
              day_item_id: response.ended_item.id,
              item: response.ended_item,
              projection: response.projection,
            } as ClockLiveActionResponse)
          }
          applyActionPayload(response)
          reconcileCooldownRef.current = Date.now()
          autoAdvanceCooldownRef.current.set(currentItem.id, Date.now())
          const latestItems = useShowClockLiveStore.getState().items ?? []
          const latestCurrent = findCurrentItem(latestItems)
          const latestNext = findNextItem(latestItems, latestCurrent)
          if (latestCurrent?.id && latestNext?.id) {
            flushSync(() => {
              setExpandedIds(new Set<number>([latestNext.id]))
            })
            scrollItemIntoView(latestNext.id)
          } else {
            applyAutoExpandForCurrent(latestCurrent?.id ?? null)
          }
          suppressAutoScrollRef.current = false
        }
      } catch (error) {
        if ((error as Error)?.name === 'AbortError') {
          return
        }
        console.error('Failed to auto-advance clock item', error)
        const message = getErrorMessage(error, 'Unable to advance clock item automatically.')
        if (message.toLowerCase().includes('dwell/overdue threshold') && fallbackProjectedStart) {
          try {
            const endResponse = await endClockDayItem(currentItem.id, {
              stationId,
              projectedStart: fallbackProjectedStart,
              projectedEnd: scheduledEntry?.scheduled_end ?? scheduledEntry?.projected_end ?? projectionEntry?.projected_end ?? null,
            })
            if (endResponse) {
              applyActionPayload(endResponse)
              reconcileCooldownRef.current = Date.now()
              autoAdvanceCooldownRef.current.set(currentItem.id, Date.now())
              const latestItems = useShowClockLiveStore.getState().items ?? []
              const latestCurrent = findCurrentItem(latestItems)
              const latestNext = findNextItem(latestItems, latestCurrent)
              if (latestNext && !hasValue(latestNext.actual_start)) {
                const nextScheduled = scheduledProjectionMap.get(latestNext.id) ?? null
                const nextStartInstant =
                  nextScheduled?.scheduled_start ?? nextScheduled?.projected_start ?? null
                const nextStartLabel =
                  nextStartInstant && stationTimeZone
                    ? formatStationDateTime(
                        resolveClockEpochMs(nextStartInstant, stationTimeZone) ?? Date.now(),
                        stationTimeZone,
                      )
                    : null
                if (nextStartLabel) {
                  const startResponse = await startClockDayItem(latestNext.id, {
                    stationId,
                    projectedStart: nextStartLabel,
                    confirm: true,
                  })
                  if (startResponse) {
                    applyActionPayload(startResponse)
                    reconcileCooldownRef.current = Date.now()
                    autoAdvanceCooldownRef.current.set(latestNext.id, Date.now())
                  }
                }
              }
            }
          } catch (fallbackError) {
            console.error('Fallback auto-advance failed', fallbackError)
          }
        } else if (message.toLowerCase().includes('auto-advance') || message.toLowerCase().includes('409')) {
          autoAdvanceCooldownRef.current.set(currentItem.id, Date.now())
          if (autoAdvanceTimerRef.current !== null) {
            clearTimeout(autoAdvanceTimerRef.current)
          }
          autoAdvanceTimerRef.current = window.setTimeout(() => {
            void runAutoAdvance()
          }, 3000)
          return
        } else if (isMountedRef.current) {
          setSnackbar({
            message,
            severity: 'error',
          })
        }
      } finally {
        if (autoAdvanceStateRef.current.runningId === currentItem.id) {
          autoAdvanceStateRef.current.runningId = null
        }
        autoAdvanceLockRef.current.delete(currentItem.id)
        autoAdvanceInFlightRef.current = false
        setBusyCount((count) => Math.max(0, count - 1))
      }
    }

    autoAdvanceTimerRef.current = window.setTimeout(() => {
      void runAutoAdvance()
    }, delayMs)

    return () => {
      if (autoAdvanceTimerRef.current !== null) {
        clearTimeout(autoAdvanceTimerRef.current)
        autoAdvanceTimerRef.current = null
      }
    }
  }, [
    applyActionPayload,
    applyAutoExpandForCurrent,
    catchUpLocked,
    currentItem,
    day,
    holdActive,
    liveAdvanceMode,
    items,
    projectionMap,
    reconcileApplying,
    reconcilePromptOpen,
    reconcileReady,
    scheduledProjectionMap,
    stationId,
    stationTimeZone,
    stationTimeZoneFallbackActive,
    setSnackbar,
  ])

  React.useEffect(() => {
    if (catchUpLocked) {
      return
    }
    if (prevHoldActiveRef.current && !holdActive) {
      prevHoldActiveRef.current = holdActive
      const current = currentItem
      if (!current || !isCurrentRunning || !stationNowEpochMs) {
        return
      }
      const shouldAutoAdvanceCurrent =
        liveAdvanceMode === 'auto' || (liveAdvanceMode === 'hybrid' && Boolean(current.auto_advance))
      if (!shouldAutoAdvanceCurrent) {
        return
      }
      const projectionEntry = projectionMap.get(current.id) ?? null
      const projectedEndEpochMs = projectionEntry
        ? resolveClockEpochMs(projectionEntry.projected_end ?? null, stationTimeZone)
        : null
      const nowMs = stationNowEpochMs
      if (projectedEndEpochMs !== null && projectedEndEpochMs > nowMs) {
        return
      }
      void autoAdvanceClockDayItem(current.id, {
        stationId: stationId ?? undefined,
        projectedStart: projectionEntry?.projected_start ?? null,
      })
    } else {
      prevHoldActiveRef.current = holdActive
    }
  }, [catchUpLocked, currentItem, holdActive, isCurrentRunning, liveAdvanceMode, projectionMap, stationId, stationNowEpochMs, stationTimeZone])

  React.useEffect(() => {
    autoAdvanceEffect()
  }, [autoAdvanceEffect])

  React.useEffect(() => {
    return () => {
      autoAdvanceControllerRef.current?.abort()
      if (autoAdvanceRetryTimerRef.current) {
        clearTimeout(autoAdvanceRetryTimerRef.current)
      }
      if (autoAdvanceTimerRef.current !== null) {
        clearTimeout(autoAdvanceTimerRef.current)
        autoAdvanceTimerRef.current = null
      }
    }
  }, [])

  const nextSummary = lookupState?.response.next ?? null
  const nextStationTimeZone = React.useMemo(() => {
    if (!nextSummary?.station_id) {
      return stationTimeZone
    }
    const cached = getCachedStationTimeZone(nextSummary.station_id)
    if (cached) {
      return cached
    }
    if (
      lookupState?.response.station_timezone &&
      lookupState.response.next?.station_id === nextSummary.station_id
    ) {
      return lookupState.response.station_timezone
    }
    return stationTimeZone
  }, [
    getCachedStationTimeZone,
    lookupState,
    nextSummary?.station_id,
    stationTimeZone,
  ])
  const nextCountdownSeconds = React.useMemo(() => {
    if (!lookupState || lookupState.response.current || !lookupState.response.next) {
      return null
    }
    const initial = lookupState.response.seconds_until_next
    if (Number.isFinite(initial) && initial !== null) {
      const referenceNow = stationNowEpochMs ?? Date.now()
      const elapsedMs = Math.max(0, referenceNow - lookupState.fetchedAt)
      const elapsedSeconds = Math.floor(elapsedMs / 1000)
      const remaining = Math.max(0, Math.round(initial) - elapsedSeconds)
      return remaining
    }
    return null
  }, [lookupState, stationNowEpochMs])

  React.useEffect(() => {
    if (!stationNowEpochMs) {
      setStationNowEpochMs(Date.now())
    }
  }, [stationNowEpochMs, setStationNowEpochMs])

  React.useEffect(() => {
    if (!day?.id || !stationTimeZone || !stationNowEpochMs) {
      return
    }
    if (catchUpLocked) {
      return
    }
    if (autoStartForcedRef.current.has(day.id)) {
      return
    }
    const firstItem = items.find((candidate) => !candidate.skipped) ?? null
    if (!firstItem || hasValue(firstItem.actual_start)) {
      return
    }
    const fixedEpoch = resolveClockEpochMs(firstItem.fixed_time ?? null, stationTimeZone)
    const windowStartMs = resolveClockEpochMs(day.window_start ?? null, stationTimeZone)
    const targetStartMs =
      fixedEpoch && fixedEpoch <= stationNowEpochMs
        ? fixedEpoch
        : windowStartMs && windowStartMs <= stationNowEpochMs
          ? windowStartMs
          : null
    if (targetStartMs === null) {
      return
    }

    const projectedStartLabel = formatStationDateTime(targetStartMs, stationTimeZone, {
      withSeconds: true,
      twentyFourHour: true,
    })
    const optimisticItem: ClockDayItem = {
      ...firstItem,
      actual_start: projectedStartLabel,
      actual_start_epoch: Math.round(targetStartMs / 1000),
      actual_start_iso_utc: null,
    }
    const optimisticItems = items.map((entry) => (entry.id === firstItem.id ? optimisticItem : entry))
    const optimisticProjection = computeSequentialProjection({
      items: optimisticItems,
      previousProjection: projection,
      nowEpochMs: stationNowEpochMs,
      stationTimeZone,
      baseStartEpochMs: baseScheduleEpochMs,
    })

    autoStartForcedRef.current.add(day.id)
    void runAction(
      'start',
      firstItem,
      (signal) =>
        startClockDayItem(firstItem.id, {
          stationId,
          signal,
          confirm: true,
          projectedStart: projectedStartLabel ?? undefined,
        }),
      '',
      'Unable to mark this item as started.',
      {
        optimistic: () => {
          updateItem(optimisticItem)
          setProjectionState(optimisticProjection)
        },
        rollback: () => {
          updateItem(firstItem)
          setProjectionState(projection)
        },
      },
    )
  }, [
    baseScheduleEpochMs,
    catchUpLocked,
    day?.id,
    day?.window_start,
    items,
      projection,
    runAction,
    setProjectionState,
    stationId,
    effectiveNowMs,
    stationTimeZone,
    updateItem,
  ])

  React.useEffect(() => {
    if (!day?.id || items.length === 0) {
      return
    }
    const baseEpoch = stationNowBaseRef.current?.epochMs ?? Date.now()
    projectionNowRef.current = Number.isFinite(baseEpoch) ? baseEpoch : Date.now()
    // Avoid tying projection recomputes to the ticking clock; only refresh when inputs change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day?.id, items.length, stationTimeZone])

  const nextCountdownLabel = formatCountdownLabel(nextCountdownSeconds)
  const nextStartEpochMs = React.useMemo(() => {
    const state = lookupState
    if (!state || !state.response.next) {
      return null
    }
    const epochSeconds = state.response.next_start_epoch
    if (epochSeconds !== null && epochSeconds !== undefined) {
      const numericSeconds = Number(epochSeconds)
      if (Number.isFinite(numericSeconds)) {
        const wholeSeconds = Math.trunc(numericSeconds)
        return wholeSeconds * 1000
      }
    }
    const seconds = state.response.seconds_until_next
    if (seconds === null) {
      return null
    }
    const numericSeconds = Number(seconds)
    if (!Number.isFinite(numericSeconds)) {
      return null
    }
    const clampedSeconds = Math.max(0, numericSeconds)
    return state.fetchedAt + clampedSeconds * 1000
  }, [lookupState])
  const nextTimeInfo = React.useMemo(
    () => resolveStationTimeInfo(nextStartEpochMs, nextStationTimeZone, stationNowEpochMs ?? Date.now()),
    [nextStartEpochMs, nextStationTimeZone, stationNowEpochMs],
  )
  const nextPrimaryName = React.useMemo(() => {
    if (!nextSummary) {
      return 'Show clock'
    }
    const explicit = nextSummary.title?.trim() || nextSummary.name?.trim()
    if (explicit) {
      return explicit
    }
    return 'Show clock'
  }, [nextSummary])
  const nextPrimaryLine = React.useMemo(() => {
    const timeSegment = nextTimeInfo.timeLabel || '?'
  const dateSegment = nextTimeInfo.dateLabel ? ` • ${nextTimeInfo.dateLabel}` : ''
    return `${nextPrimaryName} @ ${timeSegment}${dateSegment}`
  }, [nextPrimaryName, nextTimeInfo.dateLabel, nextTimeInfo.timeLabel])

  React.useEffect(() => {
    if (
      resolvedDayId ||
      !lookupState ||
      lookupState.response.current ||
      !lookupState.response.next
    ) {
      return
    }
    if (nextCountdownSeconds !== null && nextCountdownSeconds <= 0 && !lookupLoading) {
      setLookupRequestId((prev) => prev + 1)
    }
  }, [lookupLoading, lookupState, nextCountdownSeconds, resolvedDayId])

  const handleOpenLookupDay = React.useCallback(
    (summary: ClockDaySummary) => {
      if (!summary.id || summary.id <= 0) {
        return
      }
      const nextId = Math.round(summary.id)
      setActiveDayId(nextId)
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        next.set('day_id', String(nextId))
        return next
      }, { replace: true })
    },
    [setSearchParams],
  )

  const handleRetryLookup = React.useCallback(() => {
    setLookupRequestId((prev) => prev + 1)
  }, [])

  const anchorSlackSeconds = React.useMemo(() => {
    // Prefer backend-provided delta to next fixed anchor (early = negative in projection, so invert)
    const deltaSource =
      effectiveProjection && Number.isFinite(effectiveProjection.next_fixed_delta ?? Number.NaN)
        ? Number(effectiveProjection.next_fixed_delta)
        : projection && Number.isFinite(projection.next_fixed_delta ?? Number.NaN)
          ? Number(projection.next_fixed_delta)
          : null
    if (deltaSource !== null) {
      return -deltaSource
    }
    const source = effectiveProjection && effectiveProjection.items && effectiveProjection.items.length
      ? effectiveProjection
      : projection && projection.items && projection.items.length
        ? projection
        : null
    if (!source || !source.items || source.items.length === 0 || !stationNowEpochMs) {
      return null
    }
    const anchorEpochMs = resolveClockEpochMs(source.next_fixed_at ?? null, stationTimeZone)
    if (anchorEpochMs === null) {
      return null
    }
    const nowMs = effectiveNowMs
    let remainingSeconds = 0
    for (const projectionItem of source.items) {
      const detail = items.find((candidate) => candidate.id === projectionItem.id)
      if (!detail || detail.skipped) {
        continue
      }
      const startMs = resolveClockEpochMs(projectionItem.projected_start, stationTimeZone)
      const endMs = resolveClockEpochMs(projectionItem.projected_end, stationTimeZone)
      if (startMs === null || endMs === null) {
        continue
      }
      if (endMs <= nowMs) {
        continue
      }
      const durationSeconds = Math.max(0, Math.round((endMs - Math.max(nowMs, startMs)) / 1000))
      remainingSeconds += durationSeconds
      const isAnchor = projectionItem.is_fixed
      if (isAnchor) {
        break
      }
    }
    const slack = Math.round((anchorEpochMs - nowMs) / 1000 - remainingSeconds)
    return slack
  }, [effectiveNowMs, effectiveProjection, items, projection, stationTimeZone])
  const anchorSlackTier = slackTierForSeconds(anchorSlackSeconds)
  const showReconcileButton = nextTimingStatus?.status === 'late'
  const slackToastRef = React.useRef<{ lastTier: AnchorSlackTier; lastToastAt: number | null }>({
    lastTier: 'unknown',
    lastToastAt: null,
  })
  const timingStatusDisplay = React.useMemo(() => {
    if (!nextTimingStatus) {
      return null
    }
    const { deltaSeconds, status, isFixed } = nextTimingStatus
    const abs = Math.abs(deltaSeconds)
    const minutes = Math.floor(abs / 60)
    const secs = abs % 60
    const formatted = abs === 0 ? '0s' : minutes > 0 ? `${minutes}:${secs.toString().padStart(2, '0')}` : `${secs}s`
    const sign = deltaSeconds > 0 ? '+' : deltaSeconds < 0 ? '-' : ''
    const statusLabel = status === 'late' ? 'Late' : status === 'early' ? 'Early' : 'On time'
    const color: 'default' | 'success' | 'warning' | 'error' =
      status === 'late' ? 'error' : status === 'early' ? 'success' : 'default'
    const prefix = isFixed ? 'Fixed' : 'Next'
    const deltaLabel = status === 'on_time' ? '' : ` ${sign}${formatted}`
    return {
      label: `${prefix}: ${statusLabel}${deltaLabel}`,
      color,
    }
  }, [nextTimingStatus])
  const anchorSlackDisplay = React.useMemo(() => {
    if (!Number.isFinite(anchorSlackSeconds ?? Number.NaN) || anchorSlackSeconds === null) {
      return null
    }
    const seconds = Number(anchorSlackSeconds)
    const abs = Math.abs(seconds)
    const sign = seconds > 0 ? '+' : seconds < 0 ? '−' : ''
    const minutes = Math.floor(abs / 60)
    const secs = abs % 60
    const formatted = minutes > 0 ? `${minutes}:${secs.toString().padStart(2, '0')}` : `${secs}s`
    const label =
      anchorSlackTier === 'ahead'
        ? `Anchor +${formatted}`
        : anchorSlackTier === 'tight'
          ? `Anchor tight (${sign}${formatted})`
          : `Anchor ${sign}${formatted}`
    const color: 'success' | 'warning' | 'error' | 'default' =
      anchorSlackTier === 'ahead'
        ? 'success'
        : anchorSlackTier === 'tight'
          ? 'warning'
          : anchorSlackTier === 'behindOrange'
            ? 'warning'
            : anchorSlackTier === 'behindRed'
              ? 'error'
              : 'default'
    return { label, color }
  }, [anchorSlackSeconds, anchorSlackTier])

  React.useEffect(() => {
    const nowMs = effectiveNowMs ?? Date.now()
    const lastState = slackToastRef.current
    if (!anchorSlackSeconds || anchorSlackTier === 'unknown') {
      return
    }
    const hotTiers: AnchorSlackTier[] = ['tight', 'behindOrange', 'behindRed']
    const isHot = hotTiers.includes(anchorSlackTier)
    const transitioned = anchorSlackTier !== lastState.lastTier
    const cooldownMs = 30_000
    const cooled =
      lastState.lastToastAt === null ? true : nowMs - lastState.lastToastAt >= cooldownMs
    if ((isHot || anchorSlackTier === 'ahead') && (transitioned || cooled)) {
      slackToastRef.current = { lastTier: anchorSlackTier, lastToastAt: nowMs }
      const label =
        anchorSlackTier === 'tight'
          ? 'Slack is tight'
          : anchorSlackTier === 'behindOrange' || anchorSlackTier === 'behindRed'
            ? 'Behind next anchor'
            : 'Running early for anchor'
      setSnackbar({
        message: `${label} (${anchorSlackSeconds > 0 ? '+' : ''}${anchorSlackSeconds}s)`,
        severity:
          anchorSlackTier === 'behindRed'
            ? 'error'
            : anchorSlackTier === 'behindOrange' || anchorSlackTier === 'tight'
              ? 'warning'
              : 'info',
      })
    } else if (transitioned) {
      slackToastRef.current = { lastTier: anchorSlackTier, lastToastAt: lastState.lastToastAt }
    }
  }, [anchorSlackSeconds, anchorSlackTier, setSnackbar, stationNowEpochMs])

  const headerEndsInSeconds = React.useMemo(() => {
    if (!currentItem || !stationNowEpochMs) {
      return null
    }
    const projectionEntry = projectionMap.get(currentItem.id) ?? null
    const projectedEndMs = projectionEntry ? resolveClockEpochMs(projectionEntry.projected_end, stationTimeZone) : null
    if (projectedEndMs === null) {
      return null
    }
    return Math.max(0, Math.round((projectedEndMs - stationNowEpochMs) / 1000))
  }, [currentItem, projectionMap, stationNowEpochMs, stationTimeZone])

  const nextHintLabel = React.useMemo(() => {
    if (!nextItem) {
      return null
    }
    const label = displayTitles[nextItem.id] ?? nextItem.title ?? 'Next'
    const projectionEntry = projectionMap.get(nextItem.id) ?? null
    const projectedStartMs = projectionEntry
      ? resolveClockEpochMs(projectionEntry.projected_start, stationTimeZone)
      : null
    const startLabel =
      projectedStartMs !== null
        ? formatClockTimeLabel(projectedStartMs, stationTimeZone, { withSeconds: false, twentyFourHour: true })
        : null
    return startLabel ? `${label} @ ${startLabel}` : label
  }, [displayTitles, nextItem, projectionMap, stationTimeZone])

  const anchorChip = React.useMemo(() => {
    if (!projection || !projection.items || projection.items.length === 0) {
      return null
    }

    for (const projectionItem of projection.items) {
      if (!projectionItem.is_fixed) {
        continue
      }
      const detail = items.find((candidate) => candidate.id === projectionItem.id) ?? null
      if (!detail || detail.skipped) {
        continue
      }
      if (hasValue(detail.actual_start) || hasValue(detail.actual_end)) {
        continue
      }
      const projectedStartMs = resolveClockEpochMs(projectionItem.projected_start, stationTimeZone)
      const fixedEpochMs = resolveClockEpochMs(detail.fixed_time, stationTimeZone)
      if (projectedStartMs === null || fixedEpochMs === null) {
        continue
      }
      const deltaSeconds = Math.round((projectedStartMs - fixedEpochMs) / 1000)
      if (deltaSeconds === 0) {
        return null
      }
      const formattedDelta = formatClockDuration(Math.abs(deltaSeconds))
      const fixedLabel = formatClockTimeLabel(detail.fixed_time, stationTimeZone)
      const prefix = fixedLabel && fixedLabel !== '?' ? `${fixedLabel} – ` : ''
      const tone: 'late' | 'early' = deltaSeconds > 0 ? 'late' : 'early'
      return {
        label: `${prefix}${tone === 'late' ? 'Late' : 'Early'} by ${formattedDelta}`,
        tone,
      }
    }

    return null
  }, [getHoldAccumMs, items, projection, stationTimeZone])

  const reconcileButtonColor =
    anchorSlackTier === 'behindRed'
      ? 'error'
      : anchorSlackTier === 'behindOrange'
        ? 'warning'
        : anchorSlackTier === 'tight'
          ? 'warning'
          : anchorSlackTier === 'ahead'
            ? 'success'
            : 'primary'
  const reconcileButtonVariant = anchorSlackTier === 'ahead' ? 'outlined' : 'contained'

  const warningContext = (warning?.context ?? null) as { anchor_at?: string; seconds_until_anchor?: number } | null
  const isFixedAnchorToastWarning =
    warning?.code === 'anchor_enforced' || warning?.code === 'anchor_already_started'

  React.useEffect(() => {
    if (!error) {
      lastNotifiedErrorRef.current = null
      return
    }
    if (lastNotifiedErrorRef.current === error) {
      return
    }
    lastNotifiedErrorRef.current = error
    setSnackbar({ message: error, severity: 'error' })
  }, [error, setSnackbar])

  React.useEffect(() => {
    if (!stationTimeZoneFallbackActive || hasNotifiedTimeZoneFallbackRef.current) {
      return
    }
    hasNotifiedTimeZoneFallbackRef.current = true
    setSnackbar({
      message: 'Station timezone unavailable; using browser time.',
      severity: 'info',
    })
  }, [setSnackbar, stationTimeZoneFallbackActive])

  React.useEffect(() => {
    if (!warning || isFixedAnchorToastWarning) {
      return
    }
    const anchorAt = warningContext?.anchor_at
      ? formatClockTimeLabel(warningContext.anchor_at, stationTimeZone)
      : null
    setSnackbar({
      message: warning.message,
      details: anchorAt ? `Fixed anchor at ${anchorAt}` : null,
      severity: 'warning',
      autoHideDuration: 20_000,
      big: true,
    })
    clearWarning()
  }, [clearWarning, isFixedAnchorToastWarning, setSnackbar, stationTimeZone, warning, warningContext?.anchor_at])

  React.useEffect(() => {
    if (!warning || !isFixedAnchorToastWarning) {
      return
    }
    const anchorAt = warningContext?.anchor_at
      ? formatClockTimeLabel(warningContext.anchor_at, stationTimeZone)
      : null
    setSnackbar({
      message: warning.message,
      details: anchorAt ? `Fixed anchor at ${anchorAt}` : null,
      severity: 'warning',
      autoHideDuration: 30_000,
      big: true,
    })
    clearWarning()
  }, [clearWarning, isFixedAnchorToastWarning, stationTimeZone, warning, warningContext?.anchor_at])

  const headerTiming = React.useMemo(() => {
    if (!currentItem || !effectiveNowMs) {
      return null
    }
    const projectionEntry = projectionMap.get(currentItem.id) ?? null
    const projectedStartMs = projectionEntry
      ? resolveClockEpochMs(
          projectionEntry.scheduled_start ?? projectionEntry.projected_start ?? currentItem.fixed_time ?? null,
          stationTimeZone,
        )
      : resolveClockEpochMs(currentItem.fixed_time ?? null, stationTimeZone)
    const projectedEndMs = projectionEntry
      ? resolveClockEpochMs(projectionEntry.projected_end, stationTimeZone)
      : null
    const hasActualStart = hasValue(currentItem.actual_start)
    const nowMs = effectiveNowMs
    const fallbackStartMs =
      resolveClockEpochMs(currentItem.actual_start ?? null, stationTimeZone) ??
      resolveClockEpochMs(projectionEntry?.projected_start ?? null, stationTimeZone)
    const plannedDurationMs =
      Number.isFinite(currentItem.planned_duration_sec) && currentItem.planned_duration_sec > 0
        ? currentItem.planned_duration_sec * 1000
        : null
    const computedEndMs =
      projectedEndMs ??
      (fallbackStartMs !== null && plannedDurationMs !== null
        ? fallbackStartMs + plannedDurationMs
        : projectedStartMs !== null && plannedDurationMs !== null
          ? projectedStartMs + plannedDurationMs
          : null)
    const anchorCutoffMs =
      hasActualStart && nextPendingFixedAnchor?.scheduledStartMs
        ? nextPendingFixedAnchor.scheduledStartMs
        : null
    const effectiveEndMs =
      computedEndMs !== null && anchorCutoffMs !== null && anchorCutoffMs > nowMs + 500
        ? Math.min(computedEndMs, anchorCutoffMs)
        : computedEndMs
    const startsInSeconds =
      !hasActualStart && projectedStartMs !== null ? Math.max(0, Math.round((projectedStartMs - nowMs) / 1000)) : null
    const endsInSeconds =
      hasActualStart && effectiveEndMs !== null ? Math.max(0, Math.round((effectiveEndMs - nowMs) / 1000)) : null
    const holdAccumMs = getHoldAccumMs()
    const adjustedProjectedEndMs = effectiveEndMs !== null ? effectiveEndMs + holdAccumMs : null
    const expectedEndLabel =
      adjustedProjectedEndMs !== null
        ? formatClockTimeLabel(adjustedProjectedEndMs, stationTimeZone, { withSeconds: true, twentyFourHour: true })
        : null
    let progress: number | null = null
    if (hasActualStart && projectedStartMs !== null && projectedEndMs !== null) {
      const total = Math.max(1, projectedEndMs - projectedStartMs)
      const elapsed = Math.min(total, Math.max(0, nowMs - projectedStartMs))
      progress = Math.min(1, Math.max(0, elapsed / total))
    }
    return { startsInSeconds, endsInSeconds, expectedEndLabel, progress }
  }, [currentItem, projectionMap, effectiveNowMs, nextPendingFixedAnchor, stationTimeZone])

  const headerBarVisuals = React.useMemo(() => {
    if (!headerTiming) {
      return null
    }
    const endsIn = headerTiming.endsInSeconds
    const startsIn = headerTiming.startsInSeconds
    const barColor = '#0b4f2a'
    let value =
      headerTiming.progress !== null ? Math.max(0, Math.min(100, 100 - headerTiming.progress * 100)) : startsIn !== null ? 100 : 0

    if (HEADER_BAR_REFILL_ON_WARNINGS && endsIn !== null) {
      if (endsIn <= HEADER_CRITICAL_THRESHOLD_SECONDS) {
        value = Math.max(0, Math.min(100, (endsIn / HEADER_CRITICAL_THRESHOLD_SECONDS) * 100))
      } else if (endsIn <= HEADER_WARN_THRESHOLD_SECONDS) {
        value = Math.max(0, Math.min(100, (endsIn / HEADER_WARN_THRESHOLD_SECONDS) * 100))
      }
    }

    const shouldFlash = endsIn !== null && endsIn <= HEADER_WARN_THRESHOLD_SECONDS
    const flashColor =
      endsIn !== null && endsIn <= HEADER_CRITICAL_THRESHOLD_SECONDS
        ? theme.palette.error.main
        : endsIn !== null && endsIn <= HEADER_WARN_THRESHOLD_SECONDS
          ? theme.palette.warning.main
          : barColor

    const isWarning = endsIn !== null && endsIn <= HEADER_WARN_THRESHOLD_SECONDS
    const activeColor = isWarning ? flashColor : barColor
    const trackColor = isWarning ? alpha(flashColor, 0.18) : alpha(barColor, 0.12)

    return {
      value,
      barColor,
      flashColor,
      shouldFlash,
      activeColor,
      trackColor,
    }
  }, [headerTiming, theme])
  const livePaletteMode = liveThemeOverride === 'inherit' ? theme.palette.mode : liveThemeOverride
  const liveTheme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode: livePaletteMode,
          primary: {
            main: livePaletteMode === 'dark' ? '#9bcaff' : '#3355ff',
          },
          secondary: {
            main: livePaletteMode === 'dark' ? '#ffb2a6' : '#ff5d7d',
          },
          background: {
            default: livePaletteMode === 'dark' ? '#0f172a' : '#f8f9ff',
            paper: livePaletteMode === 'dark' ? '#1b2138' : '#ffffff',
          },
        },
        shape: theme.shape,
        typography: theme.typography,
      }),
    [livePaletteMode, theme.shape, theme.typography],
  )
  const effectiveHeaderFill = headerFillEnabled || headerCollapsed

  React.useEffect(() => {
    if (!showHeaderBar && headerCollapsed) {
      setHeaderCollapsed(false)
    }
  }, [headerCollapsed, showHeaderBar])

  const headerFlashKeyframes = React.useMemo(() => {
    if (!headerBarVisuals?.shouldFlash) {
      return null
    }
    const color = headerBarVisuals.flashColor
    return keyframes`
      0% { background-color: ${color}; }
      50% { background-color: ${alpha(color, 0.45)}; }
      100% { background-color: ${color}; }
    `
  }, [headerBarVisuals?.flashColor, headerBarVisuals?.shouldFlash, theme])

  const headerBorderFlashKeyframes = React.useMemo(() => {
    if (headerTiming?.endsInSeconds === null || headerTiming?.endsInSeconds === undefined) {
      return null
    }
    if (headerTiming.endsInSeconds > HEADER_WARN_THRESHOLD_SECONDS) {
      return null
    }
    const endsIn = headerTiming.endsInSeconds
    const color =
      endsIn <= HEADER_CRITICAL_THRESHOLD_SECONDS ? theme.palette.error.main : theme.palette.warning.main
    return keyframes`
      0% { border-color: ${color}; }
      50% { border-color: ${alpha(color, 0.5)}; }
      100% { border-color: ${color}; }
    `
  }, [headerTiming?.endsInSeconds, theme])

  const handleSnackbarClose = (_event?: unknown, reason?: string) => {
    if (reason === 'clickaway') {
      return
    }
    setSnackbar(null)
  }

  const openPrepDrawer = React.useCallback(
    (id: number, invoker?: HTMLElement | null, itemId?: number | null) => {
      setActivePrepId(id)
      lastInvokerRef.current = invoker ?? null
      setPrepDrawerOpen(true)
      setActivePrepContextItemId(itemId ?? null)
    },
    [],
  )

  const closePrepDrawer = React.useCallback(() => {
    setPrepDrawerOpen(false)
    queueMicrotask(() => {
      lastInvokerRef.current?.focus()
    })
    setActivePrepId(null)
    setActivePrepContextItemId(null)
  }, [])

  const closePrepPicker = React.useCallback(() => {
    setPrepPickerOpen(false)
    setPickerForItemId(null)
  }, [])

  const handleAttachPrepItem = React.useCallback(
    async (prepItemId: number) => {
      if (!stationId || !pickerForItemId) {
        const message = 'Select a show clock item before attaching prep.'
        setSnackbar({ message, severity: 'error' })
        throw new Error(message)
      }
      try {
        const response = await attachClockDayItemPrep(stationId, pickerForItemId, {
          prep_item_id: prepItemId,
        })
        let detail: PrepDetailResponse | null = null
        try {
          detail = await fetchPrepItemDetail(prepItemId)
        } catch (detailError) {
          console.error('Failed to load prep detail', detailError)
          detail = null
        }
        setPrepAttachments((prev) => {
          const existing = prev[pickerForItemId] ?? createEmptyPrepState()
          const nextCache = { ...existing.cache }
          if (detail) {
            nextCache[detail.id] = detail
            prepCacheRef.current.set(detail.id, detail)
          }
          const nextAttachments = sortPrepAttachments([
            ...existing.attachments,
            { ...response.prep, prep: detail },
          ])
          return {
            ...prev,
            [pickerForItemId]: {
              loading: false,
              error: null,
              attachments: nextAttachments,
              cache: nextCache,
            },
          }
        })
        setSnackbar({ message: 'Prep attached.', severity: 'success' })
        setExpanded(pickerForItemId, true)
      } catch (error) {
        const message = getErrorMessage(error, 'Unable to attach prep.')
        setSnackbar({ message, severity: 'error' })
        throw new Error(message)
      }
    },
    [pickerForItemId, setExpanded, setSnackbar, stationId],
  )

  const handleDetachPrep = React.useCallback(
    async (itemId: number, prepId: number) => {
      if (!stationId) {
        return
      }
      setRemovingPrepIds((prev) => ({ ...prev, [prepId]: true }))
      try {
        await detachClockDayItemPrep(stationId, prepId)
        setPrepAttachments((prev) => {
          const existing = prev[itemId]
          if (!existing) {
            return prev
          }
          const nextAttachments = existing.attachments.filter((attachment) => attachment.id !== prepId)
          const target = existing.attachments.find((attachment) => attachment.id === prepId)
          const nextCache = { ...existing.cache }
          if (target) {
            delete nextCache[target.prep_item_id]
          }
          return {
            ...prev,
            [itemId]: {
              ...existing,
              attachments: nextAttachments,
              cache: nextCache,
            },
          }
        })
        setSnackbar({ message: 'Prep removed.', severity: 'success' })
      } catch (error) {
        setSnackbar({ message: getErrorMessage(error, 'Unable to remove prep.'), severity: 'error' })
      } finally {
        setRemovingPrepIds((prev) => {
          const next = { ...prev }
          delete next[prepId]
          return next
        })
      }
    },
    [setSnackbar, stationId],
  )

  const handlePrepDragEnd = React.useCallback(
    (itemId: number, event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) {
        return
      }
      setPrepAttachments((prev) => {
        const current = prev[itemId]
        if (!current) {
          return prev
        }
        const currentIndex = current.attachments.findIndex((attachment) => attachment.id === Number(active.id))
        const targetIndex = current.attachments.findIndex((attachment) => attachment.id === Number(over.id))
        if (currentIndex === -1 || targetIndex === -1) {
          return prev
        }
        if (!stationId) {
          setSnackbar({ message: 'Select a station before reordering prep.', severity: 'error' })
          return prev
        }
        const previousOrder = current.attachments
        const nextAttachments = arrayMove(current.attachments, currentIndex, targetIndex)
        void (async () => {
          try {
            await reorderClockDayItemPreps(stationId, itemId, {
              order: nextAttachments.map((attachment) => attachment.id),
            })
          } catch (error) {
            setPrepAttachments((latest) => {
              const latestState = latest[itemId]
              if (!latestState) {
                return latest
              }
              return {
                ...latest,
                [itemId]: {
                  ...latestState,
                  attachments: previousOrder,
                },
              }
            })
            setSnackbar({
              message: getErrorMessage(error, 'Unable to reorder prep attachments.'),
              severity: 'error',
            })
          }
        })()
        return {
          ...prev,
          [itemId]: {
            ...current,
            attachments: nextAttachments,
          },
        }
      })
    },
    [setSnackbar, stationId],
  )

    const renderPrepContent = (item: ClockDayItem) => {
      const state = prepAttachments[item.id] ?? createEmptyPrepState()
      const attachments = state.attachments
      return (
        <Stack spacing={1} sx={{ py: 0.5 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Prep Attachments
            </Typography>
            <Tooltip title="Add prep item">
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => openPrepPicker(item.id)}
                  disabled={!stationId}
                  aria-label="Add prep item"
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        <DndContext sensors={prepSensors} onDragEnd={(event) => handlePrepDragEnd(item.id, event)}>
            <SortableContext items={attachments.map((attachment) => attachment.id)} strategy={verticalListSortingStrategy}>
              <Stack spacing={1}>
                {attachments.map((attachment) => (
                  <SortablePrepAttachmentRow
                    key={attachment.id}
                    attachment={attachment}
                    showTease={showPrepTease}
                    onRemove={() => void handleDetachPrep(item.id, attachment.id)}
                    onOpenDetail={(prepId, invoker) => openPrepDrawer(prepId, invoker, item.id)}
                    removing={Boolean(removingPrepIds[attachment.id])}
                  />
                ))}
            </Stack>
          </SortableContext>
        </DndContext>
        {state.loading ? <LinearProgress sx={{ borderRadius: 999 }} /> : null}
      </Stack>
    )
  }

  const drawerCachedDetail = React.useMemo(() => {
    if (!activePrepId) {
      return null
    }
    const cached = prepCacheRef.current.get(activePrepId)
    if (cached) {
      return cached
    }
    for (const state of Object.values(prepAttachments)) {
      const candidate = state?.cache?.[activePrepId]
      if (candidate) {
        return candidate
      }
    }
    return null
  }, [activePrepId, prepAttachments])

  const handlePrepDetailLoaded = React.useCallback((detail: PrepDetailResponse) => {
    prepCacheRef.current.set(detail.id, detail)
    setPrepAttachments((prev) => {
      let mutated = false
      const next: PrepAttachmentsState = {}
      for (const [key, value] of Object.entries(prev)) {
        const numericKey = Number(key)
        if (Number.isNaN(numericKey)) {
          continue
        }
        const hasAttachment = value.attachments.some((attachment) => attachment.prep_item_id === detail.id)
        if (!hasAttachment) {
          next[numericKey] = value
          continue
        }
        mutated = true
        const nextCache = { ...value.cache, [detail.id]: detail }
        const nextAttachments = value.attachments.map((attachment) =>
          attachment.prep_item_id === detail.id ? { ...attachment, prep: detail } : attachment,
        )
        next[numericKey] = { ...value, attachments: nextAttachments, cache: nextCache }
      }
      return mutated ? next : prev
    })
  }, [])

  const prepPickerAttachedIds = React.useMemo(() => {
    if (!pickerForItemId) {
      return []
    }
    const state = prepAttachments[pickerForItemId]
    return state ? state.attachments.map((attachment) => attachment.prep_item_id) : []
  }, [pickerForItemId, prepAttachments])

  const activePrepNavIds = React.useMemo(() => {
    if (!activePrepContextItemId) {
      return []
    }
    const state = prepAttachments[activePrepContextItemId]
    if (!state) {
      return []
    }
    return state.attachments.map((attachment) => attachment.prep_item_id)
  }, [activePrepContextItemId, prepAttachments])



  const handleStart = React.useCallback(
    async (item: ClockDayItem) => {
      if (!item.id) {
        return
      }
      await runAction(
        'start',
        item,
        (signal) => startClockDayItem(item.id, { stationId, signal }),
        '',
        'Unable to mark this item as started.',
      )
    },
    [runAction, stationId],
  )

  const handleStartNext = React.useCallback(
    async (item: ClockDayItem | null) => {
      if (!item || !item.id) {
        return
      }
      setBusyCount((count) => count + 1)
      try {
        if (currentItem && isCurrentRunning) {
          const projectionEntry = projectionMap.get(currentItem.id) ?? null
          const projectedStart = projectionEntry?.projected_start ?? null
          try {
            const endResponse = await endClockDayItem(currentItem.id, { stationId, projectedStart })
          if (endResponse) {
            applyActionPayload(endResponse)
          }
        } catch (error) {
          setSnackbar({ message: getErrorMessage(error, 'Unable to end the current item.'), severity: 'error' })
          return
        }
        }
        const label = getItemLabel(item)
        const success = await runAction(
          'start',
          item,
        (signal) => startClockDayItem(item.id, { stationId, signal }),
        label !== '-' ? `Started ${label}.` : 'Started next item.',
        'Unable to start the next item.',
      )
        if (!success) {
          return
        }
        applyAutoExpandForCurrent(item.id)
        window.requestAnimationFrame(() => {
          scrollItemIntoView(item.id)
        })
      } finally {
        setBusyCount((count) => Math.max(0, count - 1))
      }
    },
    [applyActionPayload, applyAutoExpandForCurrent, currentItem, getItemLabel, isCurrentRunning, projectionMap, runAction, scrollItemIntoView, setSnackbar, stationId],
  )

  const handleAdvance = React.useCallback(() => {
    if (catchUpLocked) {
      reconcileForcedOpenRef.current = true
      setReconcilePromptOpen(true)
      setSnackbar({
        message: 'Reconcile is required before you can advance the show clock.',
        severity: 'info',
      })
      return
    }
    if (!advanceTarget) {
      return
    }
    void handleStartNext(advanceTarget)
  }, [advanceTarget, catchUpLocked, handleStartNext, setSnackbar])

  React.useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.repeat) {
        return
      }
      if (catchUpLocked || reconcilePromptOpen) {
        return
      }
      const isCtrlOrMeta = event.ctrlKey || event.metaKey
      const isSpace =
        event.code === 'Space' ||
        event.key === ' ' ||
        event.key === 'Spacebar' ||
        event.key === 'Space'
      if (isCtrlOrMeta && !event.shiftKey && !event.altKey && isSpace) {
        event.preventDefault()
        handleAdvance()
      }
    }
    window.addEventListener('keydown', handleKey, { capture: true })
    return () => window.removeEventListener('keydown', handleKey, { capture: true })
  }, [catchUpLocked, handleAdvance, reconcilePromptOpen])

  const handleSkip = React.useCallback(
    async (item: ClockDayItem) => {
      if (!item.id) {
        return
      }
      const previousItem = { ...item }
      const previousProjectionClone = projection
        ? {
            ...projection,
            items: projection.items ? projection.items.map((entry) => ({ ...entry })) : [],
          }
        : null
      const stateSnapshot = useShowClockLiveStore.getState()
      const nowMs = stateSnapshot.stationNowEpochMs ?? Date.now()
      const optimisticItem: ClockDayItem = { ...item, skipped: true }
      const optimisticItems = items.map((existing) =>
        existing.id === item.id ? optimisticItem : { ...existing },
      )
      const optimisticProjection = computeSequentialProjection({
        items: optimisticItems,
        previousProjection: projection,
        nowEpochMs: nowMs,
        stationTimeZone,
      })

      await runAction(
        'skip',
        item,
        async (signal) => {
          const response = await skipClockDayItem(item.id, { stationId, signal })
          if (response && typeof response === 'object') {
            const currentState = useShowClockLiveStore.getState()
            const currentItems = currentState.items ?? []
            const mergedItems = currentItems.map((existing) =>
              existing.id === response.item.id ? { ...response.item } : { ...existing },
            )
            const projected = computeSequentialProjection({
              items: mergedItems,
              previousProjection: response.projection ?? projection,
              nowEpochMs: currentState.stationNowEpochMs ?? Date.now(),
              stationTimeZone,
            })
            response.projection = projected
          }
          return response
        },
        '',
        'Unable to skip this item.',
        {
          optimistic: () => {
            updateItem(optimisticItem)
            setProjectionState(optimisticProjection)
          },
          rollback: () => {
            updateItem(previousItem)
            setProjectionState(previousProjectionClone)
          },
        },
      )
    },
    [items, projection, runAction, setProjectionState, stationId, stationTimeZone, updateItem],
  )

  const handleAdjust = React.useCallback(
    async (item: ClockDayItem, deltaSeconds: number) => {
      if (!item.id) {
        return
      }
      const previousItem = { ...item }
      const previousProjectionClone = projection
        ? {
            ...projection,
            items: projection.items ? projection.items.map((entry) => ({ ...entry })) : [],
          }
        : null
      const baseDuration = Number.isFinite(item.planned_duration_sec)
        ? Number(item.planned_duration_sec)
        : 0
      const tentative = Number.isFinite(deltaSeconds) ? baseDuration + deltaSeconds : baseDuration
      const nextDuration = Math.max(MIN_PLANNED_DURATION_SECONDS, Math.round(tentative))

      await runAction(
        'adjust',
        item,
        async (signal) => {
          const response = await adjustClockDayItemDuration(item.id, {
            stationId,
            plannedDurationSec: nextDuration,
            signal,
          })
          if (response && typeof response === 'object') {
            const currentState = useShowClockLiveStore.getState()
            const mergedItems = (currentState.items ?? []).map((existing) =>
              existing.id === response.item.id ? { ...response.item } : { ...existing },
            )
            const projected = computeSequentialProjection({
              items: mergedItems,
              previousProjection: response.projection ?? projection,
              nowEpochMs: currentState.stationNowEpochMs ?? Date.now(),
              stationTimeZone,
              baseStartEpochMs: baseScheduleEpochMs,
            })
            response.projection = projected
          }
          return response
        },
        '',
        'Unable to adjust this item.',
        {
          optimistic: () => {
            const optimisticItem: ClockDayItem = { ...item, planned_duration_sec: nextDuration }
            const optimisticItems = items.map((existing) =>
              existing.id === item.id ? optimisticItem : { ...existing },
            )
            const optimisticProjection = computeSequentialProjection({
              items: optimisticItems,
              previousProjection: projection,
              nowEpochMs: stationNowEpochMs ?? Date.now(),
              stationTimeZone,
              baseStartEpochMs: baseScheduleEpochMs,
            })
            updateItem(optimisticItem)
            setProjectionState(optimisticProjection)
          },
          rollback: () => {
            updateItem(previousItem)
            setProjectionState(previousProjectionClone)
          },
        },
      )
    },
    [
      baseScheduleEpochMs,
      items,
      projection,
      runAction,
      setProjectionState,
      stationId,
      stationNowEpochMs,
      stationTimeZone,
      updateItem,
    ],
  )

  const openDurationEditor = React.useCallback((item: ClockDayItem) => {
    if (!item.id || item.skipped || hasValue(item.actual_end)) {
      return
    }
    const baseDuration = Number.isFinite(item.planned_duration_sec)
      ? Math.max(0, Math.round(Number(item.planned_duration_sec)))
      : 0
    const minutes = Math.floor(baseDuration / 60)
    const seconds = baseDuration % 60
    setDurationEdit({
      itemId: item.id,
      minutes: String(minutes),
      seconds: seconds.toString().padStart(2, '0'),
    })
  }, [])

  const closeDurationEditor = React.useCallback(() => {
    setDurationEdit(null)
  }, [])

  const bumpDurationDraft = React.useCallback((deltaSeconds: number) => {
    setDurationEdit((prev) => {
      if (!prev) {
        return prev
      }
      const minutes = Number.parseInt(prev.minutes || '0', 10)
      const seconds = Number.parseInt(prev.seconds || '0', 10)
      const normalized = normalizeDuration(minutes, seconds + deltaSeconds)
      return {
        itemId: prev.itemId,
        minutes: String(normalized.minutes),
        seconds: normalized.seconds.toString().padStart(2, '0'),
      }
    })
  }, [])

  const handleSetPlannedDuration = React.useCallback(
    async (item: ClockDayItem, plannedDurationSeconds: number) => {
      if (!item.id) {
        return false
      }
      const previousItem = { ...item }
      const previousProjectionClone = projection
        ? {
            ...projection,
            items: projection.items ? projection.items.map((entry) => ({ ...entry })) : [],
          }
        : null
      const nextDuration = Math.max(MIN_PLANNED_DURATION_SECONDS, Math.round(plannedDurationSeconds))

      const success = await runAction(
        'adjust',
        item,
        async (signal) => {
          const response = await adjustClockDayItemDuration(item.id, {
            stationId,
            plannedDurationSec: nextDuration,
            signal,
          })
          if (response && typeof response === 'object') {
            const currentState = useShowClockLiveStore.getState()
            const mergedItems = (currentState.items ?? []).map((existing) =>
              existing.id === response.item.id ? { ...response.item } : { ...existing },
            )
            const projected = computeSequentialProjection({
              items: mergedItems,
              previousProjection: response.projection ?? projection,
              nowEpochMs: currentState.stationNowEpochMs ?? Date.now(),
              stationTimeZone,
              baseStartEpochMs: baseScheduleEpochMs,
            })
            response.projection = projected
          }
          return response
        },
        '',
        'Unable to adjust this item.',
        {
          optimistic: () => {
            const optimisticItem: ClockDayItem = { ...item, planned_duration_sec: nextDuration }
            const optimisticItems = items.map((existing) =>
              existing.id === item.id ? optimisticItem : { ...existing },
            )
            const optimisticProjection = computeSequentialProjection({
              items: optimisticItems,
              previousProjection: projection,
              nowEpochMs: stationNowEpochMs ?? Date.now(),
              stationTimeZone,
              baseStartEpochMs: baseScheduleEpochMs,
            })
            updateItem(optimisticItem)
            setProjectionState(optimisticProjection)
          },
          rollback: () => {
            updateItem(previousItem)
            setProjectionState(previousProjectionClone)
          },
        },
      )

      return success
    },
    [
      baseScheduleEpochMs,
      items,
      projection,
      runAction,
      setProjectionState,
      stationId,
      stationNowEpochMs,
      stationTimeZone,
      updateItem,
    ],
  )

  const handleBackfillStart = React.useCallback(
    async (item: ClockDayItem, mode: 'scheduled' | 'eta') => {
      if (!item.id || !stationId) {
        return
      }
      const scheduledEntry = scheduledProjectionMap.get(item.id) ?? null
      const projectionEntry = projectionMap.get(item.id) ?? null
      const sourceInstant =
        mode === 'scheduled'
          ? scheduledEntry?.scheduled_start ?? scheduledEntry?.projected_start ?? null
          : projectionEntry?.projected_start ?? scheduledEntry?.projected_start ?? scheduledEntry?.scheduled_start ?? null
      const startMs = resolveClockEpochMs(sourceInstant, stationTimeZone)
      if (startMs === null) {
        setSnackbar({
          message: 'Unable to resolve a start time for this item.',
          severity: 'error',
        })
        return
      }
      const startLabel = formatStationDateTime(startMs, stationTimeZone, { withSeconds: true, twentyFourHour: true })
      const optimisticItem: ClockDayItem = {
        ...item,
        actual_start: startLabel,
        actual_start_epoch: Math.round(startMs / 1000),
        actual_start_iso_utc: null,
      }
      const optimisticItems = items.map((existing) => (existing.id === item.id ? optimisticItem : existing))
      const optimisticProjection = computeSequentialProjection({
        items: optimisticItems,
        previousProjection: projection,
        nowEpochMs: stationNowEpochMs ?? Date.now(),
        stationTimeZone,
        baseStartEpochMs: baseScheduleEpochMs,
      })
      await runAction(
        'start',
        item,
        (signal) =>
          startClockDayItem(item.id, { stationId, signal, confirm: true, projectedStart: startLabel ?? undefined }),
        mode === 'scheduled' ? 'Start aligned to scheduled time.' : 'Start aligned to ETA.',
        'Unable to set the start time for this item.',
        {
          optimistic: () => {
            updateItem(optimisticItem)
            setProjectionState(optimisticProjection)
          },
          rollback: () => {
            updateItem(item)
            setProjectionState(projection)
          },
        },
      )
    },
    [
      baseScheduleEpochMs,
      items,
      projection,
      projectionMap,
      runAction,
      scheduledProjectionMap,
      setProjectionState,
      setSnackbar,
      stationId,
      stationNowEpochMs,
      stationTimeZone,
      updateItem,
    ],
  )

  const renderNotes = (item: ClockDayItem) => {
    const draft = noteDrafts[item.id] ?? noteBaselinesRef.current[item.id] ?? item.notes ?? ''
    const baseline = noteBaselinesRef.current[item.id] ?? item.notes ?? ''
    const status = noteStatuses[item.id] ?? { saving: false, error: null }
    const isSaving = status.saving
    const trimmedDraft = draft.trim()
    const trimmedBaseline = baseline.trim()
    const isDirty = trimmedDraft !== trimmedBaseline
    const hasContent = trimmedDraft.length > 0
    const preferredMode = hasContent ? noteViewModes[item.id] ?? 'preview' : 'edit'
    const effectiveMode = isDirty ? 'edit' : preferredMode
    return (
      <Stack spacing={1.5} sx={{ py: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap>
          <NotesIcon fontSize="small" color="action" />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mr: 1 }}>
            Notes
          </Typography>
          {effectiveMode === 'preview' ? (
            <Button
              size="small"
              variant="contained"
              onClick={() => setNoteViewMode(item.id, 'edit')}
            >
              Edit
            </Button>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                variant="contained"
                onClick={() => void handleSaveNote(item)}
                disabled={isSaving || !isDirty}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleResetNote(item)}
                disabled={isSaving}
              >
                Cancel
              </Button>
            </Stack>
          )}
        </Stack>
        {effectiveMode === 'edit' ? (
          <>
            <TextField
              value={draft}
              onChange={(event) => handleNoteDraftChange(item.id, event.target.value)}
              multiline
              minRows={3}
              placeholder="Add notes for this segment"
              fullWidth
              inputRef={(node) => handleNoteInputRef(item.id, node)}
            />
            {status.error ? (
              <Typography variant="body2" color="error">
                {status.error}
              </Typography>
            ) : null}
            {isDirty ? (
              <Typography variant="body2" color="text.secondary">
                Unsaved changes
              </Typography>
            ) : trimmedDraft ? (
              <Typography variant="body2" color="text.secondary">
                Note saved.
              </Typography>
            ) : null}
          </>
        ) : (
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: 'background.default',
              typography: 'body2',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            <Box
              sx={{ '& a': { color: 'primary.main', fontWeight: 600 } }}
              dangerouslySetInnerHTML={{ __html: linkifyText(trimmedDraft) }}
            />
          </Paper>
        )}
      </Stack>
    )
  }

  const renderTimelineItem = (
    item: ClockDayItem,
    index: number,
    options?: LiveItemSortableRenderProps,
  ) => {
    const meta = itemTypeMap.get(item.item_type)
    const dragHandleProps = options?.dragHandleProps ?? null
    const setSortableNodeRef = options?.setSortableNodeRef
    const dragStyle = options?.style ?? undefined
    const projectionEntry = projectionMap.get(item.id) ?? null
    const status = item.skipped
      ? 'skipped'
      : hasValue(item.actual_end)
      ? 'complete'
      : hasValue(item.actual_start)
      ? 'live'
      : 'upcoming'
    const isCurrent = currentItem?.id === item.id
    const expanded = isExpanded(item.id)
    const colorStyles = resolveClockItemColorStyles(theme, meta?.colorToken ?? '')
    const ItemTypeIcon = ITEM_TYPE_ICON_MAP[item.item_type] ?? WidgetsIcon
    const itemTypeIconSize = isCurrent ? 50 : 44
    const currentCardStyles = currentItemCardStyles(theme, meta?.colorToken ?? '')
    const scheduledEntry = scheduledProjectionMap.get(item.id) ?? null
    const scheduledStartInstant =
      scheduledEntry?.scheduled_start ??
      scheduledEntry?.projected_start ??
      null
    const scheduledLabel = formatClockTimeLabel(scheduledStartInstant, stationTimeZone, { withSeconds: true })
    const fixedLabelInstant =
      scheduledEntry?.scheduled_start ??
      scheduledEntry?.projected_start ??
      item.fixed_time ??
      null
    const projectedStartInstant =
      projectionEntry?.projected_start ?? scheduledEntry?.projected_start ?? scheduledEntry?.scheduled_start ?? null
    const projectedStartLabel = formatClockTimeLabel(projectedStartInstant, stationTimeZone, { withSeconds: true })
    const isFixed = Boolean(item.fixed_time || scheduledEntry?.is_fixed || projectionEntry?.is_fixed)
    const startInstantForCountdown = isFixed
      ? item.fixed_time ?? scheduledEntry?.scheduled_start ?? scheduledStartInstant ?? projectedStartInstant ?? null
      : projectedStartInstant ?? scheduledStartInstant ?? null
    const expectedStartEpochMs = resolveClockEpochMs(startInstantForCountdown, stationTimeZone)
    const actualStartLabel = formatClockTimeLabel(
      {
        epochSeconds: item.actual_start_epoch ?? undefined,
        isoUtc: item.actual_start_iso_utc ?? null,
        stationLocal: item.actual_start ?? null,
      },
      stationTimeZone,
      { withSeconds: true },
    )
    const actualEndLabel = formatClockTimeLabel(
      {
        epochSeconds: item.actual_end_epoch ?? undefined,
        isoUtc: item.actual_end_iso_utc ?? null,
        stationLocal: item.actual_end ?? null,
      },
      stationTimeZone,
      { withSeconds: true },
    )
    const hasActualStart = hasValue(item.actual_start)
    const isPreStartCurrent = isCurrent && !hasActualStart
    const projectedEndEpochMs = resolveClockEpochMs(projectionEntry?.projected_end ?? null, stationTimeZone)
    const fallbackStartMs =
      resolveClockEpochMs(item.actual_start ?? null, stationTimeZone) ??
      resolveClockEpochMs(projectionEntry?.projected_start ?? scheduledEntry?.projected_start ?? null, stationTimeZone)
    const plannedDurationMs =
      Number.isFinite(item.planned_duration_sec) && item.planned_duration_sec > 0
        ? item.planned_duration_sec * 1000
        : null
    const computedEndMs =
      projectedEndEpochMs ??
      (fallbackStartMs !== null && plannedDurationMs !== null
        ? fallbackStartMs + plannedDurationMs
        : expectedStartEpochMs !== null && plannedDurationMs !== null
          ? expectedStartEpochMs + plannedDurationMs
          : null)
    const anchorCutoffMs =
      isCurrent && hasActualStart && nextPendingFixedAnchor?.scheduledStartMs
        ? nextPendingFixedAnchor.scheduledStartMs
        : null
    const effectiveEndMs =
      computedEndMs !== null && anchorCutoffMs !== null && anchorCutoffMs > effectiveNowMs + 500
        ? Math.min(computedEndMs, anchorCutoffMs)
        : computedEndMs
    const holdAccumMs = getHoldAccumMs()
    const adjustedProjectedEndMs = effectiveEndMs !== null ? effectiveEndMs + holdAccumMs : null
    const expectedEndLabel =
      adjustedProjectedEndMs !== null
        ? formatClockTimeLabel(adjustedProjectedEndMs, stationTimeZone, { withSeconds: true, twentyFourHour: true })
        : null
    let endsInSeconds =
      isCurrent && effectiveEndMs !== null ? (effectiveEndMs - effectiveNowMs) / 1000 : null
    if (isPreStartCurrent && expectedStartEpochMs !== null && expectedStartEpochMs > effectiveNowMs) {
      endsInSeconds = null
    }
    if (isCurrent && endsInSeconds !== null && Number.isFinite(item.planned_duration_sec ?? Number.NaN)) {
      const paddedDuration = Math.max(0, Number(item.planned_duration_sec)) + 5
      if (endsInSeconds > paddedDuration) {
        endsInSeconds = paddedDuration
      }
    }
    const startsInSeconds =
      status === 'upcoming' && expectedStartEpochMs !== null
        ? (expectedStartEpochMs - effectiveNowMs) / 1000
        : null
    const endsInRounded = endsInSeconds !== null ? Math.max(0, Math.ceil(endsInSeconds)) : null
    const urgencySwatch = endsInRounded !== null ? urgencyColor(endsInRounded, theme) : undefined
    const displayTitle = displayTitles[item.id] ?? ''
    const draftTitle = titleDrafts[item.id] ?? item.title ?? ''
    const isTitleEditing = editingTitleId === item.id
    const titleSaving = titleStatuses[item.id]?.saving ?? false
    const nextChipLabel = nextTitle ? `Next ${nextTitle}` : 'Next'

    const statusChip = (() => {
      switch (status) {
        case 'live':
          return <Chip label="In Progress" color="success" size="small" />
        case 'complete':
          return <Chip label="Completed" color="default" size="small" />
        case 'skipped':
          return <Chip label="Skipped" color="warning" size="small" />
        default:
          return <Chip label="Upcoming" color="info" size="small" />
      }
    })()

    const baseBg = theme.palette.background.paper
    const typeBg = colorStyles.backgroundColor ?? theme.palette.background.paper
    const subtleTypeTint =
      status === 'upcoming'
        ? alpha(typeBg, theme.palette.mode === 'dark' ? 0.24 : 0.18)
        : undefined
    const fadedPast = status === 'complete' || status === 'skipped'
    const currentTint = isCurrent
      ? alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.168 : 0.216)
      : undefined
    const cardBg = isCurrent
      ? alpha(typeBg, theme.palette.mode === 'dark' ? 0.28 : 0.22)
      : subtleTypeTint ?? baseBg

    const rawDriftSeconds = projectionEntry?.delta_from_schedule
    const driftSeconds =
      typeof rawDriftSeconds === 'number' && Number.isFinite(rawDriftSeconds)
        ? Math.round(rawDriftSeconds)
        : 0
    const driftLabel = formatDriftWithSign(driftSeconds)
    const driftChipColor = driftColor(driftSeconds, theme)
    const driftChip = (
      <Chip
        size="small"
        variant="outlined"
        icon={<TimerIcon fontSize="small" />}
        label={driftLabel}
        sx={{
          bgcolor: alpha(driftChipColor, theme.palette.mode === 'dark' ? 0.24 : 0.12),
          color: driftChipColor,
          borderColor: alpha(driftChipColor, 0.4),
        }}
      />
    )

    const timeRemainingNode =
      isCurrent && endsInSeconds !== null ? (
        <Chip
          size="small"
          variant="outlined"
          label={`Time Remaining ${formatCountdownLabel(endsInSeconds)}`}
          sx={{
            fontWeight: 600,
            height: 36,
            bgcolor: urgencySwatch
              ? alpha(urgencySwatch, theme.palette.mode === 'dark' ? 0.25 : 0.12)
              : undefined,
            color: urgencySwatch ?? 'inherit',
            borderColor: urgencySwatch ? alpha(urgencySwatch, 0.4) : theme.palette.divider,
            '& .MuiChip-label': {
              fontSize: '1.45rem',
            },
          }}
        />
      ) : null
    const endsInNode =
      !isCurrent && endsInSeconds !== null ? (
        <Chip
          size="small"
          variant="outlined"
          label={`Ends in ${formatCountdownLabel(endsInSeconds)}`}
          sx={{
            fontWeight: 600,
            bgcolor: urgencySwatch
              ? alpha(urgencySwatch, theme.palette.mode === 'dark' ? 0.25 : 0.12)
              : undefined,
            color: urgencySwatch ?? 'inherit',
            borderColor: urgencySwatch ? alpha(urgencySwatch, 0.4) : theme.palette.divider,
          }}
        />
      ) : null
    const startsInNode =
      endsInSeconds === null && startsInSeconds !== null && (!hasActualStart || isPreStartCurrent) ? (
        <Chip
          size="small"
          variant="outlined"
          label={`Starts in ${formatCountdownLabel(startsInSeconds)}`}
          sx={{
            height: isCurrent ? 28 : 35,
            fontWeight: 600,
            '& .MuiChip-label': { fontSize: isCurrent ? '0.95rem' : '1.2rem' },
          }}
        />
      ) : null

    const isDragging = dragHandleProps?.isDragging ?? false
    const combinedStyle: React.CSSProperties = {
      ...dragStyle,
      zIndex: isDragging ? 1300 : undefined,
    }

    let alertColor: string | undefined
    if (isCurrent && endsInRounded !== null) {
      if (endsInRounded <= 10) {
        alertColor = theme.palette.error.main
      } else if (endsInRounded <= 30) {
        alertColor = theme.palette.warning.main
      }
    }
    const currentBorderColor = currentCardStyles?.borderColor ?? theme.palette.common.white
    const currentBackground =
      isCurrent
        ? alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.08 : 0.12)
        : status === 'upcoming'
          ? subtleTypeTint
          : undefined
    const autoAdvanceEnabled = Boolean(item.auto_advance)
    const autoAdvanceDisabled = Boolean(autoAdvanceSavingById[item.id]) || Boolean(item.actual_end) || item.skipped
    const autoAdvanceBaseColor = autoAdvanceEnabled
      ? theme.palette.success.main
      : alpha(theme.palette.text.primary, 0.4)
    const autoAdvanceFlashColor = isCurrent && autoAdvanceEnabled && alertColor ? alertColor : null
    const nextItemMeta = advanceTarget ? itemTypeMap.get(advanceTarget.item_type) : null
    const nextItemBg = nextItemMeta ? resolveHeaderSolidColor(nextItemMeta.colorToken) : undefined

    const setItemRef = (node: HTMLDivElement | null) => {
      if (node) {
        itemRefs.current.set(item.id, node)
      } else {
        itemRefs.current.delete(item.id)
      }
      if (setSortableNodeRef) {
        setSortableNodeRef(node)
      }
    }

    const isActionBusy = actionState.type !== null && actionState.itemId === item.id
    const disableAdjust = isActionBusy || status === 'skipped'
    const adjustmentOptions = [-30, -10, 10, 30]
    const durationNode = (() => {
      const isEditableDuration = !item.skipped && !hasValue(item.actual_end)
      const isEditing = durationEdit?.itemId === item.id
      const isSaving = actionState.type === 'adjust' && actionState.itemId === item.id
      const minutesValue = durationEdit?.minutes ?? '0'
      const secondsValue = durationEdit?.seconds ?? '00'
      const durationFontSize = isCurrent ? '1.2rem' : '0.95rem'
      const durationHeight = isCurrent ? 35 : 28

      const commit = async () => {
        if (!durationEdit || durationEdit.itemId !== item.id) {
          return
        }
        const minutes = Number.parseInt(durationEdit.minutes || '0', 10)
        const seconds = Number.parseInt(durationEdit.seconds || '0', 10)
        const normalized = normalizeDuration(minutes, seconds)
        const ok = await handleSetPlannedDuration(item, normalized.totalSeconds)
        if (ok) {
          closeDurationEditor()
        }
      }

      if (!isEditing) {
        return (
          <ButtonBase
            onClick={(event) => {
              event.stopPropagation()
              openDurationEditor(item)
            }}
            disabled={!isEditableDuration}
            disableRipple
            sx={{ borderRadius: 999 }}
            aria-label={isEditableDuration ? 'Edit duration' : 'Duration'}
          >
            <Chip
              label={`Duration ${formatClockDuration(item.planned_duration_sec)}`}
              size="medium"
              sx={{
                height: durationHeight,
                fontSize: durationFontSize,
                fontWeight: 600,
                px: 0.25,
                cursor: isEditableDuration ? 'pointer' : 'default',
                opacity: isEditableDuration ? 1 : 0.6,
              }}
            />
          </ButtonBase>
        )
      }

      return (
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          onClick={(event) => event.stopPropagation()}
          sx={{
            px: 1,
            py: 0.25,
            borderRadius: 999,
            bgcolor: alpha(theme.palette.common.white, 0.06),
            border: `1px solid ${alpha(theme.palette.common.white, 0.18)}`,
          }}
        >
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Stack spacing={0} sx={{ lineHeight: 0 }}>
              <IconButton
                size="small"
                onClick={() => bumpDurationDraft(60)}
                disabled={isSaving}
                aria-label="Increase minutes"
              >
                <KeyboardArrowUpIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => bumpDurationDraft(-60)}
                disabled={isSaving}
                aria-label="Decrease minutes"
              >
                <KeyboardArrowDownIcon fontSize="small" />
              </IconButton>
            </Stack>
            <TextField
              value={minutesValue}
              onChange={(event) => {
                const nextRaw = event.target.value.replace(/[^\d]/g, '')
                setDurationEdit((prev) =>
                  prev && prev.itemId === item.id ? { ...prev, minutes: nextRaw } : prev,
                )
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  void commit()
                } else if (event.key === 'Escape') {
                  event.preventDefault()
                  closeDurationEditor()
                }
              }}
              onBlur={(event) => {
                const digits = event.target.value.replace(/[^\d]/g, '')
                const parsed = digits === '' ? 0 : Number.parseInt(digits, 10)
                const clamped = Math.max(0, Number.isFinite(parsed) ? parsed : 0)
                setDurationEdit((prev) =>
                  prev && prev.itemId === item.id ? { ...prev, minutes: String(clamped) } : prev,
                )
              }}
              size="small"
              variant="outlined"
              disabled={isSaving}
              inputProps={{
                inputMode: 'numeric',
                'aria-label': 'Minutes',
                style: { textAlign: 'center', padding: '6px 8px' },
              }}
              sx={{ width: 64, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Stack>
          <Typography sx={{ fontWeight: 700, opacity: 0.8 }}>:</Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Stack spacing={0} sx={{ lineHeight: 0 }}>
              <IconButton
                size="small"
                onClick={() => bumpDurationDraft(10)}
                disabled={isSaving}
                aria-label="Increase seconds"
              >
                <KeyboardArrowUpIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => bumpDurationDraft(-10)}
                disabled={isSaving}
                aria-label="Decrease seconds"
              >
                <KeyboardArrowDownIcon fontSize="small" />
              </IconButton>
            </Stack>
            <TextField
              value={secondsValue}
              onChange={(event) => {
                const digits = event.target.value.replace(/[^\d]/g, '')
                const parsed = digits === '' ? null : Number.parseInt(digits, 10)
                const nextSeconds = parsed === null ? '' : String(Math.max(0, Math.min(59, parsed)))
                setDurationEdit((prev) =>
                  prev && prev.itemId === item.id
                    ? { ...prev, seconds: nextSeconds.padStart(nextSeconds ? 2 : 0, '0') }
                    : prev,
                )
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  void commit()
                } else if (event.key === 'Escape') {
                  event.preventDefault()
                  closeDurationEditor()
                }
              }}
              onBlur={(event) => {
                const digits = event.target.value.replace(/[^\d]/g, '')
                const parsed = digits === '' ? 0 : Number.parseInt(digits, 10)
                const clamped = Math.max(0, Math.min(59, Number.isFinite(parsed) ? parsed : 0))
                setDurationEdit((prev) =>
                  prev && prev.itemId === item.id
                    ? { ...prev, seconds: clamped.toString().padStart(2, '0') }
                    : prev,
                )
              }}
              size="small"
              variant="outlined"
              disabled={isSaving}
              inputProps={{
                inputMode: 'numeric',
                'aria-label': 'Seconds',
                style: { textAlign: 'center', padding: '6px 8px' },
              }}
              sx={{ width: 64, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Stack>
          <Tooltip title="Save duration">
            <span>
              <IconButton
                size="small"
                onClick={() => void commit()}
                disabled={isSaving}
                aria-label="Save duration"
                sx={{
                  bgcolor: alpha(theme.palette.success.main, 0.15),
                  '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.25) },
                }}
              >
                {isSaving ? <CircularProgress size={18} color="inherit" /> : <CheckIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Cancel">
            <IconButton
              size="small"
              onClick={closeDurationEditor}
              disabled={isSaving}
              aria-label="Cancel duration edit"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      )
    })()

    return (
      <Paper
          key={item.id}
          ref={setItemRef}
          variant="outlined"
          style={combinedStyle}
          sx={{
            borderRadius: '16px',
            p: 2,
            position: 'relative',
            scrollMargin: theme.spacing(8),
            transition: 'background-color 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
            borderWidth: isCurrent ? 8 : 1,
            borderColor: isCurrent ? alertColor ?? currentBorderColor : theme.palette.divider,
            boxShadow: isCurrent
              ? currentCardStyles?.boxShadow ??
                `${alpha(currentBorderColor, 0.5)} 0px 0px 12px, ${theme.shadows[24]}`
              : theme.shadows[1],
          backgroundColor: currentBackground ?? cardBg,
          opacity: fadedPast ? 0.6 : 1,
          filter: fadedPast ? 'saturate(0.6)' : undefined,
          ...(isCurrent && alertColor
            ? {
                animation: 'currentBorderFlash 1s ease-in-out infinite',
                '@keyframes currentBorderFlash': {
                  '0%, 100%': {
                    borderColor: currentBorderColor,
                    boxShadow:
                      currentCardStyles?.boxShadow ??
                      `${alpha(currentBorderColor, 0.5)} 0px 0px 12px, ${theme.shadows[24]}`,
                  },
                  '50%': {
                    borderColor: alertColor,
                    boxShadow: `${alpha(alertColor, 0.8)} 0px 0px 14px, ${theme.shadows[24]}`,
                  },
                },
              }
            : {}),
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box sx={{ width: 6, borderRadius: 3, bgcolor: colorStyles.backgroundColor, flexShrink: 0 }} />
            <Box sx={{ flexGrow: 1 }}>
              {isCurrent ? (
                <Stack spacing={1}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1.5}
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    justifyContent="space-between"
                    useFlexGap
                    flexWrap="wrap"
                  >
                    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                      <Tooltip title={meta?.label ?? item.item_type}>
                        <Box
                          aria-label={meta?.label ?? item.item_type}
                          sx={{ display: 'inline-flex', alignItems: 'center', color: colorStyles.color }}
                        >
                          <ItemTypeIcon sx={{ fontSize: itemTypeIconSize }} />
                        </Box>
                      </Tooltip>
                      {isTitleEditing ? (
                        <TextField
                          value={draftTitle}
                          onChange={(event) => handleTitleFieldChange(item.id, event.target.value)}
                          onBlur={() => handleTitleFieldBlur(item.id)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault()
                              void flushTitleSave(item.id)
                              setEditingTitleId(null)
                            } else if (event.key === 'Escape') {
                              event.preventDefault()
                              cancelTitleEdit(item)
                            }
                          }}
                          variant="standard"
                          size="small"
                          autoFocus
                          inputProps={{ 'aria-label': 'Edit title' }}
                          InputProps={{
                            disableUnderline: true,
                            endAdornment: titleSaving ? <CircularProgress size={16} /> : undefined,
                            sx: { fontWeight: 600, fontSize: '1.5rem' },
                          }}
                          sx={{ minWidth: 0 }}
                        />
                      ) : (
                        <ButtonBase
                          onClick={(event) => {
                            event.stopPropagation()
                            startTitleEdit(item)
                          }}
                          disableRipple
                          sx={{ textAlign: 'left', padding: 0, borderRadius: 0 }}
                          aria-label="Edit title"
                        >
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '1.5rem' }}>
                            {displayTitle}
                          </Typography>
                        </ButtonBase>
                      )}
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" />
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                    {timeRemainingNode}
                    {durationNode}
                    {advanceTarget ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={nextChipLabel}
                        icon={<FastForwardIcon />}
                        onClick={handleAdvance}
                        clickable
                        sx={{
                          height: 34,
                          fontWeight: 600,
                          bgcolor: nextItemBg,
                          color: theme.palette.common.black,
                          borderColor: alpha(theme.palette.common.white, 0.15),
                          '& .MuiChip-label': { fontSize: '1.15rem' },
                          '& .MuiChip-icon': { color: theme.palette.common.black },
                        }}
                      />
                    ) : null}
                  </Stack>
                  <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center" useFlexGap>
                    <Typography variant="body2" color="text.secondary">
                      Scheduled: {scheduledLabel}
                    </Typography>
                    {hasValue(item.actual_start) ? (
                      <Typography variant="body2" color="text.secondary">
                        Actual Start: {actualStartLabel}
                      </Typography>
                    ) : null}
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                      {adjustmentOptions.map((seconds) => (
                        <Button
                          key={seconds}
                          variant="outlined"
                          size="small"
                          onClick={() => void handleAdjust(item, seconds)}
                          disabled={disableAdjust}
                          sx={{ minHeight: 28, px: 1 }}
                        >
                          {formatAdjustmentLabel(seconds)}
                        </Button>
                      ))}
                    </Stack>
                  </Stack>
                </Stack>
              ) : (
                <>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1.5}
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    justifyContent="space-between"
                    useFlexGap
                    flexWrap="wrap"
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap">
                      <Tooltip title={meta?.label ?? item.item_type}>
                        <Box
                          aria-label={meta?.label ?? item.item_type}
                          sx={{ display: 'inline-flex', alignItems: 'center', color: colorStyles.color }}
                        >
                          <ItemTypeIcon sx={{ fontSize: itemTypeIconSize }} />
                        </Box>
                      </Tooltip>
                      {isTitleEditing ? (
                        <TextField
                          value={draftTitle}
                          onChange={(event) => handleTitleFieldChange(item.id, event.target.value)}
                          onBlur={() => handleTitleFieldBlur(item.id)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault()
                              void flushTitleSave(item.id)
                              setEditingTitleId(null)
                            } else if (event.key === 'Escape') {
                              event.preventDefault()
                              cancelTitleEdit(item)
                            }
                          }}
                          variant="standard"
                          size="small"
                          autoFocus
                          inputProps={{ 'aria-label': 'Edit title' }}
                          InputProps={{
                            disableUnderline: true,
                            endAdornment: titleSaving ? <CircularProgress size={16} /> : undefined,
                            sx: { fontWeight: 600, fontSize: '1rem' },
                          }}
                          sx={{ minWidth: 0 }}
                        />
                      ) : (
                        <ButtonBase
                          onClick={(event) => {
                            event.stopPropagation()
                            startTitleEdit(item)
                          }}
                          disableRipple
                          sx={{ textAlign: 'left', padding: 0, borderRadius: 0 }}
                          aria-label="Edit title"
                        >
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {displayTitle}
                          </Typography>
                        </ButtonBase>
                      )}
                      {endsInNode}
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" />
                  </Stack>
                  <Stack direction="row" spacing={1.5} mt={0.5} flexWrap="wrap" alignItems="center" useFlexGap>
                    {startsInNode}
                    {durationNode}
                    <Typography variant="body2" color="text.secondary">
                      Scheduled: {scheduledLabel}
                    </Typography>
                    {!hasActualStart ? (
                      <Typography variant="body2" color="text.secondary">
                        ETA: {projectedStartLabel ?? scheduledLabel}
                      </Typography>
                    ) : null}
                    {hasValue(item.actual_start) ? (
                      <Typography variant="body2" color="text.secondary">
                        Actual Start: {actualStartLabel}
                      </Typography>
                    ) : null}
                    {hasValue(item.actual_end) ? (
                      <Typography variant="body2" color="text.secondary">
                        Ended: {actualEndLabel}
                      </Typography>
                    ) : null}
                  </Stack>
                </>
              )}
            </Box>
            <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap>
              <Stack direction="column" spacing={0.75} alignItems="flex-end" useFlexGap>
                {statusChip}
                {driftChip}
              </Stack>
              <Tooltip
                title={
                  autoAdvanceEnabled
                    ? 'Auto advance enabled (only applies in Auto or Hybrid mode).'
                    : 'Auto advance disabled (only applies in Auto or Hybrid mode).'
                }
              >
                <span style={{ display: 'inline-flex' }}>
                  <IconButton
                    size="small"
                    onClick={() => void handleToggleItemAutoAdvance(item, !autoAdvanceEnabled)}
                    disabled={autoAdvanceDisabled}
                    aria-label={`Auto advance ${displayTitle || getItemLabel(item) || 'item'}`}
                    sx={{
                      color: autoAdvanceBaseColor,
                      ...(autoAdvanceFlashColor
                        ? {
                            animation: 'autoAdvanceFlash 1s ease-in-out infinite',
                            '@keyframes autoAdvanceFlash': {
                              '0%, 100%': { color: autoAdvanceFlashColor },
                              '50%': { color: alpha(autoAdvanceFlashColor, 0.4) },
                            },
                          }
                        : null),
                    }}
                  >
                    <NextPlanIcon sx={{ fontSize: 50 }} />
                  </IconButton>
                </span>
              </Tooltip>
              {dragHandleProps ? (
                <Tooltip title="Drag to reorder">
                  <span style={{ display: 'inline-flex' }}>
                    <IconButton
                      size="small"
                      color="inherit"
                      aria-label="Drag to reorder"
                      {...dragHandleProps.attributes}
                      {...dragHandleProps.listeners}
                      ref={dragHandleProps.setActivatorNodeRef}
                      disableRipple
                    >
                      <DragIndicatorIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
              <IconButton
                size="small"
                color="inherit"
                aria-label="Item actions"
                onClick={(event) => openRowMenu(event, item)}
              >
                <MoreVertIcon />
              </IconButton>
              <Tooltip title={expanded ? 'Hide notes & prep' : 'Show notes & prep'}>
                <span style={{ display: 'inline-flex' }}>
                  <IconButton
                    aria-label="Toggle notes and prep"
                    onClick={() => toggleExpanded(item.id)}
                    size="small"
                    sx={{
                      transition: 'transform 0.2s ease',
                      transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                  >
                    <ExpandMoreIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Divider sx={{ my: 1 }} />
            <Stack spacing={1.5}>
                <Box
                  ref={splitContainerRef}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: `${liveSplit}fr 10px ${1 - liveSplit}fr`,
                    alignItems: 'stretch',
                  }}
                >
                  <Box sx={{ minWidth: 0, pr: 1 }}>{renderPrepContent(item)}</Box>
                  <Box
                    role="separator"
                    aria-label="Resize prep and notes columns"
                    onPointerDown={handleSplitPointerDown}
                    sx={{
                      cursor: 'col-resize',
                      display: 'flex',
                      alignItems: 'stretch',
                      justifyContent: 'center',
                      px: 0.5,
                      '&::before': {
                        content: '""',
                        width: 2,
                        borderRadius: 999,
                        backgroundColor: 'divider',
                        opacity: 0.8,
                      },
                    }}
                  />
                  <Box sx={{ minWidth: 0, pl: 1 }}>{renderNotes(item)}</Box>
                </Box>
              </Stack>
            </Collapse>
          </Stack>
      </Paper>
    )
  }

  const liveSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const rowMenuTarget = rowMenuState?.item ?? null
  const rowMenuAnchor = rowMenuState?.anchor ?? null
  const insertDurations = React.useMemo(
    () => ({
      song: resolveInsertDuration('song', rowMenuTarget),
      link: resolveInsertDuration('link', rowMenuTarget),
      custom: resolveInsertDuration('custom', rowMenuTarget),
    }),
    [resolveInsertDuration, rowMenuTarget],
  )
  const rowMenuIsCurrentItem = Boolean(rowMenuTarget && currentItemId && rowMenuTarget.id === currentItemId)
  const rowMenuIsCurrentRunningItem = rowMenuIsCurrentItem && isCurrentRunning
  const rowMenuIsEditable = Boolean(rowMenuTarget && !rowMenuTarget.skipped && !rowMenuTarget.actual_end)
  const rowMenuIndex = rowMenuTarget ? items.findIndex((candidate) => candidate.id === rowMenuTarget.id) : -1
  const canMoveUp = rowMenuIndex > 0
  const canMoveDown = rowMenuIndex >= 0 && rowMenuIndex < items.length - 1
  const isWorking = busyCount > 0

  return (
    <TypeChipProvider meta={clockMeta}>
      <MuiThemeProvider theme={liveTheme}>
        <>
        <PrepDetailDrawer
          open={prepDrawerOpen}
          onClose={closePrepDrawer}
          prepId={activePrepId}
          cached={drawerCachedDetail}
          fallbackItem={drawerCachedDetail}
          enableMyPrepActions
          onDetailLoaded={handlePrepDetailLoaded}
          navIds={activePrepNavIds}
          onNavigate={(nextId) => openPrepDrawer(nextId, lastInvokerRef.current, activePrepContextItemId)}
        />
        <PrepPicker
          open={prepPickerOpen}
          onClose={closePrepPicker}
          stationId={stationId ?? null}
          onAttach={handleAttachPrepItem}
          api={prepPickerApi}
          attachedItemIds={prepPickerAttachedIds}
          onViewPrep={(prepId, invoker) => openPrepDrawer(prepId, invoker, null)}
        />
        <Dialog open={Boolean(deleteTarget)} onClose={handleCloseRemove}>
          <DialogTitle>Remove item</DialogTitle>
          <DialogContent dividers>
            <Typography gutterBottom>
              {deleteTarget
                ? `Remove "${displayTitleForItem(deleteTarget) || deleteTarget.item_type}" from this show clock?`
                : 'Remove this item from the show clock?'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseRemove} disabled={deleteLoading}>
              Cancel
            </Button>
            <Button onClick={handleConfirmRemove} color="error" variant="contained" disabled={deleteLoading}>
              {deleteLoading ? 'Removing…' : 'Remove'}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={reconcilePromptOpen || catchUpLocked}
          onClose={(_, reason) => {
            if (catchUpLocked) {
              setSnackbar({
                message: 'Reconcile is required to continue when opening the show clock late.',
                severity: 'info',
              })
              return
            }
            closeReconcileDialog()
          }}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown={catchUpLocked}
        >
          <DialogTitle>Reconcile past items</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {catchUpLocked
                ? 'This show clock was opened late. Reconcile is required before you can continue.'
                : 'You are behind the projected timeline. Select the items you want to reconcile manually; zero-duration markers are pre-checked.'}
            </Typography>
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 2,
                bgcolor: 'action.hover',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Current item
              </Typography>
              {(() => {
              const displayedCurrent = reconcileCurrentItem ?? currentItem ?? null
              if (!displayedCurrent) {
                return (
                  <Typography variant="body2" color="text.secondary">
                    Current item not yet determined.
                  </Typography>
                )
              }
              const scheduledEntry = scheduledProjectionMap.get(displayedCurrent.id) ?? null
              const currentTitle = sanitizeLabel(
                displayTitles[displayedCurrent.id] ?? displayedCurrent.title ?? 'Untitled item',
              )
              const startLabel = formatClockTimeLabel(
                scheduledEntry?.scheduled_start ?? scheduledEntry?.projected_start ?? displayedCurrent.actual_start,
                stationTimeZone,
                { withSeconds: true },
              )
              const endLabel = formatClockTimeLabel(
                scheduledEntry?.scheduled_end ?? scheduledEntry?.projected_end ?? displayedCurrent.actual_end,
                stationTimeZone,
                { withSeconds: true },
              )
                return (
                  <>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {currentTitle || 'Untitled item'}
                    </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {startLabel ? `Scheduled: ${startLabel}` : 'Scheduled: ?'}{' '}
                    {endLabel ? `— Ends: ${endLabel}` : ''}
                  </Typography>
                </>
              )
            })()}
          </Box>
          <List disablePadding>
            {reconcileCandidates.map((item) => {
                const scheduledEntry = scheduledProjectionMap.get(item.id) ?? null
                const title = sanitizeLabel(displayTitles[item.id] ?? item.title ?? 'Untitled item')
                const scheduledStartLabel = scheduledEntry
                  ? formatClockTimeLabel(
                      scheduledEntry.scheduled_start ?? scheduledEntry.projected_start,
                      stationTimeZone,
                      { withSeconds: true },
                    )
                  : 'Unknown'
                const durationLabel = formatClockDuration(item.planned_duration_sec)
                return (
                  <ListItem key={item.id} disableGutters divider>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={reconcileCheckedIds.has(item.id)}
                          onChange={() => toggleReconcileCandidate(item.id)}
                          disabled={reconcileApplying}
                        />
                      }
                      label={
                        <ListItemText
                          primary={title || 'Untitled item'}
                          secondary={`Scheduled ${scheduledStartLabel} · ${durationLabel}`}
                        />
                      }
                    />
                  </ListItem>
                )
              })}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeReconcileDialog} disabled={reconcileApplying || catchUpLocked}>
              Close
            </Button>
            <Button
              variant="contained"
              onClick={handleRunReconcile}
              disabled={reconcileApplying || reconcileSelectedCount === 0}
              startIcon={reconcileApplying ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {reconcileApplying ? 'Applying...' : reconcileSelectedCount > 0 ? `Apply (${reconcileSelectedCount})` : 'Apply'}
            </Button>
          </DialogActions>
        </Dialog>
        <Box
          sx={{
            px: 3,
            pt: 0.5,
            pb: 3,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
          }}
        >
      {loading ? <LinearProgress sx={{ mb: 2 }} /> : null}
      {!day && !loading && !error ? (
        lookupLoading ? (
          <Paper variant="outlined" sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <CircularProgress size={24} />
              <Typography color="text.secondary">Checking for live show clocks...</Typography>
            </Stack>
            {headerTiming ? (
              <Box sx={{ mt: 0.75 }}>
                {(() => {
                  const endsIn = headerTiming.endsInSeconds
                  const startsIn = headerTiming.startsInSeconds
                  const barColor =
                    endsIn !== null && endsIn <= 10
                      ? theme.palette.error.main
                      : endsIn !== null && endsIn <= 30
                        ? theme.palette.warning.main
                        : theme.palette.primary.main
                  const value =
                    headerTiming.progress !== null
                      ? Math.max(0, Math.min(100, 100 - headerTiming.progress * 100))
                      : startsIn !== null
                        ? 100
                        : 0
                  return (
                    <LinearProgress
                      variant="determinate"
                      value={value}
                      sx={{
                        height: 8,
                        borderRadius: 999,
                        bgcolor: alpha(barColor, 0.12),
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: barColor,
                          transition: 'width 0.6s linear, transform 0.6s linear',
                        },
                      }}
                    />
                  )
                })()}
              </Box>
            ) : null}
          </Paper>
        ) : lookupError ? (
          <Paper variant="outlined" sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3 }}>
            <Stack spacing={2}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Unable to determine the live show clock.
              </Typography>
              <Typography color="text.secondary">{lookupError}</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <Button variant="contained" onClick={handleRetryLookup} sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}>
                  Retry
                </Button>
                <Typography color="text.secondary">
                  Or choose a clock day from Daily Clocks to launch the live presenter console.
                </Typography>
              </Stack>
              {headerTiming ? (
                <Box sx={{ mt: 1 }}>
                  {(() => {
                    const endsIn = headerTiming.endsInSeconds
                    const startsIn = headerTiming.startsInSeconds
                    const barColor =
                      endsIn !== null && endsIn <= 10
                        ? theme.palette.error.main
                        : endsIn !== null && endsIn <= 30
                          ? theme.palette.warning.main
                          : theme.palette.primary.main
                    const value =
                      headerTiming.progress !== null
                        ? Math.max(0, Math.min(100, 100 - headerTiming.progress * 100))
                        : startsIn !== null
                          ? 100
                          : 0
                    return (
                      <LinearProgress
                        variant="determinate"
                        value={value}
                        sx={{
                          height: 8,
                          borderRadius: 999,
                          bgcolor: alpha(barColor, 0.12),
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: barColor,
                            transition: 'transform 0.6s linear',
                          },
                        }}
                      />
                    )
                  })()}
                </Box>
              ) : null}
            </Stack>
          </Paper>
        ) : lookupState && !lookupState.response.current && lookupState.response.next ? (
          <Stack spacing={3}>
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 3, sm: 4 },
                borderRadius: 3,
              }}
            >
              <Typography
                component="h2"
                variant="h4"
                sx={{
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontSize: { xs: '1.5rem', sm: '1.75rem' },
                }}
              >
                NO SHOW CLOCK IS LIVE RIGHT NOW.
              </Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3 }}>
              <Stack spacing={2}>
                {nextCountdownSeconds !== null ? (
                  <Typography
                    variant="h3"
                    color="text.secondary"
                    component="p"
                    sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                  >
                    Next scheduled show clock starts in{' '}
                    <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {nextCountdownLabel}
                    </Box>
                  </Typography>
                ) : null}
                <Typography
                  component="h3"
                  variant="h5"
                  sx={{ fontWeight: 600, fontSize: { xs: '1.35rem', sm: '1.5rem' } }}
                >
                  {nextPrimaryLine}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  onClick={() => nextSummary && handleOpenLookupDay(nextSummary)}
                  aria-label="Open next show clock"
                  sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
                >
                  Open
                </Button>
              </Stack>
            </Paper>
          </Stack>
        ) : lookupState && !lookupState.response.current && !lookupState.response.next ? (
          <Paper variant="outlined" sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3 }}>
            <Typography
              component="h2"
              variant="h4"
              sx={{
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                fontSize: { xs: '1.5rem', sm: '1.75rem' },
              }}
            >
              NO SHOW CLOCK IS LIVE RIGHT NOW.
            </Typography>
          </Paper>
        ) : (
          <Paper variant="outlined" sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3 }}>
            <Stack spacing={1.5}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Checking show clocks...
              </Typography>
              <Typography color="text.secondary">
                We'll load the live presenter view automatically once a show clock is available.
              </Typography>
            </Stack>
          </Paper>
        )
      ) : null}
      {day ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            gap: 1,
            '& .MuiTooltip-tooltip': {
              fontSize: '1.4rem',
              lineHeight: 1.2,
            },
          }}
        >
          <Box
            sx={{
              height: 4,
              mt: -0.25,
              mb: 0,
              borderRadius: 0,
              width: '100%',
              backgroundColor: isWorking ? alpha(theme.palette.common.white, 0.08) : 'transparent',
            }}
          >
            {isWorking ? (
              <LinearProgress
                variant="indeterminate"
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  backgroundColor: 'transparent',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 2,
                    animationDuration: '1.3s',
                    animationTimingFunction: 'linear',
                  },
                }}
              />
            ) : null}
          </Box>
          {showHeaderBar ? (
            <Paper
              variant="outlined"
              sx={{
                px: headerCollapsed ? 0 : 1.5,
                py: headerCollapsed ? 0 : 0.25,
                borderRadius: headerCollapsed ? 0 : '16px',
                flexShrink: 0,
                position: 'relative',
                overflow: headerCollapsed ? 'visible' : 'hidden',
                pb: headerCollapsed ? 0 : 1,
                height: headerCollapsed ? 8 : 'auto',
                minHeight: headerCollapsed ? 8 : undefined,
                borderWidth: headerCollapsed ? 0 : 4,
                borderStyle: 'solid',
                backgroundColor:
                  theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.08) : theme.palette.grey[100],
                borderColor:
                  headerTiming && headerTiming.endsInSeconds !== null && headerTiming.endsInSeconds <= 30
                    ? headerTiming.endsInSeconds <= 10
                      ? theme.palette.error.main
                      : theme.palette.warning.main
                    : theme.palette.common.white,
                animation:
                  headerTiming && headerTiming.endsInSeconds !== null && headerTiming.endsInSeconds <= 30
                    ? headerBorderFlashKeyframes
                      ? `${headerBorderFlashKeyframes} 1s ease-in-out infinite`
                      : undefined
                    : undefined,
                ...(effectiveHeaderFill && headerBarVisuals
                  ? {
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        borderRadius: 'inherit',
                        backgroundColor: headerBarVisuals.activeColor,
                        transformOrigin: 'left center',
                        transform: `scaleX(${headerBarVisuals.value / 100})`,
                        transition: 'transform 0.6s linear, background-color 0.3s ease',
                        pointerEvents: 'none',
                        zIndex: 0,
                        animation:
                          headerBarVisuals.shouldFlash && headerFlashKeyframes
                            ? `${headerFlashKeyframes} 1s ease-in-out infinite`
                            : undefined,
                      },
                    }
                  : undefined),
              }}
              ref={headerRef}
            >
              {!headerCollapsed ? (
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={0.75}
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  justifyContent="space-between"
                  flexWrap
                  useFlexGap
                  sx={{ rowGap: 0.75, position: 'relative', zIndex: 1 }}
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={0.75}
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    useFlexGap
                    flexWrap
                    sx={{ rowGap: 0.75, minWidth: 0, flex: 1 }}
                  >
                    <Stack
                      direction="row"
                      spacing={0.75}
                      alignItems="center"
                      useFlexGap
                      flexWrap
                      sx={{ rowGap: 0.75, minWidth: 0, flex: 1 }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                        <Chip
                          label={`Now: ${truncatedCurrentChipLabel}`}
                          color="primary"
                          variant="filled"
                          size="medium"
                          onClick={currentItemId ? handleScrollToCurrent : undefined}
                          aria-label={headerInfo.currentChipLabel}
                          sx={{
                            cursor: currentItemId ? 'pointer' : 'default',
                            height: 40,
                            maxWidth: '100%',
                            '& .MuiChip-label': {
                              fontSize: '1.5rem',
                              fontWeight: 800,
                              color: theme.palette.common.black,
                              letterSpacing: '0.12em',
                              textTransform: 'uppercase',
                              px: 1.5,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            },
                            backgroundColor: headerCurrentBg,
                          }}
                        />
                      </Box>
                      {headerTiming ? (
                        <>
                          {headerTiming.startsInSeconds !== null ? (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`Starts in ${formatCountdownLabel(headerTiming.startsInSeconds)}`}
                              sx={{
                                fontWeight: 700,
                                bgcolor: alpha(theme.palette.common.white, 0.08),
                                flexShrink: 0,
                              }}
                            />
                          ) : headerTiming.endsInSeconds !== null ? (
                            <Chip
                              size="small"
                              variant="filled"
                              label={`>> ${formatCountdownLabel(headerTiming.endsInSeconds)} >>`}
                              sx={{
                                fontWeight: 800,
                                fontSize: '2rem',
                                height: 42,
                                flexShrink: 0,
                                '& .MuiChip-label': {
                                  px: 1.5,
                                  py: 0.25,
                                },
                                bgcolor:
                                  headerTiming.endsInSeconds <= HEADER_CRITICAL_THRESHOLD_SECONDS
                                    ? theme.palette.error.main
                                    : headerTiming.endsInSeconds <= HEADER_WARN_THRESHOLD_SECONDS
                                      ? theme.palette.warning.main
                                      : theme.palette.primary.main,
                                color: theme.palette.common.black,
                              }}
                            />
                          ) : null}
                        </>
                      ) : null}
                      <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                        <Chip
                          label={`Next: ${truncatedNextChipLabel}`}
                          color="default"
                          variant="outlined"
                          size="medium"
                          aria-label={headerInfo.nextChipLabel}
                          sx={{
                            maxWidth: '100%',
                            '& .MuiChip-label': {
                              fontSize: '1rem',
                              fontWeight: 700,
                              color: theme.palette.common.black,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            },
                            bgcolor: headerNextBg,
                            borderColor: alpha(theme.palette.common.white, 0.15),
                            cursor: advanceTarget ? 'pointer' : 'default',
                          }}
                          onClick={advanceTarget ? handleAdvance : undefined}
                        />
                      </Box>
                    </Stack>
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    useFlexGap
                    flexWrap="nowrap"
                    sx={{ rowGap: 0.75, marginLeft: 'auto', flexShrink: 0, whiteSpace: 'nowrap' }}
                  >
                    {isWorking ? (
                      <CircularProgress
                        size={18}
                        thickness={4}
                        sx={{ color: theme.palette.common.white }}
                        aria-label="Working"
                      />
                    ) : null}
                    <StatusChip driftSeconds={headerInfo.driftSeconds} />
                    {showReconcileButton ? (
                      <Button
                        variant={reconcileButtonVariant}
                        color={reconcileButtonColor}
                        onClick={handleOpenReconcileDialog}
                        sx={
                          reconcileButtonVariant === 'outlined'
                            ? {
                                bgcolor: alpha(theme.palette.common.white, 0.08),
                                borderColor: alpha(theme.palette.common.white, 0.2),
                                '&:hover': { bgcolor: alpha(theme.palette.common.white, 0.12) },
                              }
                            : undefined
                        }
                      >
                        Reconcile
                      </Button>
                    ) : null}
                    <Chip
                      label="Auto"
                      color={liveAdvanceMode === 'auto' ? 'primary' : 'default'}
                      variant={liveAdvanceMode === 'auto' ? 'filled' : 'outlined'}
                      onClick={() => setLiveAdvanceMode('auto')}
                      size="small"
                      clickable
                      aria-pressed={liveAdvanceMode === 'auto'}
                      sx={liveAdvanceMode !== 'auto' ? { bgcolor: alpha(theme.palette.common.white, 0.08) } : undefined}
                    />
                    <Chip
                      label="Hybrid"
                      color={liveAdvanceMode === 'hybrid' ? 'primary' : 'default'}
                      variant={liveAdvanceMode === 'hybrid' ? 'filled' : 'outlined'}
                      onClick={() => setLiveAdvanceMode('hybrid')}
                      size="small"
                      clickable
                      aria-pressed={liveAdvanceMode === 'hybrid'}
                      sx={liveAdvanceMode !== 'hybrid' ? { bgcolor: alpha(theme.palette.common.white, 0.08) } : undefined}
                    />
                    <Chip
                      label="Manual"
                      color={liveAdvanceMode === 'manual' ? 'primary' : 'default'}
                      variant={liveAdvanceMode === 'manual' ? 'filled' : 'outlined'}
                      onClick={() => setLiveAdvanceMode('manual')}
                      size="small"
                      clickable
                      aria-pressed={liveAdvanceMode === 'manual'}
                      sx={liveAdvanceMode !== 'manual' ? { bgcolor: alpha(theme.palette.common.white, 0.08) } : undefined}
                    />
                    <Button
                      variant={holdActive ? 'contained' : 'outlined'}
                      color={holdActive ? 'warning' : 'primary'}
                      onClick={() => setHoldActive((prev) => !prev)}
                      disabled={!isCurrentRunning}
                      sx={{
                        ...(holdActive
                          ? {
                              animation: 'holdPulse 1.1s ease-in-out infinite',
                              '@keyframes holdPulse': {
                                '0%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.warning.main, 0.45)}` },
                                '50%': { boxShadow: `0 0 0 6px ${alpha(theme.palette.warning.main, 0.12)}` },
                                '100%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.warning.main, 0.45)}` },
                              },
                            }
                          : {
                              bgcolor: alpha(theme.palette.common.white, 0.08),
                              borderColor: alpha(theme.palette.common.white, 0.2),
                              '&:hover': { bgcolor: alpha(theme.palette.common.white, 0.12) },
                            }),
                      }}
                    >
                      {holdActive ? 'Release' : 'Hold'}
                    </Button>
                    <IconButton
                      size="small"
                      aria-label="Collapse header bar"
                      onClick={() => setHeaderCollapsed(true)}
                    >
                      <ExpandLessIcon />
                    </IconButton>
                  </Stack>
                </Stack>
              ) : (
                <IconButton
                  size="small"
                  aria-label="Expand header bar"
                  onClick={() => setHeaderCollapsed(false)}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: -18,
                    zIndex: 2,
                    bgcolor: alpha(theme.palette.background.paper, 0.7),
                    '&:hover': { bgcolor: alpha(theme.palette.background.paper, 0.9) },
                  }}
                >
                  <ExpandMoreIcon fontSize="small" />
                </IconButton>
              )}
              {headerBarVisuals && !effectiveHeaderFill ? (
                <Box
                  sx={{
                    position: 'absolute',
                    left: theme.spacing(1),
                    right: theme.spacing(1),
                    bottom: theme.spacing(0.5),
                    pointerEvents: 'none',
                    transform: 'translateY(4px)',
                    zIndex: 1,
                  }}
                >
                  <LinearProgress
                    variant="determinate"
                    value={headerBarVisuals.value}
                    sx={{
                      width: '100%',
                      height: 12,
                      borderRadius: 999,
                      bgcolor: headerBarVisuals.trackColor,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: headerBarVisuals.activeColor,
                        transition: 'width 0.6s linear, transform 0.6s linear',
                        animation:
                          headerBarVisuals.shouldFlash && headerFlashKeyframes
                            ? `${headerFlashKeyframes} 1s ease-in-out infinite`
                            : undefined,
                      },
                    }}
                  />
                </Box>
              ) : null}
            </Paper>
          ) : null}


          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {items.length ? (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  pr: { xs: 0, md: 1.5 },
                }}
                ref={scrollContainerRef}
              >
                <DndContext sensors={liveSensors} onDragEnd={handleLiveDragEnd}>
                  <SortableContext
                    items={items.map((item) => item.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <Stack spacing={1} pb={1}>
                      {items.map((item, index) => (
                        <SortableLiveItemRow
                          key={item.id}
                          item={item}
                          render={({ dragHandleProps, setSortableNodeRef, style }) =>
                            renderTimelineItem(item, index, {
                              dragHandleProps,
                              setSortableNodeRef,
                              style,
                            })
                          }
                        />
                      ))}
                    </Stack>
                  </SortableContext>
                </DndContext>
              </Box>
            ) : (
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, flexShrink: 0 }}>
                <Typography color="text.secondary">This clock day has no scheduled items.</Typography>
              </Paper>
            )}
          </Box>
        </Box>
      ) : null}

      <Menu anchorEl={rowMenuAnchor} open={Boolean(rowMenuAnchor)} onClose={closeRowMenu}>
        <MenuItem onClick={handleAddSongBelow} disabled={!rowMenuTarget}>
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={
              rowMenuTarget
                ? `Add song below ${formatClockDuration(insertDurations.song)}`
                : 'Add song below'
            }
          />
        </MenuItem>
        <MenuItem onClick={handleAddLinkBelow} disabled={!rowMenuTarget}>
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={
              rowMenuTarget
                ? `Add link below ${formatClockDuration(insertDurations.link)}`
                : 'Add link below'
            }
          />
        </MenuItem>
        <MenuItem onClick={handleAddCustomBelow} disabled={!rowMenuTarget}>
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={
              rowMenuTarget
                ? `Add custom item below ${formatClockDuration(insertDurations.custom)}`
                : 'Add custom item below'
            }
          />
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            closeRowMenu()
            moveItemRelative(-1)
          }}
          disabled={!canMoveUp}
        >
          <ListItemIcon>
            <ArrowUpwardIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Move Up" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeRowMenu()
            moveItemRelative(1)
          }}
          disabled={!canMoveDown}
        >
          <ListItemIcon>
            <ArrowDownwardIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Move Down" />
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            closeRowMenu()
            rowMenuTarget && void handleAdjust(rowMenuTarget, 30)
          }}
          disabled={!rowMenuIsEditable}
        >
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Add 30 seconds" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeRowMenu()
            rowMenuTarget && void handleAdjust(rowMenuTarget, -30)
          }}
          disabled={!rowMenuIsEditable}
        >
          <ListItemIcon>
            <RemoveIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Minus 30 seconds" />
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            closeRowMenu()
            handleAdvance()
          }}
          disabled={!rowMenuIsCurrentRunningItem || !advanceTarget}
        >
          <ListItemIcon>
            <FastForwardIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={nextTitle ? `Next: ${nextTitle}` : 'Next'} />
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeRowMenu()
            rowMenuTarget && void handleSkip(rowMenuTarget)
          }}
          disabled={!rowMenuIsCurrentRunningItem || !rowMenuIsEditable}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Skip Item" />
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            closeRowMenu()
            rowMenuTarget && handleBackfillStart(rowMenuTarget, 'scheduled')
          }}
          disabled={!rowMenuIsCurrentRunningItem}
        >
          <ListItemIcon>
            <AccessTimeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Set start to Scheduled" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeRowMenu()
            rowMenuTarget && handleBackfillStart(rowMenuTarget, 'eta')
          }}
          disabled={!rowMenuIsCurrentRunningItem}
        >
          <ListItemIcon>
            <AccessTimeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Set start to ETA" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeRowMenu()
            rowMenuTarget && void handleStart(rowMenuTarget)
          }}
          disabled={!rowMenuIsCurrentItem}
        >
          <ListItemIcon>
            <PlayArrowIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Set as Started Now" />
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleRemoveItem} disabled={!rowMenuTarget} sx={{ color: 'error.main' }}>
          <ListItemIcon sx={{ color: 'inherit' }}>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Remove Item" />
        </MenuItem>
      </Menu>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={snackbar?.autoHideDuration ?? 4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{
          '& .MuiAlert-root': snackbar?.big
            ? {
                minWidth: { xs: 320, sm: 420 },
                maxWidth: { xs: '92vw', sm: 560 },
                fontSize: '1.05rem',
                lineHeight: 1.25,
                py: 1.5,
                px: 2,
              }
            : undefined,
        }}
      >
        {snackbar ? (
          <SnackbarAlert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
            <Stack spacing={0.5}>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                {snackbar.message}
              </Typography>
              {snackbar.details ? <Typography variant="body2">{snackbar.details}</Typography> : null}
            </Stack>
          </SnackbarAlert>
        ) : null}
      </Snackbar>
        </Box>
        </>
      </MuiThemeProvider>
    </TypeChipProvider>
  )
}

type LiveItemDragHandleProps = {
  attributes: DraggableAttributes
  listeners: DraggableSyntheticListeners
  setActivatorNodeRef: (node: HTMLElement | null) => void
  isDragging: boolean
}

type LiveItemSortableRenderProps = {
  dragHandleProps: LiveItemDragHandleProps
  setSortableNodeRef: (node: HTMLDivElement | null) => void
  style: React.CSSProperties
}

const SortableLiveItemRow: React.FC<{
  item: ClockDayItem
  render: (props: LiveItemSortableRenderProps) => React.ReactNode
}> = ({ item, render }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id })
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  }
  return (
    <>
      {render({
        dragHandleProps: {
          attributes,
          listeners,
          setActivatorNodeRef,
          isDragging,
        },
        setSortableNodeRef: setNodeRef,
        style,
      })}
    </>
  )
}

const SortablePrepAttachmentRow: React.FC<{
    attachment: PrepAttachment
    showTease: boolean
    onRemove: () => void
    onOpenDetail: (prepId: number, invoker?: HTMLElement | null) => void
    removing?: boolean
  }> = ({ attachment, showTease, onRemove, onOpenDetail, removing = false }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: attachment.id,
  })
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
  }

    return (
      <Paper ref={setNodeRef} style={style} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <IconButton
          size="small"
          color="inherit"
          {...attributes}
          {...listeners}
          ref={setActivatorNodeRef}
          aria-label="Drag to reorder prep"
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <ButtonBase
            onClick={(event) => onOpenDetail(attachment.prep_item_id, event.currentTarget)}
            sx={{
              textAlign: 'left',
              width: '100%',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              borderRadius: 1,
              p: 0,
            }}
          >
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {attachment.prep?.headline ?? `Prep #${attachment.prep_item_id}`}
              </Typography>
                {showTease && attachment.prep?.tease ? (
                  <Typography variant="body2" color="text.secondary">
                    {attachment.prep.tease}
                  </Typography>
                ) : null}
            </Box>
          </ButtonBase>
        </Box>
        <Tooltip title="Remove attachment">
          <span>
            <IconButton size="small" color="error" onClick={onRemove} disabled={removing}>
              {removing ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <DeleteIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Paper>
  )
}

export default ShowClockLivePage
